//#region \0rolldown/runtime.js
var __commonJSMin = (cb, mod) => () => (mod || cb((mod = { exports: {} }).exports, mod), mod.exports);
//#endregion
//#region src/ComputedPropertyWrapper.ts
var ComputedPropertyWrapper = class {
	#properties;
	#computedProperties;
	#proxy;
	constructor(properties, computedProperties) {
		this.#properties = properties;
		this.#computedProperties = computedProperties;
		this.#proxy = new Proxy(this, {
			get: (target, prop) => {
				if (prop === "toJSON") return this.toJSONWrapped;
				if (typeof prop === "string" && prop in target.#computedProperties) {
					const func = target.#computedProperties[prop];
					return func(target.#properties);
				} else return target.#properties[prop];
			},
			has: (target, prop) => {
				return Reflect.has(target.#properties, prop) || Reflect.has(target.#computedProperties, prop);
			},
			ownKeys: (target) => {
				const keys = [...Reflect.ownKeys(target.#properties), ...Reflect.ownKeys(target.#computedProperties)];
				return Array.from(new Set(keys));
			},
			getOwnPropertyDescriptor(target, key) {
				return Reflect.getOwnPropertyDescriptor(target.#properties, key) ?? Reflect.getOwnPropertyDescriptor(target.#computedProperties, key);
			}
		});
		return this.#proxy;
	}
	/**
	* This method is called by `JSON.stringify` to serialize the object. For computed properties, it returns the computed value at that moment.
	*/
	toJSONWrapped() {
		return { ...this };
	}
};
//#endregion
//#region src/CombinedObjectWrapper.ts
/**
* A class that combines two objects and returns a proxy object that can access properties from both objects.
* 
* @example
* const object1 = { x: 5 };
* const object2 = { y: 10 };
* const combined = new CombinedObjectWrapper(object1, object2);
* combined.x; // 5
* combined.y; // 10
* combined.z; // undefined
* {...combined} // { x: 5, y: 10 }
* JSON.stringify(combined) // '{"x":5,"y":10}'
*/
var CombinedObjectWrapper = class {
	#object1;
	#object2;
	#proxy;
	constructor(object1, object2) {
		this.#object1 = object1;
		this.#object2 = object2;
		this.#proxy = new Proxy(this, {
			get: (target, prop) => {
				if (prop === "toJSON") return this.toJSONWrapped;
				return target.#object1[prop] ?? target.#object2[prop];
			},
			has: (target, prop) => {
				return Reflect.has(target.#object1, prop) || Reflect.has(target.#object2, prop);
			},
			ownKeys: (target) => {
				const keys = [...Reflect.ownKeys(target.#object1), ...Reflect.ownKeys(target.#object2)];
				return Array.from(new Set(keys));
			},
			getOwnPropertyDescriptor(target, key) {
				return Reflect.getOwnPropertyDescriptor(target.#object1, key) ?? Reflect.getOwnPropertyDescriptor(target.#object2, key);
			}
		});
		return this.#proxy;
	}
	/**
	* This method is called by `JSON.stringify` to serialize the object. For computed properties, it returns the computed value at that moment.
	*/
	toJSONWrapped() {
		return { ...this };
	}
};
//#endregion
//#region src/ObjectWatcher.ts
/**
* A class that monitors and records property accesses on a target object.
* The proxy mechanism is used to intercept and log property accesses.
*
* @example
* const myObject = { foo: "bar", baz: 42 };
* const watchedObject = new ObjectWatcher(myObject);
*
* // Access properties on the watched object
* watchedObject.foo; // "bar"
* watchedObject.baz; // 42
*
* // Check accessed properties
* watchedObject.__accessedProperties; // ["foo", "baz"]
*
* // Reset the accessed properties log
* watchedObject.reset();
* watchedObject.__accessedProperties; // []
*/
var ObjectWatcher = class ObjectWatcher {
	/**
	* Unwrap the target from an ObjectWatcher or FieldPropertyAccessErrorWrapper.
	* If the object isn't wrapped, return the object.
	*
	* @param object the object to unwrap (can be an object watcher or a regular object)
	* @returns the watched object if the given object is an object watcher, otherwise the object itself
	*/
	static unwrap(object) {
		while (object instanceof ObjectWatcher) object = object.__target;
		return object;
	}
	__accessedProperties = [];
	__propertyAccessed;
	__target;
	constructor(target, propertyAccessed) {
		this.__propertyAccessed = propertyAccessed ?? ((propertyName) => this.__accessedProperties.push(propertyName));
		this.__target = target;
		return this.__createProxy(target, this.__propertyAccessed);
	}
	__childWatcher(childPropName, target) {
		return new ObjectWatcher(target, (grandchildPropName) => {
			this.__propertyAccessed(`${childPropName}.${grandchildPropName}`);
		});
	}
	/**
	*
	* @param additionalProperties
	* @returns
	*/
	__withAdditionalProperties(additionalProperties) {
		return new ObjectWatcher(new CombinedObjectWrapper(additionalProperties, this.__target));
	}
	get __isObjectWatcher() {
		return true;
	}
	toJSON() {
		return this.__target;
	}
	__createProxy(target, propertyAccessed) {
		return new Proxy(this, { get: (proxy, prop) => {
			if (prop in proxy) return proxy[prop];
			if (typeof prop === "string") {
				const snakeCaseProp = this.__toSnakeCase(prop);
				let propertyName = null;
				if (target !== null && typeof target === "object") if (!(prop in target) && snakeCaseProp in target) propertyName = snakeCaseProp;
				else propertyName = prop;
				if (propertyName) {
					propertyAccessed?.(propertyName);
					const value = target[propertyName];
					if (this.__isWrappable(value)) return this.__childWatcher(propertyName, value);
					else if (typeof value === "function") return function(...args) {
						const returnValue = value.apply(target, args);
						if (returnValue !== null && typeof returnValue === "object") return proxy.__childWatcher(propertyName, returnValue);
						else return returnValue;
					};
					return value;
				}
			}
			return target[prop];
		} });
	}
	/**
	* This checks a given value to determine if it should be wrapped by ObjectWatcher.
	*
	* We only want to watch objects and arrays, not primitives, functions, or objects created from classes (like Date or RegExp).
	* @param value
	* @returns true when value is something that should be wrapped by ObjectWatcher
	*/
	__isWrappable(value) {
		return value !== null && typeof value === "object" && (Array.isArray(value) || ["Object", "ObjectWatcher"].includes(value.constructor.name));
	}
	__reset() {
		this.__accessedProperties = [];
	}
	__toSnakeCase(str) {
		return str.replace(/([A-Z])/g, "_$1").toLowerCase();
	}
};
//#endregion
//#region src/BaseTemplate.ts
var UserFunctionError = class extends Error {
	errorResult;
	constructor(message, errorResult) {
		super(message);
		this.errorResult = errorResult;
	}
};
var BaseTemplate = class {
	/**
	* A list of properties used in the final evaluation. For example,
	* if a conditional evaluates to false, fields within the then branch are
	* not included in this list.
	*
	* This includes all properties accessed in the template, including those nested on other objects.
	* For example, if code accesses `fields.stakeholder.name`, both `stakeholder` and `stakeholder.name` are included.
	*/
	usedProperties = [];
	fields;
	evaluation;
	contentControlValues = {};
	currentContentControlValuesScope = this.contentControlValues;
	documentProperties = { shouldRender: true };
	constructor(fields, computedFields, evaluation) {
		this.fields = new ObjectWatcher(new ComputedPropertyWrapper(fields, computedFields));
		this.evaluation = evaluation;
	}
	reset() {
		this.usedProperties = [];
		this.fields.__reset();
		this.contentControlValues = {};
		this.currentContentControlValuesScope = this.contentControlValues;
	}
	evaluate() {
		this.reset();
		this.evaluation({
			fields: this.fields,
			setRenderConditionExpression: this.setRenderConditionExpression.bind(this),
			setVariableExpression: this.setVariableExpression.bind(this),
			setConditionalExpression: this.setConditionalExpression.bind(this),
			setLoopExpression: this.setLoopExpression.bind(this)
		});
		this.fields;
	}
	addUsedFields(list) {
		this.usedProperties = Array.from(new Set([...this.usedProperties, ...list]));
	}
	executeUserFunction(id, userFunction) {
		try {
			return userFunction(this.fields);
		} catch (e) {
			const details = {
				cause: e,
				message: `${e}`,
				source: userFunction.toString()
			};
			if (e instanceof Error) {
				details.type = e.name;
				details.message = e.message;
				details.stack = e.stack;
			}
			throw new UserFunctionError("An error occurred while evaluating the expression", {
				id,
				type: "error",
				userMessage: "An error occurred while evaluating the expression",
				details
			});
		} finally {
			this.addUsedFields(this.fields.__accessedProperties);
		}
	}
	isErrorResult(result) {
		return typeof result === "object" && result !== null && "type" in result && result.type === "error";
	}
	setVariableExpression(variableExpression) {
		try {
			const value = this.executeUserFunction(variableExpression.id, variableExpression.value);
			this.currentContentControlValuesScope[variableExpression.id] = {
				id: variableExpression.id,
				value: ObjectWatcher.unwrap(value) ?? null,
				type: "variable"
			};
		} catch (e) {
			if (e instanceof UserFunctionError) this.currentContentControlValuesScope[variableExpression.id] = e.errorResult;
			else throw e;
		}
	}
	setRenderConditionExpression(valueFunction) {
		try {
			const value = this.executeUserFunction(-1, valueFunction);
			this.documentProperties.shouldRender = !!value;
		} catch (e) {
			if (e instanceof UserFunctionError) {} else throw e;
		}
	}
	setConditionalExpression(conditionalExpression) {
		let value = false;
		try {
			value = !!this.executeUserFunction(conditionalExpression.id, conditionalExpression.value);
		} catch (e) {
			if (e instanceof UserFunctionError) {
				this.currentContentControlValuesScope[conditionalExpression.id] = e.errorResult;
				return;
			} else throw e;
		}
		const previousValuesScope = this.currentContentControlValuesScope;
		this.currentContentControlValuesScope = {};
		try {
			if (value) conditionalExpression.thenBranch();
			else conditionalExpression.elseBranch?.();
			previousValuesScope[conditionalExpression.id] = {
				id: conditionalExpression.id,
				value,
				branch: this.currentContentControlValuesScope,
				type: "conditional"
			};
		} finally {
			this.currentContentControlValuesScope = previousValuesScope;
		}
	}
	setLoopExpression(loopExpression) {
		let collection = [];
		try {
			collection = this.executeUserFunction(loopExpression.id, loopExpression.value) ?? [];
			if (!Array.isArray(ObjectWatcher.unwrap(collection))) {
				const message = `Loop must return an array of items to iterate over, got: ${JSON.stringify(collection)}`;
				throw new UserFunctionError(message, {
					id: loopExpression.id,
					type: "error",
					userMessage: message,
					details: {
						cause: collection,
						message
					}
				});
			}
		} catch (e) {
			if (e instanceof UserFunctionError) {
				this.currentContentControlValuesScope[loopExpression.id] = e.errorResult;
				return;
			} else throw e;
		}
		const previousValuesScope = this.currentContentControlValuesScope;
		const iterations = [];
		try {
			const length = collection.length;
			for (let index = 0; index < length; index++) {
				const item = collection[index];
				this.currentContentControlValuesScope = {};
				const loopFields = this.fields.__withAdditionalProperties({
					[loopExpression.iterableName]: item,
					[`${loopExpression.iterableName}Loop`]: {
						index,
						length,
						first: index === 0,
						last: index === length - 1
					}
				});
				const previousFields = this.fields;
				this.fields = loopFields;
				try {
					loopExpression.branch();
				} finally {
					this.fields = previousFields;
					this.addUsedFields(this.fields.__accessedProperties);
				}
				iterations.push(this.currentContentControlValuesScope);
			}
		} finally {
			previousValuesScope[loopExpression.id] = {
				id: loopExpression.id,
				iterations,
				type: "loop"
			};
			this.currentContentControlValuesScope = previousValuesScope;
		}
	}
};
//#endregion
//#region src/SignTab.ts
var SignTab = {
	build: (signTab, props) => {
		if (!signTab) return null;
		return {
			type: "signTab",
			value: {
				...props,
				signer: ObjectWatcher.unwrap(signTab)
			}
		};
	},
	signature: (signTab, props) => {
		return SignTab.build(signTab, {
			type: "signature",
			...props
		});
	},
	initials: (signTab, props) => {
		return SignTab.build(signTab, {
			type: "initials",
			...props
		});
	},
	dateSigned: (signTab, props) => {
		return SignTab.build(signTab, {
			type: "dateSigned",
			...props
		});
	},
	radio: (signTab, props) => {
		return SignTab.build(signTab, {
			type: "radio",
			...props
		});
	},
	checkbox: (signTab, props) => {
		return SignTab.build(signTab, {
			type: "checkbox",
			...props
		});
	},
	text: (signTab, props) => {
		return SignTab.build(signTab, {
			type: "text",
			...props
		});
	},
	signField: (signField) => {
		return {
			type: "signField",
			value: ObjectWatcher.unwrap(signField)
		};
	}
};
//#endregion
//#region src/Collection.ts
var Collection = {
	sort: (collection, options) => {
		const { sortBy, direction: order = "asc" } = options;
		const unwrappedCollection = ObjectWatcher.unwrap(collection);
		if (!Array.isArray(unwrappedCollection)) return collection;
		const sorted = unwrappedCollection.toSorted((a, b) => {
			const aValue = typeof sortBy === "function" ? sortBy(a) : a[sortBy];
			const bValue = typeof sortBy === "function" ? sortBy(b) : b[sortBy];
			if (typeof aValue === "string" && typeof bValue === "string") return aValue.localeCompare(bValue);
			else if (typeof aValue === "number" && typeof bValue === "number") return aValue - bValue;
			else if (aValue instanceof Date && bValue instanceof Date) return aValue.getTime() - bValue.getTime();
			else if (typeof aValue === "boolean" && typeof bValue === "boolean") return aValue === bValue ? 0 : aValue ? 1 : -1;
			return 0;
		});
		return order === "asc" ? sorted : sorted.reverse();
	},
	applyLimitOffset: (collection, options) => {
		const { limit, offset = 0 } = options;
		const unwrappedCollection = ObjectWatcher.unwrap(collection);
		if (limit === void 0) return unwrappedCollection.slice(offset);
		return unwrappedCollection.slice(offset, offset + limit);
	},
	filter: (collection, filter) => {
		return ObjectWatcher.unwrap(collection).filter((item) => {
			return filter(new ObjectWatcher(item));
		});
	}
};
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/toDate.mjs
/**
* @name toDate
* @category Common Helpers
* @summary Convert the given argument to an instance of Date.
*
* @description
* Convert the given argument to an instance of Date.
*
* If the argument is an instance of Date, the function returns its clone.
*
* If the argument is a number, it is treated as a timestamp.
*
* If the argument is none of the above, the function returns Invalid Date.
*
* **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param argument - The value to convert
*
* @returns The parsed date in the local time zone
*
* @example
* // Clone the date:
* const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
* //=> Tue Feb 11 2014 11:30:30
*
* @example
* // Convert the timestamp to date:
* const result = toDate(1392098430000)
* //=> Tue Feb 11 2014 11:30:30
*/
function toDate(argument) {
	const argStr = Object.prototype.toString.call(argument);
	if (argument instanceof Date || typeof argument === "object" && argStr === "[object Date]") return new argument.constructor(+argument);
	else if (typeof argument === "number" || argStr === "[object Number]" || typeof argument === "string" || argStr === "[object String]") return new Date(argument);
	else return /* @__PURE__ */ new Date(NaN);
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/constructFrom.mjs
/**
* @name constructFrom
* @category Generic Helpers
* @summary Constructs a date using the reference date and the value
*
* @description
* The function constructs a new date using the constructor from the reference
* date and the given value. It helps to build generic functions that accept
* date extensions.
*
* It defaults to `Date` if the passed reference date is a number or a string.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The reference date to take constructor from
* @param value - The value to create the date
*
* @returns Date initialized using the given date and value
*
* @example
* import { constructFrom } from 'date-fns'
*
* // A function that clones a date preserving the original type
* function cloneDate<DateType extends Date(date: DateType): DateType {
*   return constructFrom(
*     date, // Use contrustor from the given date
*     date.getTime() // Use the date value to create a new date
*   )
* }
*/
function constructFrom(date, value) {
	if (date instanceof Date) return new date.constructor(value);
	else return new Date(value);
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/constants.mjs
/**
* @constant
* @name daysInYear
* @summary Days in 1 year.
*
* @description
* How many days in a year.
*
* One years equals 365.2425 days according to the formula:
*
* > Leap year occures every 4 years, except for years that are divisable by 100 and not divisable by 400.
* > 1 mean year = (365+1/4-1/100+1/400) days = 365.2425 days
*/
var daysInYear = 365.2425;
Math.pow(10, 8) * 24 * 60 * 60 * 1e3;
/**
* @constant
* @name millisecondsInWeek
* @summary Milliseconds in 1 week.
*/
var millisecondsInWeek = 6048e5;
/**
* @constant
* @name millisecondsInDay
* @summary Milliseconds in 1 day.
*/
var millisecondsInDay = 864e5;
/**
* @constant
* @name millisecondsInMinute
* @summary Milliseconds in 1 minute
*/
var millisecondsInMinute = 6e4;
/**
* @constant
* @name millisecondsInHour
* @summary Milliseconds in 1 hour
*/
var millisecondsInHour = 36e5;
/**
* @constant
* @name secondsInDay
* @summary Seconds in 1 day.
*/
var secondsInDay = 3600 * 24;
secondsInDay * 7;
secondsInDay * daysInYear / 12 * 3;
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/_lib/defaultOptions.mjs
var defaultOptions = {};
function getDefaultOptions() {
	return defaultOptions;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/startOfWeek.mjs
/**
* The {@link startOfWeek} function options.
*/
/**
* @name startOfWeek
* @category Week Helpers
* @summary Return the start of a week for the given date.
*
* @description
* Return the start of a week for the given date.
* The result will be in the local timezone.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The original date
* @param options - An object with options
*
* @returns The start of a week
*
* @example
* // The start of a week for 2 September 2014 11:55:00:
* const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0))
* //=> Sun Aug 31 2014 00:00:00
*
* @example
* // If the week starts on Monday, the start of the week for 2 September 2014 11:55:00:
* const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
* //=> Mon Sep 01 2014 00:00:00
*/
function startOfWeek(date, options) {
	const defaultOptions = getDefaultOptions();
	const weekStartsOn = options?.weekStartsOn ?? options?.locale?.options?.weekStartsOn ?? defaultOptions.weekStartsOn ?? defaultOptions.locale?.options?.weekStartsOn ?? 0;
	const _date = toDate(date);
	const day = _date.getDay();
	const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
	_date.setDate(_date.getDate() - diff);
	_date.setHours(0, 0, 0, 0);
	return _date;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/startOfISOWeek.mjs
/**
* @name startOfISOWeek
* @category ISO Week Helpers
* @summary Return the start of an ISO week for the given date.
*
* @description
* Return the start of an ISO week for the given date.
* The result will be in the local timezone.
*
* ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The original date
*
* @returns The start of an ISO week
*
* @example
* // The start of an ISO week for 2 September 2014 11:55:00:
* const result = startOfISOWeek(new Date(2014, 8, 2, 11, 55, 0))
* //=> Mon Sep 01 2014 00:00:00
*/
function startOfISOWeek(date) {
	return startOfWeek(date, { weekStartsOn: 1 });
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/getISOWeekYear.mjs
/**
* @name getISOWeekYear
* @category ISO Week-Numbering Year Helpers
* @summary Get the ISO week-numbering year of the given date.
*
* @description
* Get the ISO week-numbering year of the given date,
* which always starts 3 days before the year's first Thursday.
*
* ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The given date
*
* @returns The ISO week-numbering year
*
* @example
* // Which ISO-week numbering year is 2 January 2005?
* const result = getISOWeekYear(new Date(2005, 0, 2))
* //=> 2004
*/
function getISOWeekYear(date) {
	const _date = toDate(date);
	const year = _date.getFullYear();
	const fourthOfJanuaryOfNextYear = constructFrom(date, 0);
	fourthOfJanuaryOfNextYear.setFullYear(year + 1, 0, 4);
	fourthOfJanuaryOfNextYear.setHours(0, 0, 0, 0);
	const startOfNextYear = startOfISOWeek(fourthOfJanuaryOfNextYear);
	const fourthOfJanuaryOfThisYear = constructFrom(date, 0);
	fourthOfJanuaryOfThisYear.setFullYear(year, 0, 4);
	fourthOfJanuaryOfThisYear.setHours(0, 0, 0, 0);
	const startOfThisYear = startOfISOWeek(fourthOfJanuaryOfThisYear);
	if (_date.getTime() >= startOfNextYear.getTime()) return year + 1;
	else if (_date.getTime() >= startOfThisYear.getTime()) return year;
	else return year - 1;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/startOfDay.mjs
/**
* @name startOfDay
* @category Day Helpers
* @summary Return the start of a day for the given date.
*
* @description
* Return the start of a day for the given date.
* The result will be in the local timezone.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The original date
*
* @returns The start of a day
*
* @example
* // The start of a day for 2 September 2014 11:55:00:
* const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
* //=> Tue Sep 02 2014 00:00:00
*/
function startOfDay(date) {
	const _date = toDate(date);
	_date.setHours(0, 0, 0, 0);
	return _date;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/_lib/getTimezoneOffsetInMilliseconds.mjs
/**
* Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
* They usually appear for dates that denote time before the timezones were introduced
* (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
* and GMT+01:00:00 after that date)
*
* Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
* which would lead to incorrect calculations.
*
* This function returns the timezone offset in milliseconds that takes seconds in account.
*/
function getTimezoneOffsetInMilliseconds(date) {
	const _date = toDate(date);
	const utcDate = new Date(Date.UTC(_date.getFullYear(), _date.getMonth(), _date.getDate(), _date.getHours(), _date.getMinutes(), _date.getSeconds(), _date.getMilliseconds()));
	utcDate.setUTCFullYear(_date.getFullYear());
	return +date - +utcDate;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/differenceInCalendarDays.mjs
/**
* @name differenceInCalendarDays
* @category Day Helpers
* @summary Get the number of calendar days between the given dates.
*
* @description
* Get the number of calendar days between the given dates. This means that the times are removed
* from the dates and then the difference in days is calculated.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param dateLeft - The later date
* @param dateRight - The earlier date
*
* @returns The number of calendar days
*
* @example
* // How many calendar days are between
* // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
* const result = differenceInCalendarDays(
*   new Date(2012, 6, 2, 0, 0),
*   new Date(2011, 6, 2, 23, 0)
* )
* //=> 366
* // How many calendar days are between
* // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
* const result = differenceInCalendarDays(
*   new Date(2011, 6, 3, 0, 1),
*   new Date(2011, 6, 2, 23, 59)
* )
* //=> 1
*/
function differenceInCalendarDays(dateLeft, dateRight) {
	const startOfDayLeft = startOfDay(dateLeft);
	const startOfDayRight = startOfDay(dateRight);
	const timestampLeft = +startOfDayLeft - getTimezoneOffsetInMilliseconds(startOfDayLeft);
	const timestampRight = +startOfDayRight - getTimezoneOffsetInMilliseconds(startOfDayRight);
	return Math.round((timestampLeft - timestampRight) / millisecondsInDay);
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/startOfISOWeekYear.mjs
/**
* @name startOfISOWeekYear
* @category ISO Week-Numbering Year Helpers
* @summary Return the start of an ISO week-numbering year for the given date.
*
* @description
* Return the start of an ISO week-numbering year,
* which always starts 3 days before the year's first Thursday.
* The result will be in the local timezone.
*
* ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The original date
*
* @returns The start of an ISO week-numbering year
*
* @example
* // The start of an ISO week-numbering year for 2 July 2005:
* const result = startOfISOWeekYear(new Date(2005, 6, 2))
* //=> Mon Jan 03 2005 00:00:00
*/
function startOfISOWeekYear(date) {
	const year = getISOWeekYear(date);
	const fourthOfJanuary = constructFrom(date, 0);
	fourthOfJanuary.setFullYear(year, 0, 4);
	fourthOfJanuary.setHours(0, 0, 0, 0);
	return startOfISOWeek(fourthOfJanuary);
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/isDate.mjs
/**
* @name isDate
* @category Common Helpers
* @summary Is the given value a date?
*
* @description
* Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
*
* @param value - The value to check
*
* @returns True if the given value is a date
*
* @example
* // For a valid date:
* const result = isDate(new Date())
* //=> true
*
* @example
* // For an invalid date:
* const result = isDate(new Date(NaN))
* //=> true
*
* @example
* // For some value:
* const result = isDate('2014-02-31')
* //=> false
*
* @example
* // For an object:
* const result = isDate({})
* //=> false
*/
function isDate(value) {
	return value instanceof Date || typeof value === "object" && Object.prototype.toString.call(value) === "[object Date]";
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/isValid.mjs
/**
* @name isValid
* @category Common Helpers
* @summary Is the given date valid?
*
* @description
* Returns false if argument is Invalid Date and true otherwise.
* Argument is converted to Date using `toDate`. See [toDate](https://date-fns.org/docs/toDate)
* Invalid Date is a Date, whose time value is NaN.
*
* Time value of Date: http://es5.github.io/#x15.9.1.1
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The date to check
*
* @returns The date is valid
*
* @example
* // For the valid date:
* const result = isValid(new Date(2014, 1, 31))
* //=> true
*
* @example
* // For the value, convertable into a date:
* const result = isValid(1393804800000)
* //=> true
*
* @example
* // For the invalid date:
* const result = isValid(new Date(''))
* //=> false
*/
function isValid(date) {
	if (!isDate(date) && typeof date !== "number") return false;
	const _date = toDate(date);
	return !isNaN(Number(_date));
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/startOfYear.mjs
/**
* @name startOfYear
* @category Year Helpers
* @summary Return the start of a year for the given date.
*
* @description
* Return the start of a year for the given date.
* The result will be in the local timezone.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The original date
*
* @returns The start of a year
*
* @example
* // The start of a year for 2 September 2014 11:55:00:
* const result = startOfYear(new Date(2014, 8, 2, 11, 55, 00))
* //=> Wed Jan 01 2014 00:00:00
*/
function startOfYear(date) {
	const cleanDate = toDate(date);
	const _date = constructFrom(date, 0);
	_date.setFullYear(cleanDate.getFullYear(), 0, 1);
	_date.setHours(0, 0, 0, 0);
	return _date;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/en-US/_lib/formatDistance.mjs
var formatDistanceLocale = {
	lessThanXSeconds: {
		one: "less than a second",
		other: "less than {{count}} seconds"
	},
	xSeconds: {
		one: "1 second",
		other: "{{count}} seconds"
	},
	halfAMinute: "half a minute",
	lessThanXMinutes: {
		one: "less than a minute",
		other: "less than {{count}} minutes"
	},
	xMinutes: {
		one: "1 minute",
		other: "{{count}} minutes"
	},
	aboutXHours: {
		one: "about 1 hour",
		other: "about {{count}} hours"
	},
	xHours: {
		one: "1 hour",
		other: "{{count}} hours"
	},
	xDays: {
		one: "1 day",
		other: "{{count}} days"
	},
	aboutXWeeks: {
		one: "about 1 week",
		other: "about {{count}} weeks"
	},
	xWeeks: {
		one: "1 week",
		other: "{{count}} weeks"
	},
	aboutXMonths: {
		one: "about 1 month",
		other: "about {{count}} months"
	},
	xMonths: {
		one: "1 month",
		other: "{{count}} months"
	},
	aboutXYears: {
		one: "about 1 year",
		other: "about {{count}} years"
	},
	xYears: {
		one: "1 year",
		other: "{{count}} years"
	},
	overXYears: {
		one: "over 1 year",
		other: "over {{count}} years"
	},
	almostXYears: {
		one: "almost 1 year",
		other: "almost {{count}} years"
	}
};
var formatDistance = (token, count, options) => {
	let result;
	const tokenValue = formatDistanceLocale[token];
	if (typeof tokenValue === "string") result = tokenValue;
	else if (count === 1) result = tokenValue.one;
	else result = tokenValue.other.replace("{{count}}", count.toString());
	if (options?.addSuffix) if (options.comparison && options.comparison > 0) return "in " + result;
	else return result + " ago";
	return result;
};
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/_lib/buildFormatLongFn.mjs
function buildFormatLongFn(args) {
	return (options = {}) => {
		const width = options.width ? String(options.width) : args.defaultWidth;
		return args.formats[width] || args.formats[args.defaultWidth];
	};
}
var formatLong = {
	date: buildFormatLongFn({
		formats: {
			full: "EEEE, MMMM do, y",
			long: "MMMM do, y",
			medium: "MMM d, y",
			short: "MM/dd/yyyy"
		},
		defaultWidth: "full"
	}),
	time: buildFormatLongFn({
		formats: {
			full: "h:mm:ss a zzzz",
			long: "h:mm:ss a z",
			medium: "h:mm:ss a",
			short: "h:mm a"
		},
		defaultWidth: "full"
	}),
	dateTime: buildFormatLongFn({
		formats: {
			full: "{{date}} 'at' {{time}}",
			long: "{{date}} 'at' {{time}}",
			medium: "{{date}}, {{time}}",
			short: "{{date}}, {{time}}"
		},
		defaultWidth: "full"
	})
};
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/en-US/_lib/formatRelative.mjs
var formatRelativeLocale = {
	lastWeek: "'last' eeee 'at' p",
	yesterday: "'yesterday at' p",
	today: "'today at' p",
	tomorrow: "'tomorrow at' p",
	nextWeek: "eeee 'at' p",
	other: "P"
};
var formatRelative = (token, _date, _baseDate, _options) => formatRelativeLocale[token];
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/_lib/buildLocalizeFn.mjs
/**
* The localize function argument callback which allows to convert raw value to
* the actual type.
*
* @param value - The value to convert
*
* @returns The converted value
*/
/**
* The map of localized values for each width.
*/
/**
* The index type of the locale unit value. It types conversion of units of
* values that don't start at 0 (i.e. quarters).
*/
/**
* Converts the unit value to the tuple of values.
*/
/**
* The tuple of localized era values. The first element represents BC,
* the second element represents AD.
*/
/**
* The tuple of localized quarter values. The first element represents Q1.
*/
/**
* The tuple of localized day values. The first element represents Sunday.
*/
/**
* The tuple of localized month values. The first element represents January.
*/
function buildLocalizeFn(args) {
	return (value, options) => {
		const context = options?.context ? String(options.context) : "standalone";
		let valuesArray;
		if (context === "formatting" && args.formattingValues) {
			const defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
			const width = options?.width ? String(options.width) : defaultWidth;
			valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
		} else {
			const defaultWidth = args.defaultWidth;
			const width = options?.width ? String(options.width) : args.defaultWidth;
			valuesArray = args.values[width] || args.values[defaultWidth];
		}
		const index = args.argumentCallback ? args.argumentCallback(value) : value;
		return valuesArray[index];
	};
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/en-US/_lib/localize.mjs
var eraValues = {
	narrow: ["B", "A"],
	abbreviated: ["BC", "AD"],
	wide: ["Before Christ", "Anno Domini"]
};
var quarterValues = {
	narrow: [
		"1",
		"2",
		"3",
		"4"
	],
	abbreviated: [
		"Q1",
		"Q2",
		"Q3",
		"Q4"
	],
	wide: [
		"1st quarter",
		"2nd quarter",
		"3rd quarter",
		"4th quarter"
	]
};
var monthValues = {
	narrow: [
		"J",
		"F",
		"M",
		"A",
		"M",
		"J",
		"J",
		"A",
		"S",
		"O",
		"N",
		"D"
	],
	abbreviated: [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec"
	],
	wide: [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December"
	]
};
var dayValues = {
	narrow: [
		"S",
		"M",
		"T",
		"W",
		"T",
		"F",
		"S"
	],
	short: [
		"Su",
		"Mo",
		"Tu",
		"We",
		"Th",
		"Fr",
		"Sa"
	],
	abbreviated: [
		"Sun",
		"Mon",
		"Tue",
		"Wed",
		"Thu",
		"Fri",
		"Sat"
	],
	wide: [
		"Sunday",
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday"
	]
};
var dayPeriodValues = {
	narrow: {
		am: "a",
		pm: "p",
		midnight: "mi",
		noon: "n",
		morning: "morning",
		afternoon: "afternoon",
		evening: "evening",
		night: "night"
	},
	abbreviated: {
		am: "AM",
		pm: "PM",
		midnight: "midnight",
		noon: "noon",
		morning: "morning",
		afternoon: "afternoon",
		evening: "evening",
		night: "night"
	},
	wide: {
		am: "a.m.",
		pm: "p.m.",
		midnight: "midnight",
		noon: "noon",
		morning: "morning",
		afternoon: "afternoon",
		evening: "evening",
		night: "night"
	}
};
var formattingDayPeriodValues = {
	narrow: {
		am: "a",
		pm: "p",
		midnight: "mi",
		noon: "n",
		morning: "in the morning",
		afternoon: "in the afternoon",
		evening: "in the evening",
		night: "at night"
	},
	abbreviated: {
		am: "AM",
		pm: "PM",
		midnight: "midnight",
		noon: "noon",
		morning: "in the morning",
		afternoon: "in the afternoon",
		evening: "in the evening",
		night: "at night"
	},
	wide: {
		am: "a.m.",
		pm: "p.m.",
		midnight: "midnight",
		noon: "noon",
		morning: "in the morning",
		afternoon: "in the afternoon",
		evening: "in the evening",
		night: "at night"
	}
};
var ordinalNumber = (dirtyNumber, _options) => {
	const number = Number(dirtyNumber);
	const rem100 = number % 100;
	if (rem100 > 20 || rem100 < 10) switch (rem100 % 10) {
		case 1: return number + "st";
		case 2: return number + "nd";
		case 3: return number + "rd";
	}
	return number + "th";
};
var localize = {
	ordinalNumber,
	era: buildLocalizeFn({
		values: eraValues,
		defaultWidth: "wide"
	}),
	quarter: buildLocalizeFn({
		values: quarterValues,
		defaultWidth: "wide",
		argumentCallback: (quarter) => quarter - 1
	}),
	month: buildLocalizeFn({
		values: monthValues,
		defaultWidth: "wide"
	}),
	day: buildLocalizeFn({
		values: dayValues,
		defaultWidth: "wide"
	}),
	dayPeriod: buildLocalizeFn({
		values: dayPeriodValues,
		defaultWidth: "wide",
		formattingValues: formattingDayPeriodValues,
		defaultFormattingWidth: "wide"
	})
};
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/_lib/buildMatchFn.mjs
function buildMatchFn(args) {
	return (string, options = {}) => {
		const width = options.width;
		const matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
		const matchResult = string.match(matchPattern);
		if (!matchResult) return null;
		const matchedString = matchResult[0];
		const parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
		const key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, (pattern) => pattern.test(matchedString)) : findKey(parsePatterns, (pattern) => pattern.test(matchedString));
		let value;
		value = args.valueCallback ? args.valueCallback(key) : key;
		value = options.valueCallback ? options.valueCallback(value) : value;
		const rest = string.slice(matchedString.length);
		return {
			value,
			rest
		};
	};
}
function findKey(object, predicate) {
	for (const key in object) if (Object.prototype.hasOwnProperty.call(object, key) && predicate(object[key])) return key;
}
function findIndex(array, predicate) {
	for (let key = 0; key < array.length; key++) if (predicate(array[key])) return key;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/_lib/buildMatchPatternFn.mjs
function buildMatchPatternFn(args) {
	return (string, options = {}) => {
		const matchResult = string.match(args.matchPattern);
		if (!matchResult) return null;
		const matchedString = matchResult[0];
		const parseResult = string.match(args.parsePattern);
		if (!parseResult) return null;
		let value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
		value = options.valueCallback ? options.valueCallback(value) : value;
		const rest = string.slice(matchedString.length);
		return {
			value,
			rest
		};
	};
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/locale/en-US.mjs
/**
* @category Locales
* @summary English locale (United States).
* @language English
* @iso-639-2 eng
* @author Sasha Koss [@kossnocorp](https://github.com/kossnocorp)
* @author Lesha Koss [@leshakoss](https://github.com/leshakoss)
*/
var enUS = {
	code: "en-US",
	formatDistance,
	formatLong,
	formatRelative,
	localize,
	match: {
		ordinalNumber: buildMatchPatternFn({
			matchPattern: /^(\d+)(th|st|nd|rd)?/i,
			parsePattern: /\d+/i,
			valueCallback: (value) => parseInt(value, 10)
		}),
		era: buildMatchFn({
			matchPatterns: {
				narrow: /^(b|a)/i,
				abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
				wide: /^(before christ|before common era|anno domini|common era)/i
			},
			defaultMatchWidth: "wide",
			parsePatterns: { any: [/^b/i, /^(a|c)/i] },
			defaultParseWidth: "any"
		}),
		quarter: buildMatchFn({
			matchPatterns: {
				narrow: /^[1234]/i,
				abbreviated: /^q[1234]/i,
				wide: /^[1234](th|st|nd|rd)? quarter/i
			},
			defaultMatchWidth: "wide",
			parsePatterns: { any: [
				/1/i,
				/2/i,
				/3/i,
				/4/i
			] },
			defaultParseWidth: "any",
			valueCallback: (index) => index + 1
		}),
		month: buildMatchFn({
			matchPatterns: {
				narrow: /^[jfmasond]/i,
				abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
				wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
			},
			defaultMatchWidth: "wide",
			parsePatterns: {
				narrow: [
					/^j/i,
					/^f/i,
					/^m/i,
					/^a/i,
					/^m/i,
					/^j/i,
					/^j/i,
					/^a/i,
					/^s/i,
					/^o/i,
					/^n/i,
					/^d/i
				],
				any: [
					/^ja/i,
					/^f/i,
					/^mar/i,
					/^ap/i,
					/^may/i,
					/^jun/i,
					/^jul/i,
					/^au/i,
					/^s/i,
					/^o/i,
					/^n/i,
					/^d/i
				]
			},
			defaultParseWidth: "any"
		}),
		day: buildMatchFn({
			matchPatterns: {
				narrow: /^[smtwf]/i,
				short: /^(su|mo|tu|we|th|fr|sa)/i,
				abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
				wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
			},
			defaultMatchWidth: "wide",
			parsePatterns: {
				narrow: [
					/^s/i,
					/^m/i,
					/^t/i,
					/^w/i,
					/^t/i,
					/^f/i,
					/^s/i
				],
				any: [
					/^su/i,
					/^m/i,
					/^tu/i,
					/^w/i,
					/^th/i,
					/^f/i,
					/^sa/i
				]
			},
			defaultParseWidth: "any"
		}),
		dayPeriod: buildMatchFn({
			matchPatterns: {
				narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
				any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
			},
			defaultMatchWidth: "any",
			parsePatterns: { any: {
				am: /^a/i,
				pm: /^p/i,
				midnight: /^mi/i,
				noon: /^no/i,
				morning: /morning/i,
				afternoon: /afternoon/i,
				evening: /evening/i,
				night: /night/i
			} },
			defaultParseWidth: "any"
		})
	},
	options: {
		weekStartsOn: 0,
		firstWeekContainsDate: 1
	}
};
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/getDayOfYear.mjs
/**
* @name getDayOfYear
* @category Day Helpers
* @summary Get the day of the year of the given date.
*
* @description
* Get the day of the year of the given date.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The given date
*
* @returns The day of year
*
* @example
* // Which day of the year is 2 July 2014?
* const result = getDayOfYear(new Date(2014, 6, 2))
* //=> 183
*/
function getDayOfYear(date) {
	const _date = toDate(date);
	return differenceInCalendarDays(_date, startOfYear(_date)) + 1;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/getISOWeek.mjs
/**
* @name getISOWeek
* @category ISO Week Helpers
* @summary Get the ISO week of the given date.
*
* @description
* Get the ISO week of the given date.
*
* ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The given date
*
* @returns The ISO week
*
* @example
* // Which week of the ISO-week numbering year is 2 January 2005?
* const result = getISOWeek(new Date(2005, 0, 2))
* //=> 53
*/
function getISOWeek(date) {
	const _date = toDate(date);
	const diff = +startOfISOWeek(_date) - +startOfISOWeekYear(_date);
	return Math.round(diff / millisecondsInWeek) + 1;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/getWeekYear.mjs
/**
* The {@link getWeekYear} function options.
*/
/**
* @name getWeekYear
* @category Week-Numbering Year Helpers
* @summary Get the local week-numbering year of the given date.
*
* @description
* Get the local week-numbering year of the given date.
* The exact calculation depends on the values of
* `options.weekStartsOn` (which is the index of the first day of the week)
* and `options.firstWeekContainsDate` (which is the day of January, which is always in
* the first week of the week-numbering year)
*
* Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The given date
* @param options - An object with options.
*
* @returns The local week-numbering year
*
* @example
* // Which week numbering year is 26 December 2004 with the default settings?
* const result = getWeekYear(new Date(2004, 11, 26))
* //=> 2005
*
* @example
* // Which week numbering year is 26 December 2004 if week starts on Saturday?
* const result = getWeekYear(new Date(2004, 11, 26), { weekStartsOn: 6 })
* //=> 2004
*
* @example
* // Which week numbering year is 26 December 2004 if the first week contains 4 January?
* const result = getWeekYear(new Date(2004, 11, 26), { firstWeekContainsDate: 4 })
* //=> 2004
*/
function getWeekYear(date, options) {
	const _date = toDate(date);
	const year = _date.getFullYear();
	const defaultOptions = getDefaultOptions();
	const firstWeekContainsDate = options?.firstWeekContainsDate ?? options?.locale?.options?.firstWeekContainsDate ?? defaultOptions.firstWeekContainsDate ?? defaultOptions.locale?.options?.firstWeekContainsDate ?? 1;
	const firstWeekOfNextYear = constructFrom(date, 0);
	firstWeekOfNextYear.setFullYear(year + 1, 0, firstWeekContainsDate);
	firstWeekOfNextYear.setHours(0, 0, 0, 0);
	const startOfNextYear = startOfWeek(firstWeekOfNextYear, options);
	const firstWeekOfThisYear = constructFrom(date, 0);
	firstWeekOfThisYear.setFullYear(year, 0, firstWeekContainsDate);
	firstWeekOfThisYear.setHours(0, 0, 0, 0);
	const startOfThisYear = startOfWeek(firstWeekOfThisYear, options);
	if (_date.getTime() >= startOfNextYear.getTime()) return year + 1;
	else if (_date.getTime() >= startOfThisYear.getTime()) return year;
	else return year - 1;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/startOfWeekYear.mjs
/**
* The {@link startOfWeekYear} function options.
*/
/**
* @name startOfWeekYear
* @category Week-Numbering Year Helpers
* @summary Return the start of a local week-numbering year for the given date.
*
* @description
* Return the start of a local week-numbering year.
* The exact calculation depends on the values of
* `options.weekStartsOn` (which is the index of the first day of the week)
* and `options.firstWeekContainsDate` (which is the day of January, which is always in
* the first week of the week-numbering year)
*
* Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The original date
* @param options - An object with options
*
* @returns The start of a week-numbering year
*
* @example
* // The start of an a week-numbering year for 2 July 2005 with default settings:
* const result = startOfWeekYear(new Date(2005, 6, 2))
* //=> Sun Dec 26 2004 00:00:00
*
* @example
* // The start of a week-numbering year for 2 July 2005
* // if Monday is the first day of week
* // and 4 January is always in the first week of the year:
* const result = startOfWeekYear(new Date(2005, 6, 2), {
*   weekStartsOn: 1,
*   firstWeekContainsDate: 4
* })
* //=> Mon Jan 03 2005 00:00:00
*/
function startOfWeekYear(date, options) {
	const defaultOptions = getDefaultOptions();
	const firstWeekContainsDate = options?.firstWeekContainsDate ?? options?.locale?.options?.firstWeekContainsDate ?? defaultOptions.firstWeekContainsDate ?? defaultOptions.locale?.options?.firstWeekContainsDate ?? 1;
	const year = getWeekYear(date, options);
	const firstWeek = constructFrom(date, 0);
	firstWeek.setFullYear(year, 0, firstWeekContainsDate);
	firstWeek.setHours(0, 0, 0, 0);
	return startOfWeek(firstWeek, options);
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/getWeek.mjs
/**
* The {@link getWeek} function options.
*/
/**
* @name getWeek
* @category Week Helpers
* @summary Get the local week index of the given date.
*
* @description
* Get the local week index of the given date.
* The exact calculation depends on the values of
* `options.weekStartsOn` (which is the index of the first day of the week)
* and `options.firstWeekContainsDate` (which is the day of January, which is always in
* the first week of the week-numbering year)
*
* Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The given date
* @param options - An object with options
*
* @returns The week
*
* @example
* // Which week of the local week numbering year is 2 January 2005 with default options?
* const result = getWeek(new Date(2005, 0, 2))
* //=> 2
*
* @example
* // Which week of the local week numbering year is 2 January 2005,
* // if Monday is the first day of the week,
* // and the first week of the year always contains 4 January?
* const result = getWeek(new Date(2005, 0, 2), {
*   weekStartsOn: 1,
*   firstWeekContainsDate: 4
* })
* //=> 53
*/
function getWeek(date, options) {
	const _date = toDate(date);
	const diff = +startOfWeek(_date, options) - +startOfWeekYear(_date, options);
	return Math.round(diff / millisecondsInWeek) + 1;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/_lib/addLeadingZeros.mjs
function addLeadingZeros(number, targetLength) {
	return (number < 0 ? "-" : "") + Math.abs(number).toString().padStart(targetLength, "0");
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/_lib/format/lightFormatters.mjs
var lightFormatters = {
	y(date, token) {
		const signedYear = date.getFullYear();
		const year = signedYear > 0 ? signedYear : 1 - signedYear;
		return addLeadingZeros(token === "yy" ? year % 100 : year, token.length);
	},
	M(date, token) {
		const month = date.getMonth();
		return token === "M" ? String(month + 1) : addLeadingZeros(month + 1, 2);
	},
	d(date, token) {
		return addLeadingZeros(date.getDate(), token.length);
	},
	a(date, token) {
		const dayPeriodEnumValue = date.getHours() / 12 >= 1 ? "pm" : "am";
		switch (token) {
			case "a":
			case "aa": return dayPeriodEnumValue.toUpperCase();
			case "aaa": return dayPeriodEnumValue;
			case "aaaaa": return dayPeriodEnumValue[0];
			default: return dayPeriodEnumValue === "am" ? "a.m." : "p.m.";
		}
	},
	h(date, token) {
		return addLeadingZeros(date.getHours() % 12 || 12, token.length);
	},
	H(date, token) {
		return addLeadingZeros(date.getHours(), token.length);
	},
	m(date, token) {
		return addLeadingZeros(date.getMinutes(), token.length);
	},
	s(date, token) {
		return addLeadingZeros(date.getSeconds(), token.length);
	},
	S(date, token) {
		const numberOfDigits = token.length;
		const milliseconds = date.getMilliseconds();
		return addLeadingZeros(Math.trunc(milliseconds * Math.pow(10, numberOfDigits - 3)), token.length);
	}
};
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/_lib/format/formatters.mjs
var dayPeriodEnum = {
	am: "am",
	pm: "pm",
	midnight: "midnight",
	noon: "noon",
	morning: "morning",
	afternoon: "afternoon",
	evening: "evening",
	night: "night"
};
var formatters = {
	G: function(date, token, localize) {
		const era = date.getFullYear() > 0 ? 1 : 0;
		switch (token) {
			case "G":
			case "GG":
			case "GGG": return localize.era(era, { width: "abbreviated" });
			case "GGGGG": return localize.era(era, { width: "narrow" });
			default: return localize.era(era, { width: "wide" });
		}
	},
	y: function(date, token, localize) {
		if (token === "yo") {
			const signedYear = date.getFullYear();
			const year = signedYear > 0 ? signedYear : 1 - signedYear;
			return localize.ordinalNumber(year, { unit: "year" });
		}
		return lightFormatters.y(date, token);
	},
	Y: function(date, token, localize, options) {
		const signedWeekYear = getWeekYear(date, options);
		const weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;
		if (token === "YY") return addLeadingZeros(weekYear % 100, 2);
		if (token === "Yo") return localize.ordinalNumber(weekYear, { unit: "year" });
		return addLeadingZeros(weekYear, token.length);
	},
	R: function(date, token) {
		return addLeadingZeros(getISOWeekYear(date), token.length);
	},
	u: function(date, token) {
		return addLeadingZeros(date.getFullYear(), token.length);
	},
	Q: function(date, token, localize) {
		const quarter = Math.ceil((date.getMonth() + 1) / 3);
		switch (token) {
			case "Q": return String(quarter);
			case "QQ": return addLeadingZeros(quarter, 2);
			case "Qo": return localize.ordinalNumber(quarter, { unit: "quarter" });
			case "QQQ": return localize.quarter(quarter, {
				width: "abbreviated",
				context: "formatting"
			});
			case "QQQQQ": return localize.quarter(quarter, {
				width: "narrow",
				context: "formatting"
			});
			default: return localize.quarter(quarter, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	q: function(date, token, localize) {
		const quarter = Math.ceil((date.getMonth() + 1) / 3);
		switch (token) {
			case "q": return String(quarter);
			case "qq": return addLeadingZeros(quarter, 2);
			case "qo": return localize.ordinalNumber(quarter, { unit: "quarter" });
			case "qqq": return localize.quarter(quarter, {
				width: "abbreviated",
				context: "standalone"
			});
			case "qqqqq": return localize.quarter(quarter, {
				width: "narrow",
				context: "standalone"
			});
			default: return localize.quarter(quarter, {
				width: "wide",
				context: "standalone"
			});
		}
	},
	M: function(date, token, localize) {
		const month = date.getMonth();
		switch (token) {
			case "M":
			case "MM": return lightFormatters.M(date, token);
			case "Mo": return localize.ordinalNumber(month + 1, { unit: "month" });
			case "MMM": return localize.month(month, {
				width: "abbreviated",
				context: "formatting"
			});
			case "MMMMM": return localize.month(month, {
				width: "narrow",
				context: "formatting"
			});
			default: return localize.month(month, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	L: function(date, token, localize) {
		const month = date.getMonth();
		switch (token) {
			case "L": return String(month + 1);
			case "LL": return addLeadingZeros(month + 1, 2);
			case "Lo": return localize.ordinalNumber(month + 1, { unit: "month" });
			case "LLL": return localize.month(month, {
				width: "abbreviated",
				context: "standalone"
			});
			case "LLLLL": return localize.month(month, {
				width: "narrow",
				context: "standalone"
			});
			default: return localize.month(month, {
				width: "wide",
				context: "standalone"
			});
		}
	},
	w: function(date, token, localize, options) {
		const week = getWeek(date, options);
		if (token === "wo") return localize.ordinalNumber(week, { unit: "week" });
		return addLeadingZeros(week, token.length);
	},
	I: function(date, token, localize) {
		const isoWeek = getISOWeek(date);
		if (token === "Io") return localize.ordinalNumber(isoWeek, { unit: "week" });
		return addLeadingZeros(isoWeek, token.length);
	},
	d: function(date, token, localize) {
		if (token === "do") return localize.ordinalNumber(date.getDate(), { unit: "date" });
		return lightFormatters.d(date, token);
	},
	D: function(date, token, localize) {
		const dayOfYear = getDayOfYear(date);
		if (token === "Do") return localize.ordinalNumber(dayOfYear, { unit: "dayOfYear" });
		return addLeadingZeros(dayOfYear, token.length);
	},
	E: function(date, token, localize) {
		const dayOfWeek = date.getDay();
		switch (token) {
			case "E":
			case "EE":
			case "EEE": return localize.day(dayOfWeek, {
				width: "abbreviated",
				context: "formatting"
			});
			case "EEEEE": return localize.day(dayOfWeek, {
				width: "narrow",
				context: "formatting"
			});
			case "EEEEEE": return localize.day(dayOfWeek, {
				width: "short",
				context: "formatting"
			});
			default: return localize.day(dayOfWeek, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	e: function(date, token, localize, options) {
		const dayOfWeek = date.getDay();
		const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
		switch (token) {
			case "e": return String(localDayOfWeek);
			case "ee": return addLeadingZeros(localDayOfWeek, 2);
			case "eo": return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
			case "eee": return localize.day(dayOfWeek, {
				width: "abbreviated",
				context: "formatting"
			});
			case "eeeee": return localize.day(dayOfWeek, {
				width: "narrow",
				context: "formatting"
			});
			case "eeeeee": return localize.day(dayOfWeek, {
				width: "short",
				context: "formatting"
			});
			default: return localize.day(dayOfWeek, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	c: function(date, token, localize, options) {
		const dayOfWeek = date.getDay();
		const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
		switch (token) {
			case "c": return String(localDayOfWeek);
			case "cc": return addLeadingZeros(localDayOfWeek, token.length);
			case "co": return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
			case "ccc": return localize.day(dayOfWeek, {
				width: "abbreviated",
				context: "standalone"
			});
			case "ccccc": return localize.day(dayOfWeek, {
				width: "narrow",
				context: "standalone"
			});
			case "cccccc": return localize.day(dayOfWeek, {
				width: "short",
				context: "standalone"
			});
			default: return localize.day(dayOfWeek, {
				width: "wide",
				context: "standalone"
			});
		}
	},
	i: function(date, token, localize) {
		const dayOfWeek = date.getDay();
		const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
		switch (token) {
			case "i": return String(isoDayOfWeek);
			case "ii": return addLeadingZeros(isoDayOfWeek, token.length);
			case "io": return localize.ordinalNumber(isoDayOfWeek, { unit: "day" });
			case "iii": return localize.day(dayOfWeek, {
				width: "abbreviated",
				context: "formatting"
			});
			case "iiiii": return localize.day(dayOfWeek, {
				width: "narrow",
				context: "formatting"
			});
			case "iiiiii": return localize.day(dayOfWeek, {
				width: "short",
				context: "formatting"
			});
			default: return localize.day(dayOfWeek, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	a: function(date, token, localize) {
		const dayPeriodEnumValue = date.getHours() / 12 >= 1 ? "pm" : "am";
		switch (token) {
			case "a":
			case "aa": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "abbreviated",
				context: "formatting"
			});
			case "aaa": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "abbreviated",
				context: "formatting"
			}).toLowerCase();
			case "aaaaa": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "narrow",
				context: "formatting"
			});
			default: return localize.dayPeriod(dayPeriodEnumValue, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	b: function(date, token, localize) {
		const hours = date.getHours();
		let dayPeriodEnumValue;
		if (hours === 12) dayPeriodEnumValue = dayPeriodEnum.noon;
		else if (hours === 0) dayPeriodEnumValue = dayPeriodEnum.midnight;
		else dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";
		switch (token) {
			case "b":
			case "bb": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "abbreviated",
				context: "formatting"
			});
			case "bbb": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "abbreviated",
				context: "formatting"
			}).toLowerCase();
			case "bbbbb": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "narrow",
				context: "formatting"
			});
			default: return localize.dayPeriod(dayPeriodEnumValue, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	B: function(date, token, localize) {
		const hours = date.getHours();
		let dayPeriodEnumValue;
		if (hours >= 17) dayPeriodEnumValue = dayPeriodEnum.evening;
		else if (hours >= 12) dayPeriodEnumValue = dayPeriodEnum.afternoon;
		else if (hours >= 4) dayPeriodEnumValue = dayPeriodEnum.morning;
		else dayPeriodEnumValue = dayPeriodEnum.night;
		switch (token) {
			case "B":
			case "BB":
			case "BBB": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "abbreviated",
				context: "formatting"
			});
			case "BBBBB": return localize.dayPeriod(dayPeriodEnumValue, {
				width: "narrow",
				context: "formatting"
			});
			default: return localize.dayPeriod(dayPeriodEnumValue, {
				width: "wide",
				context: "formatting"
			});
		}
	},
	h: function(date, token, localize) {
		if (token === "ho") {
			let hours = date.getHours() % 12;
			if (hours === 0) hours = 12;
			return localize.ordinalNumber(hours, { unit: "hour" });
		}
		return lightFormatters.h(date, token);
	},
	H: function(date, token, localize) {
		if (token === "Ho") return localize.ordinalNumber(date.getHours(), { unit: "hour" });
		return lightFormatters.H(date, token);
	},
	K: function(date, token, localize) {
		const hours = date.getHours() % 12;
		if (token === "Ko") return localize.ordinalNumber(hours, { unit: "hour" });
		return addLeadingZeros(hours, token.length);
	},
	k: function(date, token, localize) {
		let hours = date.getHours();
		if (hours === 0) hours = 24;
		if (token === "ko") return localize.ordinalNumber(hours, { unit: "hour" });
		return addLeadingZeros(hours, token.length);
	},
	m: function(date, token, localize) {
		if (token === "mo") return localize.ordinalNumber(date.getMinutes(), { unit: "minute" });
		return lightFormatters.m(date, token);
	},
	s: function(date, token, localize) {
		if (token === "so") return localize.ordinalNumber(date.getSeconds(), { unit: "second" });
		return lightFormatters.s(date, token);
	},
	S: function(date, token) {
		return lightFormatters.S(date, token);
	},
	X: function(date, token, _localize) {
		const timezoneOffset = date.getTimezoneOffset();
		if (timezoneOffset === 0) return "Z";
		switch (token) {
			case "X": return formatTimezoneWithOptionalMinutes(timezoneOffset);
			case "XXXX":
			case "XX": return formatTimezone(timezoneOffset);
			default: return formatTimezone(timezoneOffset, ":");
		}
	},
	x: function(date, token, _localize) {
		const timezoneOffset = date.getTimezoneOffset();
		switch (token) {
			case "x": return formatTimezoneWithOptionalMinutes(timezoneOffset);
			case "xxxx":
			case "xx": return formatTimezone(timezoneOffset);
			default: return formatTimezone(timezoneOffset, ":");
		}
	},
	O: function(date, token, _localize) {
		const timezoneOffset = date.getTimezoneOffset();
		switch (token) {
			case "O":
			case "OO":
			case "OOO": return "GMT" + formatTimezoneShort(timezoneOffset, ":");
			default: return "GMT" + formatTimezone(timezoneOffset, ":");
		}
	},
	z: function(date, token, _localize) {
		const timezoneOffset = date.getTimezoneOffset();
		switch (token) {
			case "z":
			case "zz":
			case "zzz": return "GMT" + formatTimezoneShort(timezoneOffset, ":");
			default: return "GMT" + formatTimezone(timezoneOffset, ":");
		}
	},
	t: function(date, token, _localize) {
		return addLeadingZeros(Math.trunc(date.getTime() / 1e3), token.length);
	},
	T: function(date, token, _localize) {
		return addLeadingZeros(date.getTime(), token.length);
	}
};
function formatTimezoneShort(offset, delimiter = "") {
	const sign = offset > 0 ? "-" : "+";
	const absOffset = Math.abs(offset);
	const hours = Math.trunc(absOffset / 60);
	const minutes = absOffset % 60;
	if (minutes === 0) return sign + String(hours);
	return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}
function formatTimezoneWithOptionalMinutes(offset, delimiter) {
	if (offset % 60 === 0) return (offset > 0 ? "-" : "+") + addLeadingZeros(Math.abs(offset) / 60, 2);
	return formatTimezone(offset, delimiter);
}
function formatTimezone(offset, delimiter = "") {
	const sign = offset > 0 ? "-" : "+";
	const absOffset = Math.abs(offset);
	const hours = addLeadingZeros(Math.trunc(absOffset / 60), 2);
	const minutes = addLeadingZeros(absOffset % 60, 2);
	return sign + hours + delimiter + minutes;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/_lib/format/longFormatters.mjs
var dateLongFormatter = (pattern, formatLong) => {
	switch (pattern) {
		case "P": return formatLong.date({ width: "short" });
		case "PP": return formatLong.date({ width: "medium" });
		case "PPP": return formatLong.date({ width: "long" });
		default: return formatLong.date({ width: "full" });
	}
};
var timeLongFormatter = (pattern, formatLong) => {
	switch (pattern) {
		case "p": return formatLong.time({ width: "short" });
		case "pp": return formatLong.time({ width: "medium" });
		case "ppp": return formatLong.time({ width: "long" });
		default: return formatLong.time({ width: "full" });
	}
};
var dateTimeLongFormatter = (pattern, formatLong) => {
	const matchResult = pattern.match(/(P+)(p+)?/) || [];
	const datePattern = matchResult[1];
	const timePattern = matchResult[2];
	if (!timePattern) return dateLongFormatter(pattern, formatLong);
	let dateTimeFormat;
	switch (datePattern) {
		case "P":
			dateTimeFormat = formatLong.dateTime({ width: "short" });
			break;
		case "PP":
			dateTimeFormat = formatLong.dateTime({ width: "medium" });
			break;
		case "PPP":
			dateTimeFormat = formatLong.dateTime({ width: "long" });
			break;
		default:
			dateTimeFormat = formatLong.dateTime({ width: "full" });
			break;
	}
	return dateTimeFormat.replace("{{date}}", dateLongFormatter(datePattern, formatLong)).replace("{{time}}", timeLongFormatter(timePattern, formatLong));
};
var longFormatters = {
	p: timeLongFormatter,
	P: dateTimeLongFormatter
};
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/_lib/protectedTokens.mjs
var dayOfYearTokenRE = /^D+$/;
var weekYearTokenRE = /^Y+$/;
var throwTokens = [
	"D",
	"DD",
	"YY",
	"YYYY"
];
function isProtectedDayOfYearToken(token) {
	return dayOfYearTokenRE.test(token);
}
function isProtectedWeekYearToken(token) {
	return weekYearTokenRE.test(token);
}
function warnOrThrowProtectedError(token, format, input) {
	const _message = message(token, format, input);
	console.warn(_message);
	if (throwTokens.includes(token)) throw new RangeError(_message);
}
function message(token, format, input) {
	const subject = token[0] === "Y" ? "years" : "days of the month";
	return `Use \`${token.toLowerCase()}\` instead of \`${token}\` (in \`${format}\`) for formatting ${subject} to the input \`${input}\`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md`;
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/format.mjs
var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;
var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp = /^'([^]*?)'?$/;
var doubleQuoteRegExp = /''/g;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;
/**
* The {@link format} function options.
*/
/**
* @name format
* @alias formatDate
* @category Common Helpers
* @summary Format the date.
*
* @description
* Return the formatted date string in the given format. The result may vary by locale.
*
* > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
* > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
*
* The characters wrapped between two single quotes characters (') are escaped.
* Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
* (see the last example)
*
* Format of the string is based on Unicode Technical Standard #35:
* https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
* with a few additions (see note 7 below the table).
*
* Accepted patterns:
* | Unit                            | Pattern | Result examples                   | Notes |
* |---------------------------------|---------|-----------------------------------|-------|
* | Era                             | G..GGG  | AD, BC                            |       |
* |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
* |                                 | GGGGG   | A, B                              |       |
* | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
* |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
* |                                 | yy      | 44, 01, 00, 17                    | 5     |
* |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
* |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
* |                                 | yyyyy   | ...                               | 3,5   |
* | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
* |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
* |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
* |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
* |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
* |                                 | YYYYY   | ...                               | 3,5   |
* | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
* |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
* |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
* |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
* |                                 | RRRRR   | ...                               | 3,5,7 |
* | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
* |                                 | uu      | -43, 01, 1900, 2017               | 5     |
* |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
* |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
* |                                 | uuuuu   | ...                               | 3,5   |
* | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
* |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
* |                                 | QQ      | 01, 02, 03, 04                    |       |
* |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
* |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
* |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
* | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
* |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
* |                                 | qq      | 01, 02, 03, 04                    |       |
* |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
* |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
* |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
* | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
* |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
* |                                 | MM      | 01, 02, ..., 12                   |       |
* |                                 | MMM     | Jan, Feb, ..., Dec                |       |
* |                                 | MMMM    | January, February, ..., December  | 2     |
* |                                 | MMMMM   | J, F, ..., D                      |       |
* | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
* |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
* |                                 | LL      | 01, 02, ..., 12                   |       |
* |                                 | LLL     | Jan, Feb, ..., Dec                |       |
* |                                 | LLLL    | January, February, ..., December  | 2     |
* |                                 | LLLLL   | J, F, ..., D                      |       |
* | Local week of year              | w       | 1, 2, ..., 53                     |       |
* |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
* |                                 | ww      | 01, 02, ..., 53                   |       |
* | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
* |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
* |                                 | II      | 01, 02, ..., 53                   | 7     |
* | Day of month                    | d       | 1, 2, ..., 31                     |       |
* |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
* |                                 | dd      | 01, 02, ..., 31                   |       |
* | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
* |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
* |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
* |                                 | DDD     | 001, 002, ..., 365, 366           |       |
* |                                 | DDDD    | ...                               | 3     |
* | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
* |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
* |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
* |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
* | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
* |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
* |                                 | ii      | 01, 02, ..., 07                   | 7     |
* |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
* |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
* |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
* |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
* | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
* |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
* |                                 | ee      | 02, 03, ..., 01                   |       |
* |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
* |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
* |                                 | eeeee   | M, T, W, T, F, S, S               |       |
* |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
* | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
* |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
* |                                 | cc      | 02, 03, ..., 01                   |       |
* |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
* |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
* |                                 | ccccc   | M, T, W, T, F, S, S               |       |
* |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
* | AM, PM                          | a..aa   | AM, PM                            |       |
* |                                 | aaa     | am, pm                            |       |
* |                                 | aaaa    | a.m., p.m.                        | 2     |
* |                                 | aaaaa   | a, p                              |       |
* | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
* |                                 | bbb     | am, pm, noon, midnight            |       |
* |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
* |                                 | bbbbb   | a, p, n, mi                       |       |
* | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
* |                                 | BBBB    | at night, in the morning, ...     | 2     |
* |                                 | BBBBB   | at night, in the morning, ...     |       |
* | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
* |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
* |                                 | hh      | 01, 02, ..., 11, 12               |       |
* | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
* |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
* |                                 | HH      | 00, 01, 02, ..., 23               |       |
* | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
* |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
* |                                 | KK      | 01, 02, ..., 11, 00               |       |
* | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
* |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
* |                                 | kk      | 24, 01, 02, ..., 23               |       |
* | Minute                          | m       | 0, 1, ..., 59                     |       |
* |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
* |                                 | mm      | 00, 01, ..., 59                   |       |
* | Second                          | s       | 0, 1, ..., 59                     |       |
* |                                 | so      | 0th, 1st, ..., 59th               | 7     |
* |                                 | ss      | 00, 01, ..., 59                   |       |
* | Fraction of second              | S       | 0, 1, ..., 9                      |       |
* |                                 | SS      | 00, 01, ..., 99                   |       |
* |                                 | SSS     | 000, 001, ..., 999                |       |
* |                                 | SSSS    | ...                               | 3     |
* | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
* |                                 | XX      | -0800, +0530, Z                   |       |
* |                                 | XXX     | -08:00, +05:30, Z                 |       |
* |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
* |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
* | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
* |                                 | xx      | -0800, +0530, +0000               |       |
* |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
* |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
* |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
* | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
* |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
* | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
* |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
* | Seconds timestamp               | t       | 512969520                         | 7     |
* |                                 | tt      | ...                               | 3,7   |
* | Milliseconds timestamp          | T       | 512969520900                      | 7     |
* |                                 | TT      | ...                               | 3,7   |
* | Long localized date             | P       | 04/29/1453                        | 7     |
* |                                 | PP      | Apr 29, 1453                      | 7     |
* |                                 | PPP     | April 29th, 1453                  | 7     |
* |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
* | Long localized time             | p       | 12:00 AM                          | 7     |
* |                                 | pp      | 12:00:00 AM                       | 7     |
* |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
* |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
* | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
* |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
* |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
* |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
* Notes:
* 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
*    are the same as "stand-alone" units, but are different in some languages.
*    "Formatting" units are declined according to the rules of the language
*    in the context of a date. "Stand-alone" units are always nominative singular:
*
*    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
*
*    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
*
* 2. Any sequence of the identical letters is a pattern, unless it is escaped by
*    the single quote characters (see below).
*    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
*    the output will be the same as default pattern for this unit, usually
*    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
*    are marked with "2" in the last column of the table.
*
*    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
*
*    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
*
*    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
*
*    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
*
*    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
*
* 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
*    The output will be padded with zeros to match the length of the pattern.
*
*    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
*
* 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
*    These tokens represent the shortest form of the quarter.
*
* 5. The main difference between `y` and `u` patterns are B.C. years:
*
*    | Year | `y` | `u` |
*    |------|-----|-----|
*    | AC 1 |   1 |   1 |
*    | BC 1 |   1 |   0 |
*    | BC 2 |   2 |  -1 |
*
*    Also `yy` always returns the last two digits of a year,
*    while `uu` pads single digit years to 2 characters and returns other years unchanged:
*
*    | Year | `yy` | `uu` |
*    |------|------|------|
*    | 1    |   01 |   01 |
*    | 14   |   14 |   14 |
*    | 376  |   76 |  376 |
*    | 1453 |   53 | 1453 |
*
*    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
*    except local week-numbering years are dependent on `options.weekStartsOn`
*    and `options.firstWeekContainsDate` (compare [getISOWeekYear](https://date-fns.org/docs/getISOWeekYear)
*    and [getWeekYear](https://date-fns.org/docs/getWeekYear)).
*
* 6. Specific non-location timezones are currently unavailable in `date-fns`,
*    so right now these tokens fall back to GMT timezones.
*
* 7. These patterns are not in the Unicode Technical Standard #35:
*    - `i`: ISO day of week
*    - `I`: ISO week of year
*    - `R`: ISO week-numbering year
*    - `t`: seconds timestamp
*    - `T`: milliseconds timestamp
*    - `o`: ordinal number modifier
*    - `P`: long localized date
*    - `p`: long localized time
*
* 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
*    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
*
* 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
*    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param date - The original date
* @param format - The string of tokens
* @param options - An object with options
*
* @returns The formatted date string
*
* @throws `date` must not be Invalid Date
* @throws `options.locale` must contain `localize` property
* @throws `options.locale` must contain `formatLong` property
* @throws use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
* @throws use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
* @throws use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
* @throws use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
* @throws format string contains an unescaped latin alphabet character
*
* @example
* // Represent 11 February 2014 in middle-endian format:
* const result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
* //=> '02/11/2014'
*
* @example
* // Represent 2 July 2014 in Esperanto:
* import { eoLocale } from 'date-fns/locale/eo'
* const result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
*   locale: eoLocale
* })
* //=> '2-a de julio 2014'
*
* @example
* // Escape string by single quote characters:
* const result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
* //=> "3 o'clock"
*/
function format(date, formatStr, options) {
	const defaultOptions = getDefaultOptions();
	const locale = options?.locale ?? defaultOptions.locale ?? enUS;
	const firstWeekContainsDate = options?.firstWeekContainsDate ?? options?.locale?.options?.firstWeekContainsDate ?? defaultOptions.firstWeekContainsDate ?? defaultOptions.locale?.options?.firstWeekContainsDate ?? 1;
	const weekStartsOn = options?.weekStartsOn ?? options?.locale?.options?.weekStartsOn ?? defaultOptions.weekStartsOn ?? defaultOptions.locale?.options?.weekStartsOn ?? 0;
	const originalDate = toDate(date);
	if (!isValid(originalDate)) throw new RangeError("Invalid time value");
	let parts = formatStr.match(longFormattingTokensRegExp).map((substring) => {
		const firstCharacter = substring[0];
		if (firstCharacter === "p" || firstCharacter === "P") {
			const longFormatter = longFormatters[firstCharacter];
			return longFormatter(substring, locale.formatLong);
		}
		return substring;
	}).join("").match(formattingTokensRegExp).map((substring) => {
		if (substring === "''") return {
			isToken: false,
			value: "'"
		};
		const firstCharacter = substring[0];
		if (firstCharacter === "'") return {
			isToken: false,
			value: cleanEscapedString(substring)
		};
		if (formatters[firstCharacter]) return {
			isToken: true,
			value: substring
		};
		if (firstCharacter.match(unescapedLatinCharacterRegExp)) throw new RangeError("Format string contains an unescaped latin alphabet character `" + firstCharacter + "`");
		return {
			isToken: false,
			value: substring
		};
	});
	if (locale.localize.preprocessor) parts = locale.localize.preprocessor(originalDate, parts);
	const formatterOptions = {
		firstWeekContainsDate,
		weekStartsOn,
		locale
	};
	return parts.map((part) => {
		if (!part.isToken) return part.value;
		const token = part.value;
		if (!options?.useAdditionalWeekYearTokens && isProtectedWeekYearToken(token) || !options?.useAdditionalDayOfYearTokens && isProtectedDayOfYearToken(token)) warnOrThrowProtectedError(token, formatStr, String(date));
		const formatter = formatters[token[0]];
		return formatter(originalDate, token, locale.localize, formatterOptions);
	}).join("");
}
function cleanEscapedString(input) {
	const matched = input.match(escapedStringRegExp);
	if (!matched) return input;
	return matched[1].replace(doubleQuoteRegExp, "'");
}
//#endregion
//#region node_modules/.pnpm/date-fns@3.6.0/node_modules/date-fns/parseISO.mjs
/**
* The {@link parseISO} function options.
*/
/**
* @name parseISO
* @category Common Helpers
* @summary Parse ISO string
*
* @description
* Parse the given string in ISO 8601 format and return an instance of Date.
*
* Function accepts complete ISO 8601 formats as well as partial implementations.
* ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
*
* If the argument isn't a string, the function cannot parse the string or
* the values are invalid, it returns Invalid Date.
*
* @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
*
* @param argument - The value to convert
* @param options - An object with options
*
* @returns The parsed date in the local time zone
*
* @example
* // Convert string '2014-02-11T11:30:30' to date:
* const result = parseISO('2014-02-11T11:30:30')
* //=> Tue Feb 11 2014 11:30:30
*
* @example
* // Convert string '+02014101' to date,
* // if the additional number of digits in the extended year format is 1:
* const result = parseISO('+02014101', { additionalDigits: 1 })
* //=> Fri Apr 11 2014 00:00:00
*/
function parseISO(argument, options) {
	const additionalDigits = options?.additionalDigits ?? 2;
	const dateStrings = splitDateString(argument);
	let date;
	if (dateStrings.date) {
		const parseYearResult = parseYear(dateStrings.date, additionalDigits);
		date = parseDate(parseYearResult.restDateString, parseYearResult.year);
	}
	if (!date || isNaN(date.getTime())) return /* @__PURE__ */ new Date(NaN);
	const timestamp = date.getTime();
	let time = 0;
	let offset;
	if (dateStrings.time) {
		time = parseTime(dateStrings.time);
		if (isNaN(time)) return /* @__PURE__ */ new Date(NaN);
	}
	if (dateStrings.timezone) {
		offset = parseTimezone(dateStrings.timezone);
		if (isNaN(offset)) return /* @__PURE__ */ new Date(NaN);
	} else {
		const dirtyDate = new Date(timestamp + time);
		const result = /* @__PURE__ */ new Date(0);
		result.setFullYear(dirtyDate.getUTCFullYear(), dirtyDate.getUTCMonth(), dirtyDate.getUTCDate());
		result.setHours(dirtyDate.getUTCHours(), dirtyDate.getUTCMinutes(), dirtyDate.getUTCSeconds(), dirtyDate.getUTCMilliseconds());
		return result;
	}
	return new Date(timestamp + time + offset);
}
var patterns = {
	dateTimeDelimiter: /[T ]/,
	timeZoneDelimiter: /[Z ]/i,
	timezone: /([Z+-].*)$/
};
var dateRegex = /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)$/;
var timeRegex = /^(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
var timezoneRegex = /^([+-])(\d{2})(?::?(\d{2}))?$/;
function splitDateString(dateString) {
	const dateStrings = {};
	const array = dateString.split(patterns.dateTimeDelimiter);
	let timeString;
	if (array.length > 2) return dateStrings;
	if (/:/.test(array[0])) timeString = array[0];
	else {
		dateStrings.date = array[0];
		timeString = array[1];
		if (patterns.timeZoneDelimiter.test(dateStrings.date)) {
			dateStrings.date = dateString.split(patterns.timeZoneDelimiter)[0];
			timeString = dateString.substr(dateStrings.date.length, dateString.length);
		}
	}
	if (timeString) {
		const token = patterns.timezone.exec(timeString);
		if (token) {
			dateStrings.time = timeString.replace(token[1], "");
			dateStrings.timezone = token[1];
		} else dateStrings.time = timeString;
	}
	return dateStrings;
}
function parseYear(dateString, additionalDigits) {
	const regex = new RegExp("^(?:(\\d{4}|[+-]\\d{" + (4 + additionalDigits) + "})|(\\d{2}|[+-]\\d{" + (2 + additionalDigits) + "})$)");
	const captures = dateString.match(regex);
	if (!captures) return {
		year: NaN,
		restDateString: ""
	};
	const year = captures[1] ? parseInt(captures[1]) : null;
	const century = captures[2] ? parseInt(captures[2]) : null;
	return {
		year: century === null ? year : century * 100,
		restDateString: dateString.slice((captures[1] || captures[2]).length)
	};
}
function parseDate(dateString, year) {
	if (year === null) return /* @__PURE__ */ new Date(NaN);
	const captures = dateString.match(dateRegex);
	if (!captures) return /* @__PURE__ */ new Date(NaN);
	const isWeekDate = !!captures[4];
	const dayOfYear = parseDateUnit(captures[1]);
	const month = parseDateUnit(captures[2]) - 1;
	const day = parseDateUnit(captures[3]);
	const week = parseDateUnit(captures[4]);
	const dayOfWeek = parseDateUnit(captures[5]) - 1;
	if (isWeekDate) {
		if (!validateWeekDate(year, week, dayOfWeek)) return /* @__PURE__ */ new Date(NaN);
		return dayOfISOWeekYear(year, week, dayOfWeek);
	} else {
		const date = /* @__PURE__ */ new Date(0);
		if (!validateDate(year, month, day) || !validateDayOfYearDate(year, dayOfYear)) return /* @__PURE__ */ new Date(NaN);
		date.setUTCFullYear(year, month, Math.max(dayOfYear, day));
		return date;
	}
}
function parseDateUnit(value) {
	return value ? parseInt(value) : 1;
}
function parseTime(timeString) {
	const captures = timeString.match(timeRegex);
	if (!captures) return NaN;
	const hours = parseTimeUnit(captures[1]);
	const minutes = parseTimeUnit(captures[2]);
	const seconds = parseTimeUnit(captures[3]);
	if (!validateTime(hours, minutes, seconds)) return NaN;
	return hours * millisecondsInHour + minutes * millisecondsInMinute + seconds * 1e3;
}
function parseTimeUnit(value) {
	return value && parseFloat(value.replace(",", ".")) || 0;
}
function parseTimezone(timezoneString) {
	if (timezoneString === "Z") return 0;
	const captures = timezoneString.match(timezoneRegex);
	if (!captures) return 0;
	const sign = captures[1] === "+" ? -1 : 1;
	const hours = parseInt(captures[2]);
	const minutes = captures[3] && parseInt(captures[3]) || 0;
	if (!validateTimezone(hours, minutes)) return NaN;
	return sign * (hours * millisecondsInHour + minutes * millisecondsInMinute);
}
function dayOfISOWeekYear(isoWeekYear, week, day) {
	const date = /* @__PURE__ */ new Date(0);
	date.setUTCFullYear(isoWeekYear, 0, 4);
	const fourthOfJanuaryDay = date.getUTCDay() || 7;
	const diff = (week - 1) * 7 + day + 1 - fourthOfJanuaryDay;
	date.setUTCDate(date.getUTCDate() + diff);
	return date;
}
var daysInMonths = [
	31,
	null,
	31,
	30,
	31,
	30,
	31,
	31,
	30,
	31,
	30,
	31
];
function isLeapYearIndex(year) {
	return year % 400 === 0 || year % 4 === 0 && year % 100 !== 0;
}
function validateDate(year, month, date) {
	return month >= 0 && month <= 11 && date >= 1 && date <= (daysInMonths[month] || (isLeapYearIndex(year) ? 29 : 28));
}
function validateDayOfYearDate(year, dayOfYear) {
	return dayOfYear >= 1 && dayOfYear <= (isLeapYearIndex(year) ? 366 : 365);
}
function validateWeekDate(_year, week, day) {
	return week >= 1 && week <= 53 && day >= 0 && day <= 6;
}
function validateTime(hours, minutes, seconds) {
	if (hours === 24) return minutes === 0 && seconds === 0;
	return seconds >= 0 && seconds < 60 && minutes >= 0 && minutes < 60 && hours >= 0 && hours < 25;
}
function validateTimezone(_hours, minutes) {
	return minutes >= 0 && minutes <= 59;
}
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/bn-IN.js
var require_bn_IN = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "টাকা",
					plural: "টাকা",
					singular: "টাকা",
					symbol: "৳",
					fractionalUnit: {
						name: "পয়সা",
						singular: "পয়সা",
						plural: "পয়সা",
						symbol: ""
					}
				},
				texts: {
					and: "এবং",
					minus: "ঋণ",
					only: "",
					point: "দশমিক"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "কোটি"
					},
					{
						number: 1e5,
						value: "লাখ"
					},
					{
						number: 1e3,
						value: "হাজার"
					},
					{
						number: 100,
						value: "শত"
					},
					{
						number: 99,
						value: "নিরানব্বই"
					},
					{
						number: 98,
						value: "আটানব্বই"
					},
					{
						number: 97,
						value: "সাতানব্বই"
					},
					{
						number: 96,
						value: "ছিয়ানব্বই"
					},
					{
						number: 95,
						value: "পঁচানব্বই"
					},
					{
						number: 94,
						value: "চুরানব্বই"
					},
					{
						number: 93,
						value: "তিরানব্বই"
					},
					{
						number: 92,
						value: "বিরানব্বই"
					},
					{
						number: 91,
						value: "একানব্বই"
					},
					{
						number: 90,
						value: "নব্বই"
					},
					{
						number: 89,
						value: "ঊননব্বই"
					},
					{
						number: 88,
						value: "অষ্টআশি"
					},
					{
						number: 87,
						value: "সাতআশি"
					},
					{
						number: 86,
						value: "ছিয়াশী"
					},
					{
						number: 85,
						value: "পঁচাশী"
					},
					{
						number: 84,
						value: "চুরাশী"
					},
					{
						number: 83,
						value: "তিরাশী"
					},
					{
						number: 82,
						value: "বিরাশী"
					},
					{
						number: 81,
						value: "একাশী"
					},
					{
						number: 80,
						value: "আশি"
					},
					{
						number: 79,
						value: "ঊনআশি"
					},
					{
						number: 78,
						value: "অষ্টাত্তর"
					},
					{
						number: 77,
						value: "সাতাত্তর"
					},
					{
						number: 76,
						value: "ছিয়াত্তর"
					},
					{
						number: 75,
						value: "পঁচাত্তর"
					},
					{
						number: 74,
						value: "চুয়াত্তর"
					},
					{
						number: 73,
						value: "তিয়াত্তর"
					},
					{
						number: 72,
						value: "বাহাত্তর"
					},
					{
						number: 71,
						value: "একাত্তর"
					},
					{
						number: 70,
						value: "সত্তর"
					},
					{
						number: 69,
						value: "ঊনসত্তর"
					},
					{
						number: 68,
						value: "অষ্টষষ্টি"
					},
					{
						number: 67,
						value: "সাতষষ্টি"
					},
					{
						number: 66,
						value: "ছেষষ্টি"
					},
					{
						number: 65,
						value: "পঁয়ষট্টি"
					},
					{
						number: 64,
						value: "চৌষট্টি"
					},
					{
						number: 63,
						value: "তেষট্টি"
					},
					{
						number: 62,
						value: "বাষট্টি"
					},
					{
						number: 61,
						value: "একষট্টি"
					},
					{
						number: 60,
						value: "ষাট"
					},
					{
						number: 59,
						value: "ঊনষাট"
					},
					{
						number: 58,
						value: "আটান্ন"
					},
					{
						number: 57,
						value: "সাতান্ন"
					},
					{
						number: 56,
						value: "ছাপ্পান্ন"
					},
					{
						number: 55,
						value: "পঞ্চান্ন"
					},
					{
						number: 54,
						value: "চুয়ান্ন"
					},
					{
						number: 53,
						value: "তিপ্পান্ন"
					},
					{
						number: 52,
						value: "বায়ান্ন"
					},
					{
						number: 51,
						value: "একান্ন"
					},
					{
						number: 50,
						value: "পঞ্চাশ"
					},
					{
						number: 49,
						value: "ঊনপঞ্চাশ"
					},
					{
						number: 48,
						value: "আটচল্লিশ"
					},
					{
						number: 47,
						value: "সাতচল্লিশ"
					},
					{
						number: 46,
						value: "ছেচল্লিশ"
					},
					{
						number: 45,
						value: "পঁইঁতাল্লিশ"
					},
					{
						number: 44,
						value: "চুয়াল্লিশ"
					},
					{
						number: 43,
						value: "তেতাল্লিশ"
					},
					{
						number: 42,
						value: "বিয়াল্লিশ"
					},
					{
						number: 41,
						value: "একচল্লিশ"
					},
					{
						number: 40,
						value: "চল্লিশ"
					},
					{
						number: 39,
						value: "ঊনচল্লিশ"
					},
					{
						number: 38,
						value: "আটত্রিশ"
					},
					{
						number: 37,
						value: "সাঁইত্রিশ"
					},
					{
						number: 36,
						value: "ছত্রিশ"
					},
					{
						number: 35,
						value: "পঁইত্রিশ"
					},
					{
						number: 34,
						value: "চৌত্রিশ"
					},
					{
						number: 33,
						value: "তেত্রিশ"
					},
					{
						number: 32,
						value: "বত্রিশ"
					},
					{
						number: 31,
						value: "একত্রিশ"
					},
					{
						number: 30,
						value: "ত্রিশ"
					},
					{
						number: 29,
						value: "ঊনত্রিশ"
					},
					{
						number: 28,
						value: "আঠাশ"
					},
					{
						number: 27,
						value: "সাতাশ"
					},
					{
						number: 26,
						value: "ছাব্বিশ"
					},
					{
						number: 25,
						value: "পঁচিশ"
					},
					{
						number: 24,
						value: "চব্বিশ"
					},
					{
						number: 23,
						value: "তেইশ"
					},
					{
						number: 22,
						value: "বাইশ"
					},
					{
						number: 21,
						value: "একুশ"
					},
					{
						number: 20,
						value: "বিশ"
					},
					{
						number: 19,
						value: "ঊনিশ"
					},
					{
						number: 18,
						value: "আঠারো"
					},
					{
						number: 17,
						value: "সতেরো"
					},
					{
						number: 16,
						value: "ষোলো"
					},
					{
						number: 15,
						value: "পনেরো"
					},
					{
						number: 14,
						value: "চৌদ্দ"
					},
					{
						number: 13,
						value: "তেরো"
					},
					{
						number: 12,
						value: "বারো"
					},
					{
						number: 11,
						value: "এগারো"
					},
					{
						number: 10,
						value: "দশ"
					},
					{
						number: 9,
						value: "নয়"
					},
					{
						number: 8,
						value: "আট"
					},
					{
						number: 7,
						value: "সাত"
					},
					{
						number: 6,
						value: "ছয়"
					},
					{
						number: 5,
						value: "পাঁচ"
					},
					{
						number: 4,
						value: "চার"
					},
					{
						number: 3,
						value: "তিন"
					},
					{
						number: 2,
						value: "দুই"
					},
					{
						number: 1,
						value: "এক"
					},
					{
						number: 0,
						value: "শূন্য"
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/ee-EE.js
var require_ee_EE = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Euro",
					plural: "Eurot",
					singular: "Euro",
					symbol: "€",
					fractionalUnit: {
						name: "Sent",
						singular: "Sent",
						plural: "Senti",
						symbol: "¢"
					}
				},
				texts: {
					and: "Ja",
					minus: "Miinus",
					only: "Ainult",
					point: "Koma"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Kvadriljon"
					},
					{
						number: 0xe8d4a51000,
						value: "Triljon"
					},
					{
						number: 1e9,
						value: "Miljard"
					},
					{
						number: 1e6,
						value: "Miljon"
					},
					{
						number: 1e3,
						value: "Tuhat"
					},
					{
						number: 900,
						value: "Üheksasada"
					},
					{
						number: 800,
						value: "Kaheksasada"
					},
					{
						number: 700,
						value: "Seitsesada"
					},
					{
						number: 600,
						value: "Kuussada"
					},
					{
						number: 500,
						value: "Viissada"
					},
					{
						number: 400,
						value: "Nelisada"
					},
					{
						number: 300,
						value: "Kolmsada"
					},
					{
						number: 200,
						value: "Kakssada"
					},
					{
						number: 100,
						value: "Sada"
					},
					{
						number: 90,
						value: "Üheksakümmend"
					},
					{
						number: 80,
						value: "Kaheksakümmend"
					},
					{
						number: 70,
						value: "Seitsekümmend"
					},
					{
						number: 60,
						value: "Kuuskümmend"
					},
					{
						number: 50,
						value: "Viiskümmend"
					},
					{
						number: 40,
						value: "Nelikümmend"
					},
					{
						number: 30,
						value: "Kolmkümmend"
					},
					{
						number: 20,
						value: "Kakskümmend"
					},
					{
						number: 19,
						value: "Üheksateist"
					},
					{
						number: 18,
						value: "Kaheksateist"
					},
					{
						number: 17,
						value: "Seitseteist"
					},
					{
						number: 16,
						value: "Kuusteist"
					},
					{
						number: 15,
						value: "Viisteist"
					},
					{
						number: 14,
						value: "Neliteist"
					},
					{
						number: 13,
						value: "Kolmteist"
					},
					{
						number: 12,
						value: "Kaksteist"
					},
					{
						number: 11,
						value: "Üksteist"
					},
					{
						number: 10,
						value: "Kümme"
					},
					{
						number: 9,
						value: "Üheksa"
					},
					{
						number: 8,
						value: "Kaheksa"
					},
					{
						number: 7,
						value: "Seitse"
					},
					{
						number: 6,
						value: "Kuus"
					},
					{
						number: 5,
						value: "Viis"
					},
					{
						number: 4,
						value: "Neli"
					},
					{
						number: 3,
						value: "Kolm"
					},
					{
						number: 2,
						value: "Kaks"
					},
					{
						number: 1,
						value: "Üks"
					},
					{
						number: 0,
						value: "Null"
					}
				],
				ignoreOneForWords: [
					"Sada",
					"Kakssada",
					"Kolmsada",
					"Nelisada",
					"Viissada",
					"Kuussada",
					"Seitsesada",
					"Kaheksasada",
					"Üheksasada"
				],
				exactWordsMapping: [{
					number: 100,
					value: "Ükssada"
				}],
				pluralWords: [
					"Miljon",
					"Miljard",
					"Trillion",
					"Kvadriljon"
				],
				pluralMark: "it",
				onlyInFront: true
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-AE.js
var require_en_AE = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Dirham",
					plural: "Dirhams",
					singular: "Dirham",
					symbol: "",
					fractionalUnit: {
						name: "Fil",
						singular: "Fil",
						plural: "Fils",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-BD.js
var require_en_BD = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Taka",
					plural: "Taka",
					singular: "Taka",
					symbol: "৳",
					fractionalUnit: {
						name: "Poysha",
						singular: "Poysha",
						plural: "Poysha",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "Crore"
					},
					{
						number: 1e5,
						value: "Lakh"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-GB.js
var require_en_GB = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Pound",
					plural: "Pounds",
					singular: "Pound",
					symbol: "£",
					fractionalUnit: {
						name: "Pence",
						plural: "Pence",
						singular: "Penny",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-GH.js
var require_en_GH = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Cedi",
					plural: "Cedis",
					singular: "Cedi",
					symbol: "GH¢",
					fractionalUnit: {
						name: "Pesewa",
						singular: "Pesewa",
						plural: "Pesewas",
						symbol: "Gp"
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-IE.js
var require_en_IE = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Euro",
					plural: "Euros",
					singular: "Euro",
					symbol: "€",
					fractionalUnit: {
						name: "Eurocent",
						plural: "Eurocents",
						singular: "Eurocent",
						symbol: "¢"
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-IN.js
var require_en_IN = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Rupee",
					plural: "Rupees",
					singular: "Rupee",
					symbol: "₹",
					fractionalUnit: {
						name: "Paisa",
						plural: "Paise",
						singular: "Paisa",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "Crore"
					},
					{
						number: 1e5,
						value: "Lakh"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-MM.js
var require_en_MM = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Kyat",
					plural: "Kyats",
					singular: "Kyat",
					symbol: "K",
					fractionalUnit: {
						name: "Pya",
						singular: "Pya",
						plural: "Pyas",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-MU.js
var require_en_MU = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Rupee",
					plural: "Rupees",
					singular: "Rupee",
					symbol: "Rs",
					fractionalUnit: {
						name: "Cent",
						singular: "Cent",
						plural: "Cents",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "Crore"
					},
					{
						number: 1e5,
						value: "Lakh"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-NG.js
var require_en_NG = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Naira",
					plural: "Naira",
					singular: "Naira",
					symbol: "₦",
					fractionalUnit: {
						name: "Kobo",
						singular: "Kobo",
						plural: "Kobo",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-NP.js
var require_en_NP = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Rupee",
					plural: "Rupees",
					singular: "Rupee",
					symbol: "₹",
					fractionalUnit: {
						name: "Paisa",
						singular: "Paisa",
						plural: "Paisa",
						symbol: ""
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 1e11,
						value: "Kharab"
					},
					{
						number: 1e9,
						value: "Arab"
					},
					{
						number: 1e7,
						value: "Crore"
					},
					{
						number: 1e5,
						value: "Lakh"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-PH.js
var require_en_PH = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Peso",
					plural: "Pesos",
					singular: "Peso",
					symbol: "₱",
					fractionalUnit: {
						name: "Cent",
						singular: "Cent",
						plural: "Cents",
						symbol: "¢"
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/en-US.js
var require_en_US = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Dollar",
					plural: "Dollars",
					singular: "Dollar",
					symbol: "$",
					fractionalUnit: {
						name: "Cent",
						plural: "Cents",
						singular: "Cent",
						symbol: "¢"
					}
				},
				texts: {
					and: "And",
					minus: "Minus",
					only: "Only",
					point: "Point"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrillion"
					},
					{
						number: 0xe8d4a51000,
						value: "Trillion"
					},
					{
						number: 1e9,
						value: "Billion"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Thousand"
					},
					{
						number: 100,
						value: "Hundred"
					},
					{
						number: 90,
						value: "Ninety"
					},
					{
						number: 80,
						value: "Eighty"
					},
					{
						number: 70,
						value: "Seventy"
					},
					{
						number: 60,
						value: "Sixty"
					},
					{
						number: 50,
						value: "Fifty"
					},
					{
						number: 40,
						value: "Forty"
					},
					{
						number: 30,
						value: "Thirty"
					},
					{
						number: 20,
						value: "Twenty"
					},
					{
						number: 19,
						value: "Nineteen"
					},
					{
						number: 18,
						value: "Eighteen"
					},
					{
						number: 17,
						value: "Seventeen"
					},
					{
						number: 16,
						value: "Sixteen"
					},
					{
						number: 15,
						value: "Fifteen"
					},
					{
						number: 14,
						value: "Fourteen"
					},
					{
						number: 13,
						value: "Thirteen"
					},
					{
						number: 12,
						value: "Twelve"
					},
					{
						number: 11,
						value: "Eleven"
					},
					{
						number: 10,
						value: "Ten"
					},
					{
						number: 9,
						value: "Nine"
					},
					{
						number: 8,
						value: "Eight"
					},
					{
						number: 7,
						value: "Seven"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Five"
					},
					{
						number: 4,
						value: "Four"
					},
					{
						number: 3,
						value: "Three"
					},
					{
						number: 2,
						value: "Two"
					},
					{
						number: 1,
						value: "One"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "One Hundred"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/es-AR.js
var require_es_AR = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Peso Argentino",
					plural: "Pesos Argentinos",
					singular: "Peso Argentino",
					symbol: "$",
					fractionalUnit: {
						name: "Centavo",
						plural: "Centavos",
						singular: "Centavo",
						symbol: "¢"
					}
				},
				texts: {
					and: "Y",
					minus: "Menos",
					only: "",
					point: "Coma"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Trillon"
					},
					{
						number: 0xe8d4a51000,
						value: "Billon"
					},
					{
						number: 1e6,
						value: "Millon"
					},
					{
						number: 1e3,
						value: "Mil"
					},
					{
						number: 900,
						value: "Novecientos"
					},
					{
						number: 800,
						value: "Ochocientos"
					},
					{
						number: 700,
						value: "Setecientos"
					},
					{
						number: 600,
						value: "Seiscientos"
					},
					{
						number: 500,
						value: "Quinientos"
					},
					{
						number: 400,
						value: "Cuatrocientos"
					},
					{
						number: 300,
						value: "Trescientos"
					},
					{
						number: 200,
						value: "Doscientos"
					},
					{
						number: 100,
						value: "Ciento"
					},
					{
						number: 90,
						value: "Noventa Y"
					},
					{
						number: 80,
						value: "Ochenta Y"
					},
					{
						number: 70,
						value: "Setenta Y"
					},
					{
						number: 60,
						value: "Sesenta Y"
					},
					{
						number: 50,
						value: "Cincuenta Y"
					},
					{
						number: 40,
						value: "Cuarenta Y"
					},
					{
						number: 30,
						value: "Treinta Y"
					},
					{
						number: 29,
						value: "Veintinueve"
					},
					{
						number: 28,
						value: "Veintiocho"
					},
					{
						number: 27,
						value: "Veintisiete"
					},
					{
						number: 26,
						value: "Veintiseis"
					},
					{
						number: 25,
						value: "Veinticinco"
					},
					{
						number: 24,
						value: "Veinticuatro"
					},
					{
						number: 23,
						value: "Veintitrés"
					},
					{
						number: 22,
						value: "Veintidos"
					},
					{
						number: 21,
						value: "Veintiuno"
					},
					{
						number: 20,
						value: "Veinte"
					},
					{
						number: 19,
						value: "Diecinueve"
					},
					{
						number: 18,
						value: "Dieciocho"
					},
					{
						number: 17,
						value: "Dieciciete"
					},
					{
						number: 16,
						value: "Dieciseis"
					},
					{
						number: 15,
						value: "Quince"
					},
					{
						number: 14,
						value: "Catorce"
					},
					{
						number: 13,
						value: "Trece"
					},
					{
						number: 12,
						value: "Doce"
					},
					{
						number: 11,
						value: "Once"
					},
					{
						number: 10,
						value: "Diez"
					},
					{
						number: 9,
						value: "Nueve"
					},
					{
						number: 8,
						value: "Ocho"
					},
					{
						number: 7,
						value: "Siete"
					},
					{
						number: 6,
						value: "Seis"
					},
					{
						number: 5,
						value: "Cinco"
					},
					{
						number: 4,
						value: "Cuatro"
					},
					{
						number: 3,
						value: "Tres"
					},
					{
						number: 2,
						value: "Dos"
					},
					{
						number: 1,
						value: "Uno"
					},
					{
						number: 0,
						value: "Cero"
					}
				],
				ignoreOneForWords: [
					"Cien",
					"Ciento",
					"Doscientos",
					"Trescientos",
					"Cuatrocientos",
					"Quinientos",
					"Seiscientos",
					"Setecientos",
					"Ochocientos",
					"Novecientos",
					"Mil"
				],
				pluralMark: "es",
				pluralWords: [
					"Millon",
					"Billon",
					"Trillon"
				],
				exactWordsMapping: [
					{
						number: 100,
						value: "Cien"
					},
					{
						number: 90,
						value: "Noventa"
					},
					{
						number: 80,
						value: "Ochenta"
					},
					{
						number: 70,
						value: "Setenta"
					},
					{
						number: 60,
						value: "Sesenta"
					},
					{
						number: 50,
						value: "Cincuenta"
					},
					{
						number: 40,
						value: "Cuarenta"
					},
					{
						number: 30,
						value: "Treinta"
					},
					{
						number: 1,
						value: ["Un", "Uno"]
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/es-ES.js
var require_es_ES = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Euro",
					plural: "Euros",
					singular: "Euro",
					symbol: "€",
					fractionalUnit: {
						name: "Centimo",
						plural: "Centimos",
						singular: "Centimo",
						symbol: "¢"
					}
				},
				texts: {
					and: "Y",
					minus: "Menos",
					only: "",
					point: "Punto"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Trillon"
					},
					{
						number: 0xe8d4a51000,
						value: "Billon"
					},
					{
						number: 1e6,
						value: "Millon"
					},
					{
						number: 1e3,
						value: "Mil"
					},
					{
						number: 900,
						value: "Novecientos"
					},
					{
						number: 800,
						value: "Ochocientos"
					},
					{
						number: 700,
						value: "Setecientos"
					},
					{
						number: 600,
						value: "Seiscientos"
					},
					{
						number: 500,
						value: "Quinientos"
					},
					{
						number: 400,
						value: "Cuatrocientos"
					},
					{
						number: 300,
						value: "Trescientos"
					},
					{
						number: 200,
						value: "Doscientos"
					},
					{
						number: 100,
						value: "Ciento"
					},
					{
						number: 90,
						value: "Noventa Y"
					},
					{
						number: 80,
						value: "Ochenta Y"
					},
					{
						number: 70,
						value: "Setenta Y"
					},
					{
						number: 60,
						value: "Sesenta Y"
					},
					{
						number: 50,
						value: "Cincuenta Y"
					},
					{
						number: 40,
						value: "Cuarenta Y"
					},
					{
						number: 30,
						value: "Treinta Y"
					},
					{
						number: 29,
						value: "Veintinueve"
					},
					{
						number: 28,
						value: "Veintiocho"
					},
					{
						number: 27,
						value: "Veintisiete"
					},
					{
						number: 26,
						value: "Veintiseis"
					},
					{
						number: 25,
						value: "Veinticinco"
					},
					{
						number: 24,
						value: "Veinticuatro"
					},
					{
						number: 23,
						value: "Veintitrés"
					},
					{
						number: 22,
						value: "Veintidos"
					},
					{
						number: 21,
						value: "Veintiuno"
					},
					{
						number: 20,
						value: "Veinte"
					},
					{
						number: 19,
						value: "Diecinueve"
					},
					{
						number: 18,
						value: "Dieciocho"
					},
					{
						number: 17,
						value: "Dieciciete"
					},
					{
						number: 16,
						value: "Dieciseis"
					},
					{
						number: 15,
						value: "Quince"
					},
					{
						number: 14,
						value: "Catorce"
					},
					{
						number: 13,
						value: "Trece"
					},
					{
						number: 12,
						value: "Doce"
					},
					{
						number: 11,
						value: "Once"
					},
					{
						number: 10,
						value: "Diez"
					},
					{
						number: 9,
						value: "Nueve"
					},
					{
						number: 8,
						value: "Ocho"
					},
					{
						number: 7,
						value: "Siete"
					},
					{
						number: 6,
						value: "Seis"
					},
					{
						number: 5,
						value: "Cinco"
					},
					{
						number: 4,
						value: "Cuatro"
					},
					{
						number: 3,
						value: "Tres"
					},
					{
						number: 2,
						value: "Dos"
					},
					{
						number: 1,
						value: "Uno"
					},
					{
						number: 0,
						value: "Cero"
					}
				],
				ignoreOneForWords: [
					"Cien",
					"Ciento",
					"Doscientos",
					"Trescientos",
					"Cuatrocientos",
					"Quinientos",
					"Seiscientos",
					"Setecientos",
					"Ochocientos",
					"Novecientos",
					"Mil"
				],
				pluralMark: "es",
				pluralWords: [
					"Millon",
					"Billon",
					"Trillon"
				],
				exactWordsMapping: [
					{
						number: 100,
						value: "Cien"
					},
					{
						number: 90,
						value: "Noventa"
					},
					{
						number: 80,
						value: "Ochenta"
					},
					{
						number: 70,
						value: "Setenta"
					},
					{
						number: 60,
						value: "Sesenta"
					},
					{
						number: 50,
						value: "Cincuenta"
					},
					{
						number: 40,
						value: "Cuarenta"
					},
					{
						number: 30,
						value: "Treinta"
					},
					{
						number: 1,
						value: ["Un", "Uno"]
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/es-MX.js
var require_es_MX = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Peso Mexicano",
					plural: "Pesos Mexicanos",
					singular: "Peso Mexicano",
					symbol: "$",
					fractionalUnit: {
						name: "Centavo",
						plural: "Centavos",
						singular: "Centavo",
						symbol: "¢"
					}
				},
				texts: {
					and: "Y",
					minus: "Menos",
					only: "",
					point: "Punto"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Trillon"
					},
					{
						number: 0xe8d4a51000,
						value: "Billon"
					},
					{
						number: 1e6,
						value: "Millon"
					},
					{
						number: 1e3,
						value: "Mil"
					},
					{
						number: 900,
						value: "Novecientos"
					},
					{
						number: 800,
						value: "Ochocientos"
					},
					{
						number: 700,
						value: "Setecientos"
					},
					{
						number: 600,
						value: "Seiscientos"
					},
					{
						number: 500,
						value: "Quinientos"
					},
					{
						number: 400,
						value: "Cuatrocientos"
					},
					{
						number: 300,
						value: "Trescientos"
					},
					{
						number: 200,
						value: "Doscientos"
					},
					{
						number: 100,
						value: "Ciento"
					},
					{
						number: 90,
						value: "Noventa Y"
					},
					{
						number: 80,
						value: "Ochenta Y"
					},
					{
						number: 70,
						value: "Setenta Y"
					},
					{
						number: 60,
						value: "Sesenta Y"
					},
					{
						number: 50,
						value: "Cincuenta Y"
					},
					{
						number: 40,
						value: "Cuarenta Y"
					},
					{
						number: 30,
						value: "Treinta Y"
					},
					{
						number: 29,
						value: "Veintinueve"
					},
					{
						number: 28,
						value: "Veintiocho"
					},
					{
						number: 27,
						value: "Veintisiete"
					},
					{
						number: 26,
						value: "Veintiseis"
					},
					{
						number: 25,
						value: "Veinticinco"
					},
					{
						number: 24,
						value: "Veinticuatro"
					},
					{
						number: 23,
						value: "Veintitrés"
					},
					{
						number: 22,
						value: "Veintidos"
					},
					{
						number: 21,
						value: "Veintiuno"
					},
					{
						number: 20,
						value: "Veinte"
					},
					{
						number: 19,
						value: "Diecinueve"
					},
					{
						number: 18,
						value: "Dieciocho"
					},
					{
						number: 17,
						value: "Dieciciete"
					},
					{
						number: 16,
						value: "Dieciseis"
					},
					{
						number: 15,
						value: "Quince"
					},
					{
						number: 14,
						value: "Catorce"
					},
					{
						number: 13,
						value: "Trece"
					},
					{
						number: 12,
						value: "Doce"
					},
					{
						number: 11,
						value: "Once"
					},
					{
						number: 10,
						value: "Diez"
					},
					{
						number: 9,
						value: "Nueve"
					},
					{
						number: 8,
						value: "Ocho"
					},
					{
						number: 7,
						value: "Siete"
					},
					{
						number: 6,
						value: "Seis"
					},
					{
						number: 5,
						value: "Cinco"
					},
					{
						number: 4,
						value: "Cuatro"
					},
					{
						number: 3,
						value: "Tres"
					},
					{
						number: 2,
						value: "Dos"
					},
					{
						number: 1,
						value: "Uno"
					},
					{
						number: 0,
						value: "Cero"
					}
				],
				ignoreOneForWords: [
					"Cien",
					"Ciento",
					"Doscientos",
					"Trescientos",
					"Cuatrocientos",
					"Quinientos",
					"Seiscientos",
					"Setecientos",
					"Ochocientos",
					"Novecientos",
					"Mil"
				],
				pluralMark: "es",
				pluralWords: [
					"Millon",
					"Billon",
					"Trillon"
				],
				exactWordsMapping: [
					{
						number: 100,
						value: "Cien"
					},
					{
						number: 90,
						value: "Noventa"
					},
					{
						number: 80,
						value: "Ochenta"
					},
					{
						number: 70,
						value: "Setenta"
					},
					{
						number: 60,
						value: "Sesenta"
					},
					{
						number: 50,
						value: "Cincuenta"
					},
					{
						number: 40,
						value: "Cuarenta"
					},
					{
						number: 30,
						value: "Treinta"
					},
					{
						number: 1,
						value: ["Un", "Uno"]
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/fa-IR.js
var require_fa_IR = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "تومان",
					plural: "",
					singular: "",
					symbol: "تومان",
					fractionalUnit: {
						name: "",
						singular: "ریال",
						plural: "تومان",
						symbol: ""
					}
				},
				texts: {
					and: "و",
					minus: "منفی",
					only: "",
					point: "و"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "کوادریلیون"
					},
					{
						number: 0xe8d4a51000,
						value: "تیلیارد"
					},
					{
						number: 1e9,
						value: "میلیارد"
					},
					{
						number: 1e6,
						value: "میلیون"
					},
					{
						number: 1e3,
						value: "هزار"
					},
					{
						number: 900,
						value: "نهصد"
					},
					{
						number: 800,
						value: "هشتصد"
					},
					{
						number: 700,
						value: "هفتصد"
					},
					{
						number: 600,
						value: "ششصد"
					},
					{
						number: 500,
						value: "پانصد"
					},
					{
						number: 400,
						value: "چهارصد"
					},
					{
						number: 300,
						value: "سیصد"
					},
					{
						number: 200,
						value: "دویست"
					},
					{
						number: 100,
						value: "صد"
					},
					{
						number: 90,
						value: "نود"
					},
					{
						number: 80,
						value: "هشتاد"
					},
					{
						number: 70,
						value: "هفتاد"
					},
					{
						number: 60,
						value: "شصت"
					},
					{
						number: 50,
						value: "پنجاه"
					},
					{
						number: 40,
						value: "چهل"
					},
					{
						number: 30,
						value: "سی"
					},
					{
						number: 20,
						value: "بیست"
					},
					{
						number: 19,
						value: "نوزده"
					},
					{
						number: 18,
						value: "هجده"
					},
					{
						number: 17,
						value: "هفده"
					},
					{
						number: 16,
						value: "شانزده"
					},
					{
						number: 15,
						value: "پانزده"
					},
					{
						number: 14,
						value: "چهارده"
					},
					{
						number: 13,
						value: "سیزده"
					},
					{
						number: 12,
						value: "دوازده"
					},
					{
						number: 11,
						value: "یازده"
					},
					{
						number: 10,
						value: "ده"
					},
					{
						number: 9,
						value: "نه"
					},
					{
						number: 8,
						value: "هشت"
					},
					{
						number: 7,
						value: "هفت"
					},
					{
						number: 6,
						value: "شش"
					},
					{
						number: 5,
						value: "پنج"
					},
					{
						number: 4,
						value: "چهار"
					},
					{
						number: 3,
						value: "سه"
					},
					{
						number: 2,
						value: "دو"
					},
					{
						number: 1,
						value: "یک"
					},
					{
						number: 0,
						value: "صفر"
					}
				],
				namedLessThan1000: true,
				splitWord: "و",
				ignoreZeroInDecimals: true,
				decimalLengthWordMapping: {
					1: "دهم",
					2: "صدم",
					3: "هزارم",
					4: "ده‌هزارم",
					5: "صد‌هزارم",
					6: "میلیونیوم",
					7: "ده‌میلیونیوم",
					8: "صد‌میلیونیوم",
					9: "میلیاردیوم"
				}
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/fr-BE.js
var require_fr_BE = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Euro",
					plural: "Euros",
					singular: "Euro",
					symbol: "€",
					fractionalUnit: {
						name: "Centime",
						singular: "Centime",
						plural: "Centimes",
						symbol: "Cts"
					}
				},
				texts: {
					and: "Et",
					minus: "Moins",
					only: "",
					point: "Virgule"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Billiard"
					},
					{
						number: 0xe8d4a51000,
						value: "Billion"
					},
					{
						number: 1e9,
						value: "Milliard"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Mille"
					},
					{
						number: 100,
						value: "Cent"
					},
					{
						number: 99,
						value: "Nonante-Neuf"
					},
					{
						number: 98,
						value: "Nonante-Huit"
					},
					{
						number: 97,
						value: "Nonante-Sept"
					},
					{
						number: 96,
						value: "Nonante-Six"
					},
					{
						number: 95,
						value: "Nonante-Cinq"
					},
					{
						number: 94,
						value: "Nonante-Quatre"
					},
					{
						number: 93,
						value: "Nonante-Trois"
					},
					{
						number: 92,
						value: "Nonante-Deux"
					},
					{
						number: 91,
						value: "Nonante-Et-Un"
					},
					{
						number: 90,
						value: "Nonante"
					},
					{
						number: 89,
						value: "Quatre-Vingt-Neuf"
					},
					{
						number: 88,
						value: "Quatre-Vingt-Huit"
					},
					{
						number: 87,
						value: "Quatre-Vingt-Sept"
					},
					{
						number: 86,
						value: "Quatre-Vingt-Six"
					},
					{
						number: 85,
						value: "Quatre-Vingt-Cinq"
					},
					{
						number: 84,
						value: "Quatre-Vingt-Quatre"
					},
					{
						number: 83,
						value: "Quatre-Vingt-Trois"
					},
					{
						number: 82,
						value: "Quatre-Vingt-Deux"
					},
					{
						number: 81,
						value: "Quatre-Vingt-Un"
					},
					{
						number: 80,
						value: "Quatre-Vingt"
					},
					{
						number: 79,
						value: "Septante-Neuf"
					},
					{
						number: 78,
						value: "Septante-Huit"
					},
					{
						number: 77,
						value: "Septante-Sept"
					},
					{
						number: 76,
						value: "Septante-Six"
					},
					{
						number: 75,
						value: "Septante-Cinq"
					},
					{
						number: 74,
						value: "Septante-Quatre"
					},
					{
						number: 73,
						value: "Septante-Trois"
					},
					{
						number: 72,
						value: "Septante-Deux"
					},
					{
						number: 71,
						value: "Septante-Et-Un"
					},
					{
						number: 70,
						value: "Septante"
					},
					{
						number: 69,
						value: "Soixante-Neuf"
					},
					{
						number: 68,
						value: "Soixante-Huit"
					},
					{
						number: 67,
						value: "Soixante-Sept"
					},
					{
						number: 66,
						value: "Soixante-Six"
					},
					{
						number: 65,
						value: "Soixante-Cinq"
					},
					{
						number: 64,
						value: "Soixante-Quatre"
					},
					{
						number: 63,
						value: "Soixante-Trois"
					},
					{
						number: 62,
						value: "Soixante-Deux"
					},
					{
						number: 61,
						value: "Soixante-Et-Un"
					},
					{
						number: 60,
						value: "Soixante"
					},
					{
						number: 59,
						value: "Cinquante-Neuf"
					},
					{
						number: 58,
						value: "Cinquante-Huit"
					},
					{
						number: 57,
						value: "Cinquante-Sept"
					},
					{
						number: 56,
						value: "Cinquante-Six"
					},
					{
						number: 55,
						value: "Cinquante-Cinq"
					},
					{
						number: 54,
						value: "Cinquante-Quatre"
					},
					{
						number: 53,
						value: "Cinquante-Trois"
					},
					{
						number: 52,
						value: "Cinquante-Deux"
					},
					{
						number: 51,
						value: "Cinquante-Et-Un"
					},
					{
						number: 50,
						value: "Cinquante"
					},
					{
						number: 49,
						value: "Quarante-Neuf"
					},
					{
						number: 48,
						value: "Quarante-Huit"
					},
					{
						number: 47,
						value: "Quarante-Sept"
					},
					{
						number: 46,
						value: "Quarante-Six"
					},
					{
						number: 45,
						value: "Quarante-Cinq"
					},
					{
						number: 44,
						value: "Quarante-Quatre"
					},
					{
						number: 43,
						value: "Quarante-Trois"
					},
					{
						number: 42,
						value: "Quarante-Deux"
					},
					{
						number: 41,
						value: "Quarante-Et-Un"
					},
					{
						number: 40,
						value: "Quarante"
					},
					{
						number: 39,
						value: "Trente-Neuf"
					},
					{
						number: 38,
						value: "Trente-Huit"
					},
					{
						number: 37,
						value: "Trente-Sept"
					},
					{
						number: 36,
						value: "Trente-Six"
					},
					{
						number: 35,
						value: "Trente-Cinq"
					},
					{
						number: 34,
						value: "Trente-Quatre"
					},
					{
						number: 33,
						value: "Trente-Trois"
					},
					{
						number: 32,
						value: "Trente-Deux"
					},
					{
						number: 31,
						value: "Trente-Et-Un"
					},
					{
						number: 30,
						value: "Trente"
					},
					{
						number: 29,
						value: "Vingt-Neuf"
					},
					{
						number: 28,
						value: "Vingt-Huit"
					},
					{
						number: 27,
						value: "Vingt-Sept"
					},
					{
						number: 26,
						value: "Vingt-Six"
					},
					{
						number: 25,
						value: "Vingt-Cinq"
					},
					{
						number: 24,
						value: "Vingt-Quatre"
					},
					{
						number: 23,
						value: "Vingt-Trois"
					},
					{
						number: 22,
						value: "Vingt-Deux"
					},
					{
						number: 21,
						value: "Vingt-Et-Un"
					},
					{
						number: 20,
						value: "Vingt"
					},
					{
						number: 19,
						value: "Dix-Neuf"
					},
					{
						number: 18,
						value: "Dix-Huit"
					},
					{
						number: 17,
						value: "Dix-Sept"
					},
					{
						number: 16,
						value: "Seize"
					},
					{
						number: 15,
						value: "Quinze"
					},
					{
						number: 14,
						value: "Quatorze"
					},
					{
						number: 13,
						value: "Treize"
					},
					{
						number: 12,
						value: "Douze"
					},
					{
						number: 11,
						value: "Onze"
					},
					{
						number: 10,
						value: "Dix"
					},
					{
						number: 9,
						value: "Neuf"
					},
					{
						number: 8,
						value: "Huit"
					},
					{
						number: 7,
						value: "Sept"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Cinq"
					},
					{
						number: 4,
						value: "Quatre"
					},
					{
						number: 3,
						value: "Trois"
					},
					{
						number: 2,
						value: "Deux"
					},
					{
						number: 1,
						value: "Un"
					},
					{
						number: 0,
						value: "Zéro"
					}
				],
				ignoreOneForWords: ["Cent", "Mille"],
				pluralMark: "s",
				pluralWords: [
					"Billiard",
					"Billion",
					"Milliard",
					"Million"
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/fr-FR.js
var require_fr_FR = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Euro",
					plural: "Euros",
					singular: "Euro",
					symbol: "€",
					fractionalUnit: {
						name: "Centime",
						singular: "Centime",
						plural: "Centimes",
						symbol: "Cts"
					}
				},
				texts: {
					and: "Et",
					minus: "Moins",
					only: "",
					point: "Virgule"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Billiard"
					},
					{
						number: 0xe8d4a51000,
						value: "Billion"
					},
					{
						number: 1e9,
						value: "Milliard"
					},
					{
						number: 1e6,
						value: "Million"
					},
					{
						number: 1e3,
						value: "Mille"
					},
					{
						number: 100,
						value: "Cent"
					},
					{
						number: 99,
						value: "Quatre-Vingt-Dix-Neuf"
					},
					{
						number: 98,
						value: "Quatre-Vingt-Dix-Huit"
					},
					{
						number: 97,
						value: "Quatre-Vingt-Dix-Sept"
					},
					{
						number: 96,
						value: "Quatre-Vingt-Seize"
					},
					{
						number: 95,
						value: "Quatre-Vingt-Quinze"
					},
					{
						number: 94,
						value: "Quatre-Vingt-Quatorze"
					},
					{
						number: 93,
						value: "Quatre-Vingt-Treize"
					},
					{
						number: 92,
						value: "Quatre-Vingt-Douze"
					},
					{
						number: 91,
						value: "Quatre-Vingt-Onze"
					},
					{
						number: 90,
						value: "Quatre-Vingt-Dix"
					},
					{
						number: 89,
						value: "Quatre-Vingt-Neuf"
					},
					{
						number: 88,
						value: "Quatre-Vingt-Huit"
					},
					{
						number: 87,
						value: "Quatre-Vingt-Sept"
					},
					{
						number: 86,
						value: "Quatre-Vingt-Six"
					},
					{
						number: 85,
						value: "Quatre-Vingt-Cinq"
					},
					{
						number: 84,
						value: "Quatre-Vingt-Quatre"
					},
					{
						number: 83,
						value: "Quatre-Vingt-Trois"
					},
					{
						number: 82,
						value: "Quatre-Vingt-Deux"
					},
					{
						number: 81,
						value: "Quatre-Vingt-Un"
					},
					{
						number: 80,
						value: "Quatre-Vingt"
					},
					{
						number: 79,
						value: "Soixante-Dix-Neuf"
					},
					{
						number: 78,
						value: "Soixante-Dix-Huit"
					},
					{
						number: 77,
						value: "Soixante-Dix-Sept"
					},
					{
						number: 76,
						value: "Soixante-Seize"
					},
					{
						number: 75,
						value: "Soixante-Quinze"
					},
					{
						number: 74,
						value: "Soixante-Quatorze"
					},
					{
						number: 73,
						value: "Soixante-Treize"
					},
					{
						number: 72,
						value: "Soixante-Douze"
					},
					{
						number: 71,
						value: "Soixante Et Onze"
					},
					{
						number: 70,
						value: "Soixante-dix"
					},
					{
						number: 69,
						value: "Soixante-Neuf"
					},
					{
						number: 68,
						value: "Soixante-Huit"
					},
					{
						number: 67,
						value: "Soixante-Sept"
					},
					{
						number: 66,
						value: "Soixante-Six"
					},
					{
						number: 65,
						value: "Soixante-Cinq"
					},
					{
						number: 64,
						value: "Soixante-Quatre"
					},
					{
						number: 63,
						value: "Soixante-Trois"
					},
					{
						number: 62,
						value: "Soixante-Deux"
					},
					{
						number: 61,
						value: "Soixante Et Un"
					},
					{
						number: 60,
						value: "Soixante"
					},
					{
						number: 59,
						value: "Cinquante-Neuf"
					},
					{
						number: 58,
						value: "Cinquante-Huit"
					},
					{
						number: 57,
						value: "Cinquante-Sept"
					},
					{
						number: 56,
						value: "Cinquante-Six"
					},
					{
						number: 55,
						value: "Cinquante-Cinq"
					},
					{
						number: 54,
						value: "Cinquante-Quatre"
					},
					{
						number: 53,
						value: "Cinquante-Trois"
					},
					{
						number: 52,
						value: "Cinquante-Deux"
					},
					{
						number: 51,
						value: "Cinquante Et Un"
					},
					{
						number: 50,
						value: "Cinquante"
					},
					{
						number: 49,
						value: "Quarante-Neuf"
					},
					{
						number: 48,
						value: "Quarante-Huit"
					},
					{
						number: 47,
						value: "Quarante-Sept"
					},
					{
						number: 46,
						value: "Quarante-Six"
					},
					{
						number: 45,
						value: "Quarante-Cinq"
					},
					{
						number: 44,
						value: "Quarante-Quatre"
					},
					{
						number: 43,
						value: "Quarante-Trois"
					},
					{
						number: 42,
						value: "Quarante-Deux"
					},
					{
						number: 41,
						value: "Quarante Et Un"
					},
					{
						number: 40,
						value: "Quarante"
					},
					{
						number: 39,
						value: "Trente-Neuf"
					},
					{
						number: 38,
						value: "Trente-Huit"
					},
					{
						number: 37,
						value: "Trente-Sept"
					},
					{
						number: 36,
						value: "Trente-Six"
					},
					{
						number: 35,
						value: "Trente-Cinq"
					},
					{
						number: 34,
						value: "Trente-Quatre"
					},
					{
						number: 33,
						value: "Trente-Trois"
					},
					{
						number: 32,
						value: "Trente-Deux"
					},
					{
						number: 31,
						value: "Trente Et Un"
					},
					{
						number: 30,
						value: "Trente"
					},
					{
						number: 29,
						value: "Vingt-Neuf"
					},
					{
						number: 28,
						value: "Vingt-Huit"
					},
					{
						number: 27,
						value: "Vingt-Sept"
					},
					{
						number: 26,
						value: "Vingt-Six"
					},
					{
						number: 25,
						value: "Vingt-Cinq"
					},
					{
						number: 24,
						value: "Vingt-Quatre"
					},
					{
						number: 23,
						value: "Vingt-Trois"
					},
					{
						number: 22,
						value: "Vingt-Deux"
					},
					{
						number: 21,
						value: "Vingt Et Un"
					},
					{
						number: 20,
						value: "Vingt"
					},
					{
						number: 19,
						value: "Dix-Neuf"
					},
					{
						number: 18,
						value: "Dix-Huit"
					},
					{
						number: 17,
						value: "Dix-Sept"
					},
					{
						number: 16,
						value: "Seize"
					},
					{
						number: 15,
						value: "Quinze"
					},
					{
						number: 14,
						value: "Quatorze"
					},
					{
						number: 13,
						value: "Treize"
					},
					{
						number: 12,
						value: "Douze"
					},
					{
						number: 11,
						value: "Onze"
					},
					{
						number: 10,
						value: "Dix"
					},
					{
						number: 9,
						value: "Neuf"
					},
					{
						number: 8,
						value: "Huit"
					},
					{
						number: 7,
						value: "Sept"
					},
					{
						number: 6,
						value: "Six"
					},
					{
						number: 5,
						value: "Cinq"
					},
					{
						number: 4,
						value: "Quatre"
					},
					{
						number: 3,
						value: "Trois"
					},
					{
						number: 2,
						value: "Deux"
					},
					{
						number: 1,
						value: "Un"
					},
					{
						number: 0,
						value: "Zéro"
					}
				],
				ignoreOneForWords: ["Cent", "Mille"],
				pluralMark: "s",
				pluralWords: [
					"Billiard",
					"Billion",
					"Milliard",
					"Million"
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/gu-IN.js
var require_gu_IN = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "રૂપિયો",
					plural: "રૂપિયા",
					singular: "રૂપિયો",
					symbol: "₹",
					fractionalUnit: {
						name: "પૈસા",
						singular: "પૈસા",
						plural: "પૈસા",
						symbol: ""
					}
				},
				texts: {
					and: "અને",
					minus: "ઋણ",
					only: "",
					point: "દશાંશ"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "કરોડ"
					},
					{
						number: 1e5,
						value: "લાખ"
					},
					{
						number: 1e3,
						value: "હજાર"
					},
					{
						number: 100,
						value: "સો"
					},
					{
						number: 99,
						value: "નવ્વાણું"
					},
					{
						number: 98,
						value: "અઠ્ઠાણું"
					},
					{
						number: 97,
						value: "સત્તાણું"
					},
					{
						number: 96,
						value: "છન્નું"
					},
					{
						number: 95,
						value: "પંચાણું"
					},
					{
						number: 94,
						value: "ચોરાણું"
					},
					{
						number: 93,
						value: "ત્રાણું"
					},
					{
						number: 92,
						value: "બાણું"
					},
					{
						number: 91,
						value: "એકાણું"
					},
					{
						number: 90,
						value: "નેવું"
					},
					{
						number: 89,
						value: "નેવ્યાસી"
					},
					{
						number: 88,
						value: "ઈઠ્યાસી"
					},
					{
						number: 87,
						value: "સિત્યાસી"
					},
					{
						number: 86,
						value: "છ્યાસી"
					},
					{
						number: 85,
						value: "પંચ્યાસી"
					},
					{
						number: 84,
						value: "ચોર્યાસી"
					},
					{
						number: 83,
						value: "ત્યાસી"
					},
					{
						number: 82,
						value: "બ્યાસી"
					},
					{
						number: 81,
						value: "એક્યાસી"
					},
					{
						number: 80,
						value: "એંસી"
					},
					{
						number: 79,
						value: "ઓગણાએંસી"
					},
					{
						number: 78,
						value: "ઇઠ્યોતેર"
					},
					{
						number: 77,
						value: "સિત્યોતેર"
					},
					{
						number: 76,
						value: "છોતેર"
					},
					{
						number: 75,
						value: "પંચોતેર"
					},
					{
						number: 74,
						value: "ચુમોતેર"
					},
					{
						number: 73,
						value: "તોતેર"
					},
					{
						number: 72,
						value: "બોતેર"
					},
					{
						number: 71,
						value: "એકોતેર"
					},
					{
						number: 70,
						value: "સિત્તેર"
					},
					{
						number: 69,
						value: "ઓગણોસિત્તેર"
					},
					{
						number: 68,
						value: "અડસઠ"
					},
					{
						number: 67,
						value: "સડસઠ"
					},
					{
						number: 66,
						value: "છાસઠ"
					},
					{
						number: 65,
						value: "પાંસઠ"
					},
					{
						number: 64,
						value: "ચોસઠ"
					},
					{
						number: 63,
						value: "ત્રેસઠ"
					},
					{
						number: 62,
						value: "બાસઠ"
					},
					{
						number: 61,
						value: "એકસઠ"
					},
					{
						number: 60,
						value: "સાઈઠ"
					},
					{
						number: 59,
						value: "ઓગણસાઠ"
					},
					{
						number: 58,
						value: "અઠ્ઠાવન"
					},
					{
						number: 57,
						value: "સત્તાવન"
					},
					{
						number: 56,
						value: "છપ્પન"
					},
					{
						number: 55,
						value: "પંચાવન"
					},
					{
						number: 54,
						value: "ચોપન"
					},
					{
						number: 53,
						value: "ત્રેપન"
					},
					{
						number: 52,
						value: "બાવન"
					},
					{
						number: 51,
						value: "એકાવન"
					},
					{
						number: 50,
						value: "પચાસ"
					},
					{
						number: 49,
						value: "ઓગણપચાસ"
					},
					{
						number: 48,
						value: "સુડતાલીસ"
					},
					{
						number: 47,
						value: "સુડતાલીસ"
					},
					{
						number: 46,
						value: "છેતાલીસ"
					},
					{
						number: 45,
						value: "પિસ્તાલીસ"
					},
					{
						number: 44,
						value: "ચુંમાલીસ"
					},
					{
						number: 43,
						value: "ત્રેતાલીસ"
					},
					{
						number: 42,
						value: "એકતાલીસ"
					},
					{
						number: 41,
						value: "એકતાલીસ"
					},
					{
						number: 40,
						value: "ચાલીસ"
					},
					{
						number: 39,
						value: "ઓગણચાલીસ"
					},
					{
						number: 38,
						value: "આડત્રીસ"
					},
					{
						number: 37,
						value: "સાડત્રીસ"
					},
					{
						number: 36,
						value: "છત્રીસ"
					},
					{
						number: 35,
						value: "પાંત્રીસ"
					},
					{
						number: 34,
						value: "ચોત્રીસ"
					},
					{
						number: 33,
						value: "તેત્રીસ"
					},
					{
						number: 32,
						value: "બત્રીસ"
					},
					{
						number: 31,
						value: "એકત્રીસ"
					},
					{
						number: 30,
						value: "ત્રીસ"
					},
					{
						number: 29,
						value: "ઓગણત્રીસ"
					},
					{
						number: 28,
						value: "અઠ્ઠાવીસ"
					},
					{
						number: 27,
						value: "સત્તાવીસ"
					},
					{
						number: 26,
						value: "છવીસ"
					},
					{
						number: 25,
						value: "પચ્ચીસ"
					},
					{
						number: 24,
						value: "ચોવીસ"
					},
					{
						number: 23,
						value: "તેવીસ"
					},
					{
						number: 22,
						value: "બાવીસ"
					},
					{
						number: 21,
						value: "એકવીસ"
					},
					{
						number: 20,
						value: "વીસ"
					},
					{
						number: 19,
						value: "ઓગણિસ"
					},
					{
						number: 18,
						value: "અઢાર"
					},
					{
						number: 17,
						value: "સત્તર"
					},
					{
						number: 16,
						value: "સોળ"
					},
					{
						number: 15,
						value: "પંદર"
					},
					{
						number: 14,
						value: "ચૌદ"
					},
					{
						number: 13,
						value: "તેર"
					},
					{
						number: 12,
						value: "બાર"
					},
					{
						number: 11,
						value: "અગિયાર"
					},
					{
						number: 10,
						value: "દસ"
					},
					{
						number: 9,
						value: "નવ"
					},
					{
						number: 8,
						value: "આઠ"
					},
					{
						number: 7,
						value: "સાત"
					},
					{
						number: 6,
						value: "છ"
					},
					{
						number: 5,
						value: "પાંચ"
					},
					{
						number: 4,
						value: "ચાર"
					},
					{
						number: 3,
						value: "ત્રણ"
					},
					{
						number: 2,
						value: "બે"
					},
					{
						number: 1,
						value: "એક"
					},
					{
						number: 0,
						value: "શૂન્ય"
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/hi-IN.js
var require_hi_IN = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "रुपया",
					plural: "रुपये",
					singular: "रुपया",
					symbol: "₹",
					fractionalUnit: {
						name: "पैसा",
						singular: "पैसा",
						plural: "पैसे",
						symbol: ""
					}
				},
				texts: {
					and: "और",
					minus: "ऋण",
					only: "",
					point: "दशांश"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "करोड़"
					},
					{
						number: 1e5,
						value: "लाख"
					},
					{
						number: 1e3,
						value: "हज़ार"
					},
					{
						number: 100,
						value: "सौ"
					},
					{
						number: 99,
						value: "निन्यानवे"
					},
					{
						number: 98,
						value: "अट्ठानवे"
					},
					{
						number: 97,
						value: "सत्तानवे"
					},
					{
						number: 96,
						value: "छियानवे"
					},
					{
						number: 95,
						value: "पचानवे"
					},
					{
						number: 94,
						value: "चौरानवे"
					},
					{
						number: 93,
						value: "तिरानवे"
					},
					{
						number: 92,
						value: "बानवे"
					},
					{
						number: 91,
						value: "इक्यानबे"
					},
					{
						number: 90,
						value: "नब्बे"
					},
					{
						number: 89,
						value: "नवासी"
					},
					{
						number: 88,
						value: "अठासी"
					},
					{
						number: 87,
						value: "सतासी"
					},
					{
						number: 86,
						value: "छियासी"
					},
					{
						number: 85,
						value: "पचासी"
					},
					{
						number: 84,
						value: "चौरासी"
					},
					{
						number: 83,
						value: "तिरासी"
					},
					{
						number: 82,
						value: "बयासी"
					},
					{
						number: 81,
						value: "इक्यासी"
					},
					{
						number: 80,
						value: "अस्सी"
					},
					{
						number: 79,
						value: "उनासी"
					},
					{
						number: 78,
						value: "अठहत्तर"
					},
					{
						number: 77,
						value: "सतहत्तर"
					},
					{
						number: 76,
						value: "छिहत्तर"
					},
					{
						number: 75,
						value: "पचहत्तर"
					},
					{
						number: 74,
						value: "चौहत्तर"
					},
					{
						number: 73,
						value: "तिहत्तर"
					},
					{
						number: 72,
						value: "बहत्तर"
					},
					{
						number: 71,
						value: "इकहत्तर"
					},
					{
						number: 70,
						value: "सत्तर"
					},
					{
						number: 69,
						value: "उनहत्तर"
					},
					{
						number: 68,
						value: "अड़सठ"
					},
					{
						number: 67,
						value: "सड़सठ"
					},
					{
						number: 66,
						value: "छियासठ"
					},
					{
						number: 65,
						value: "पैंसठ"
					},
					{
						number: 64,
						value: "चौंसठ"
					},
					{
						number: 63,
						value: "तिरसठ"
					},
					{
						number: 62,
						value: "बासठ"
					},
					{
						number: 61,
						value: "इकसठ"
					},
					{
						number: 60,
						value: "साठ"
					},
					{
						number: 59,
						value: "उनसठ"
					},
					{
						number: 58,
						value: "अट्ठावन"
					},
					{
						number: 57,
						value: "सत्तावन"
					},
					{
						number: 56,
						value: "छप्पन"
					},
					{
						number: 55,
						value: "पचपन"
					},
					{
						number: 54,
						value: "चौबन"
					},
					{
						number: 53,
						value: "तिरेपन"
					},
					{
						number: 52,
						value: "बावन"
					},
					{
						number: 51,
						value: "इक्याबन"
					},
					{
						number: 50,
						value: "पचास"
					},
					{
						number: 49,
						value: "उनचास"
					},
					{
						number: 48,
						value: "अड़तालीस"
					},
					{
						number: 47,
						value: "सैंतालीस"
					},
					{
						number: 46,
						value: "छियालीस"
					},
					{
						number: 45,
						value: "पैंतालीस"
					},
					{
						number: 44,
						value: "चौंतालीस"
					},
					{
						number: 43,
						value: "तैंतालीस"
					},
					{
						number: 42,
						value: "बयालीस"
					},
					{
						number: 41,
						value: "इकतालीस"
					},
					{
						number: 40,
						value: "चालीस"
					},
					{
						number: 39,
						value: "उनतालीस"
					},
					{
						number: 38,
						value: "अड़तीस"
					},
					{
						number: 37,
						value: "सैंतीस"
					},
					{
						number: 36,
						value: "छत्तीस"
					},
					{
						number: 35,
						value: "पैंतीस"
					},
					{
						number: 34,
						value: "चौंतीस"
					},
					{
						number: 33,
						value: "तैंतीस"
					},
					{
						number: 32,
						value: "बत्तीस"
					},
					{
						number: 31,
						value: "इकतीस"
					},
					{
						number: 30,
						value: "तीस"
					},
					{
						number: 29,
						value: "उनतीस"
					},
					{
						number: 28,
						value: "अट्ठाईस"
					},
					{
						number: 27,
						value: "सत्ताईस"
					},
					{
						number: 26,
						value: "छब्बीस"
					},
					{
						number: 25,
						value: "पच्चीस"
					},
					{
						number: 24,
						value: "चौबीस"
					},
					{
						number: 23,
						value: "तेईस"
					},
					{
						number: 22,
						value: "बाईस"
					},
					{
						number: 21,
						value: "इक्कीस"
					},
					{
						number: 20,
						value: "बीस"
					},
					{
						number: 19,
						value: "उन्नीस"
					},
					{
						number: 18,
						value: "अठारह"
					},
					{
						number: 17,
						value: "सत्रह"
					},
					{
						number: 16,
						value: "सोलह"
					},
					{
						number: 15,
						value: "पंद्रह"
					},
					{
						number: 14,
						value: "चौदह"
					},
					{
						number: 13,
						value: "तेरह"
					},
					{
						number: 12,
						value: "बारह"
					},
					{
						number: 11,
						value: "ग्यारह"
					},
					{
						number: 10,
						value: "दस"
					},
					{
						number: 9,
						value: "नौ"
					},
					{
						number: 8,
						value: "आठ"
					},
					{
						number: 7,
						value: "सात"
					},
					{
						number: 6,
						value: "छह"
					},
					{
						number: 5,
						value: "पांच"
					},
					{
						number: 4,
						value: "चार"
					},
					{
						number: 3,
						value: "तीन"
					},
					{
						number: 2,
						value: "दो"
					},
					{
						number: 1,
						value: "एक"
					},
					{
						number: 0,
						value: "शून्य"
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/kn-IN.js
var require_kn_IN = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "ರೂಪಾಯಿ",
					plural: "ರೂಪಾಯಿಗಳು",
					singular: "ರೂಪಾಯಿ",
					symbol: "₹",
					fractionalUnit: {
						name: "ಪೈಸೆ",
						singular: "ಪೈಸೆ",
						plural: "ಪೈಸೆಗಳು",
						symbol: ""
					}
				},
				texts: {
					and: "ಮತ್ತು",
					minus: "ಋಣ",
					only: "",
					point: "ದಶಾಂಶ"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "ಕೋಟಿ"
					},
					{
						number: 1e5,
						value: "ಲಕ್ಷ"
					},
					{
						number: 1e3,
						value: "ಸಾವಿರ"
					},
					{
						number: 100,
						value: "ನೂರು"
					},
					{
						number: 99,
						value: "ತೊಂಬತ್ತೊಂಬತ್ತು"
					},
					{
						number: 98,
						value: "ತೊಂಬತ್ತೆಂಟು"
					},
					{
						number: 97,
						value: "ತೊಂಬತ್ತೇಳು"
					},
					{
						number: 96,
						value: "ತೊಂಬತ್ತಾರು"
					},
					{
						number: 95,
						value: "ತೊಂಬತ್ತೈದು"
					},
					{
						number: 94,
						value: "ತೊಂಬತ್ತನಾಲ್ಕು"
					},
					{
						number: 93,
						value: "ತೊಂಬತ್ತಮೂರು"
					},
					{
						number: 92,
						value: "ತೊಂಬತ್ತೆರಡು"
					},
					{
						number: 91,
						value: "ತೊಂಬತ್ತೊಂದು"
					},
					{
						number: 90,
						value: "ತೊಂಬತ್ತು"
					},
					{
						number: 89,
						value: "ಎಂಭತ್ತೊಂಬತ್ತು"
					},
					{
						number: 88,
						value: "ಎಂಭತ್ತೆಂಟು"
					},
					{
						number: 87,
						value: "ಎಂಭತ್ತೇಳು"
					},
					{
						number: 86,
						value: "ಎಂಭತ್ತಾರು"
					},
					{
						number: 85,
						value: "ಎಂಭತ್ತೈದು"
					},
					{
						number: 84,
						value: "ಎಂಭತ್ತನಾಲ್ಕು"
					},
					{
						number: 83,
						value: "ಎಂಭತ್ತಮೂರು"
					},
					{
						number: 82,
						value: "ಎಂಭತ್ತೆರಡು"
					},
					{
						number: 81,
						value: "ಎಂಭತ್ತೊಂದು"
					},
					{
						number: 80,
						value: "ಎಂಭತ್ತು"
					},
					{
						number: 79,
						value: "ಎಪ್ಪತ್ತೊಂಬತ್ತು"
					},
					{
						number: 78,
						value: "ಎಪ್ಪತ್ತೆಂಟು"
					},
					{
						number: 77,
						value: "ಎಪ್ಪತ್ತೇಳು"
					},
					{
						number: 76,
						value: "ಎಪ್ಪತ್ತಾರು"
					},
					{
						number: 75,
						value: "ಎಪ್ಪತ್ತೈದು"
					},
					{
						number: 74,
						value: "ಎಪ್ಪತ್ತನಾಲ್ಕು"
					},
					{
						number: 73,
						value: "ಎಪ್ಪತ್ತಮೂರು"
					},
					{
						number: 72,
						value: "ಎಪ್ಪತ್ತೆರಡು"
					},
					{
						number: 71,
						value: "ಎಪ್ಪತ್ತೊಂದು"
					},
					{
						number: 70,
						value: "ಎಪ್ಪತ್ತು"
					},
					{
						number: 69,
						value: "ಅರುವತ್ತೊಂಬತ್ತು"
					},
					{
						number: 68,
						value: "ಅರುವತ್ತೆಂಟು"
					},
					{
						number: 67,
						value: "ಅರುವತ್ತೇಳು"
					},
					{
						number: 66,
						value: "ಅರುವತ್ತಾರು"
					},
					{
						number: 65,
						value: "ಅರುವತ್ತೈದು"
					},
					{
						number: 64,
						value: "ಅರುವತ್ತನಾಲ್ಕು"
					},
					{
						number: 63,
						value: "ಅರುವತ್ತಮೂರು"
					},
					{
						number: 62,
						value: "ಅರುವತ್ತೆರಡು"
					},
					{
						number: 61,
						value: "ಅರುವತ್ತೊಂದು"
					},
					{
						number: 60,
						value: "ಅರುವತ್ತು"
					},
					{
						number: 59,
						value: "ಐವತ್ತೊಂಬತ್ತು"
					},
					{
						number: 58,
						value: "ಐವತ್ತೆಂಟು"
					},
					{
						number: 57,
						value: "ಐವತ್ತೇಳು"
					},
					{
						number: 56,
						value: "ಐವತ್ತಾರು"
					},
					{
						number: 55,
						value: "ಐವತ್ತೈದು"
					},
					{
						number: 54,
						value: "ಐವತ್ತನಾಲ್ಕು"
					},
					{
						number: 53,
						value: "ಐವತ್ತಮೂರು"
					},
					{
						number: 52,
						value: "ಐವತ್ತೆರಡು"
					},
					{
						number: 51,
						value: "ಐವತ್ತೊಂದು"
					},
					{
						number: 50,
						value: "ಐವತ್ತು"
					},
					{
						number: 49,
						value: "ನಲವತ್ತೊಂಬತ್ತು"
					},
					{
						number: 48,
						value: "ನಲವತ್ತೆಂಟು"
					},
					{
						number: 47,
						value: "ನಲವತ್ತೇಳು"
					},
					{
						number: 46,
						value: "ನಲವತ್ತಾರು"
					},
					{
						number: 45,
						value: "ನಲವತ್ತೈದು"
					},
					{
						number: 44,
						value: "ನಲವತ್ತನಾಲ್ಕು"
					},
					{
						number: 43,
						value: "ನಲವತ್ತಮೂರು"
					},
					{
						number: 42,
						value: "ನಲವತ್ತೆರಡು"
					},
					{
						number: 41,
						value: "ನಲವತ್ತೊಂದು"
					},
					{
						number: 40,
						value: "ನಲವತ್ತು"
					},
					{
						number: 39,
						value: "ಮೂವತ್ತೊಂಬತ್ತು"
					},
					{
						number: 38,
						value: "ಮೂವತ್ತೆಂಟು"
					},
					{
						number: 37,
						value: "ಮೂವತ್ತೇಳು"
					},
					{
						number: 36,
						value: "ಮೂವತ್ತಾರು"
					},
					{
						number: 35,
						value: "ಮೂವತ್ತೈದು"
					},
					{
						number: 34,
						value: "ಮೂವತ್ತನಾಲ್ಕು"
					},
					{
						number: 33,
						value: "ಮೂವತ್ತಮೂರು"
					},
					{
						number: 32,
						value: "ಮೂವತ್ತೆರಡು"
					},
					{
						number: 31,
						value: "ಮೂವತ್ತೊಂದು"
					},
					{
						number: 30,
						value: "ಮೂವತ್ತು"
					},
					{
						number: 29,
						value: "ಇಪ್ಪತ್ತೊಂಬತ್ತು"
					},
					{
						number: 28,
						value: "ಇಪ್ಪತ್ತೆಂಟು"
					},
					{
						number: 27,
						value: "ಇಪ್ಪತ್ತೇಳು"
					},
					{
						number: 26,
						value: "ಇಪ್ಪತ್ತಾರು"
					},
					{
						number: 25,
						value: "ಇಪ್ಪತ್ತೈದು"
					},
					{
						number: 24,
						value: "ಇಪ್ಪತ್ತನಾಲ್ಕು"
					},
					{
						number: 23,
						value: "ಇಪ್ಪತ್ತಮೂರು"
					},
					{
						number: 22,
						value: "ಇಪ್ಪತ್ತೆರಡು"
					},
					{
						number: 21,
						value: "ಇಪ್ಪತ್ತೊಂದು"
					},
					{
						number: 20,
						value: "ಇಪ್ಪತ್ತು"
					},
					{
						number: 19,
						value: "ಹತ್ತೊಂಬತ್ತು"
					},
					{
						number: 18,
						value: "ಹದಿನೆಂಟು"
					},
					{
						number: 17,
						value: "ಹದಿನೇಳು"
					},
					{
						number: 16,
						value: "ಹದಿನಾರು"
					},
					{
						number: 15,
						value: "ಹದಿನೈದು"
					},
					{
						number: 14,
						value: "ಹದಿನಾಲ್ಕು"
					},
					{
						number: 13,
						value: "ಹದಿಮೂರು"
					},
					{
						number: 12,
						value: "ಹನ್ನೆರಡು"
					},
					{
						number: 11,
						value: "ಹನ್ನೊಂದು"
					},
					{
						number: 10,
						value: "ಹತ್ತು"
					},
					{
						number: 9,
						value: "ಒಂಬತ್ತು"
					},
					{
						number: 8,
						value: "ಎಂಟು"
					},
					{
						number: 7,
						value: "ಏಳು"
					},
					{
						number: 6,
						value: "ಆರು"
					},
					{
						number: 5,
						value: "ಐದು"
					},
					{
						number: 4,
						value: "ನಾಲ್ಕು"
					},
					{
						number: 3,
						value: "ಮೂರು"
					},
					{
						number: 2,
						value: "ಎರಡು"
					},
					{
						number: 1,
						value: "ಒಂದು"
					},
					{
						number: 0,
						value: "ಶೂನ್ಯ"
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/ko-KR.js
var require_ko_KR = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "원",
					plural: "원",
					symbol: "",
					fractionalUnit: {
						name: "",
						plural: "",
						symbol: ""
					}
				},
				texts: {
					and: "하고 ",
					minus: "마이너스",
					only: "",
					point: "점"
				},
				trim: true,
				numberWordsMapping: [
					{
						number: 0xe8d4a51000,
						value: "조"
					},
					{
						number: 1e8,
						value: "억"
					},
					{
						number: 1e4,
						value: "만"
					},
					{
						number: 1e3,
						value: "천"
					},
					{
						number: 100,
						value: "백"
					},
					{
						number: 90,
						value: "구십"
					},
					{
						number: 80,
						value: "팔십"
					},
					{
						number: 70,
						value: "칠십"
					},
					{
						number: 60,
						value: "육십"
					},
					{
						number: 50,
						value: "오십"
					},
					{
						number: 40,
						value: "사십"
					},
					{
						number: 30,
						value: "삼십"
					},
					{
						number: 20,
						value: "이십"
					},
					{
						number: 19,
						value: "십구"
					},
					{
						number: 18,
						value: "십팔"
					},
					{
						number: 17,
						value: "십칠"
					},
					{
						number: 16,
						value: "십육"
					},
					{
						number: 15,
						value: "십오"
					},
					{
						number: 14,
						value: "십사"
					},
					{
						number: 13,
						value: "십삼"
					},
					{
						number: 12,
						value: "십이"
					},
					{
						number: 11,
						value: "십일"
					},
					{
						number: 10,
						value: "십"
					},
					{
						number: 9,
						value: "구"
					},
					{
						number: 8,
						value: "팔"
					},
					{
						number: 7,
						value: "칠"
					},
					{
						number: 6,
						value: "육"
					},
					{
						number: 5,
						value: "오"
					},
					{
						number: 4,
						value: "사"
					},
					{
						number: 3,
						value: "삼"
					},
					{
						number: 2,
						value: "이"
					},
					{
						number: 1,
						value: "일"
					},
					{
						number: 0,
						value: "영"
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/lv-LV.js
var require_lv_LV = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "eiro",
					plural: "eiro",
					symbol: "€",
					fractionalUnit: {
						name: "cents",
						plural: "centi",
						symbol: ""
					}
				},
				texts: {
					and: "un",
					minus: "mīnus",
					only: "",
					point: "komats"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "kvadriljon"
					},
					{
						number: 0xe8d4a51000,
						value: "triljon"
					},
					{
						number: 1e9,
						value: "miljard"
					},
					{
						number: 1e6,
						value: "miljoni",
						singularValue: "miljons"
					},
					{
						number: 1e3,
						value: "tūkstoši",
						singularValue: "tūkstotis"
					},
					{
						number: 900,
						value: "deviņi simti"
					},
					{
						number: 800,
						value: "astoņi simti"
					},
					{
						number: 700,
						value: "septiņi simti"
					},
					{
						number: 600,
						value: "seši simti"
					},
					{
						number: 500,
						value: "pieci simti"
					},
					{
						number: 400,
						value: "četri simti"
					},
					{
						number: 300,
						value: "trīs simti"
					},
					{
						number: 200,
						value: "divi simti"
					},
					{
						number: 100,
						value: "simtu"
					},
					{
						number: 90,
						value: "deviņdesmit"
					},
					{
						number: 80,
						value: "astoņdesmit"
					},
					{
						number: 70,
						value: "septiņdesmit"
					},
					{
						number: 60,
						value: "sešdesmit"
					},
					{
						number: 50,
						value: "piecdesmit"
					},
					{
						number: 40,
						value: "četrdesmit"
					},
					{
						number: 30,
						value: "trīsdesmit"
					},
					{
						number: 20,
						value: "divdesmit"
					},
					{
						number: 19,
						value: "deviņpadsmit"
					},
					{
						number: 18,
						value: "astoņpadsmit"
					},
					{
						number: 17,
						value: "septiņpadsmit"
					},
					{
						number: 16,
						value: "sešpadsmit"
					},
					{
						number: 15,
						value: "piecpadsmit"
					},
					{
						number: 14,
						value: "četrdpadsmit"
					},
					{
						number: 13,
						value: "trīspadsmit"
					},
					{
						number: 12,
						value: "divpadsmit"
					},
					{
						number: 11,
						value: "vienpadsmit"
					},
					{
						number: 10,
						value: "desmit"
					},
					{
						number: 9,
						value: "deviņi"
					},
					{
						number: 8,
						value: "astoņi"
					},
					{
						number: 7,
						value: "septiņi"
					},
					{
						number: 6,
						value: "seši"
					},
					{
						number: 5,
						value: "pieci"
					},
					{
						number: 4,
						value: "četri"
					},
					{
						number: 3,
						value: "trīs"
					},
					{
						number: 2,
						value: "divi"
					},
					{
						number: 1,
						value: "viens"
					},
					{
						number: 0,
						value: "nulle"
					}
				],
				ignoreOneForWords: [
					"simtu",
					"divi simti",
					"trīs simti",
					"četri simti",
					"pieci simti",
					"seši simti",
					"septiņi simti",
					"astoņi simti",
					"deviņi simti"
				],
				exactWordsMapping: [{
					number: 100,
					value: "Simtu"
				}],
				pluralMark: "i",
				pluralWords: [
					"kvadriljon",
					"triljon",
					"miljard"
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/mr-IN.js
var require_mr_IN = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "रुपया",
					plural: "रुपये",
					singular: "रुपया",
					symbol: "₹",
					fractionalUnit: {
						name: "पैसा",
						singular: "पैसा",
						plural: "पैसे",
						symbol: ""
					}
				},
				texts: {
					and: "आणि",
					minus: "वजा",
					only: "",
					point: "पूर्णांक"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "कोटी"
					},
					{
						number: 1e5,
						value: "लाख"
					},
					{
						number: 1e3,
						value: "हजार"
					},
					{
						number: 900,
						value: "नऊशे"
					},
					{
						number: 800,
						value: "आठशे"
					},
					{
						number: 700,
						value: "सातशे"
					},
					{
						number: 600,
						value: "सहाशे"
					},
					{
						number: 500,
						value: "पाचशे"
					},
					{
						number: 400,
						value: "चारशे"
					},
					{
						number: 300,
						value: "तीनशे"
					},
					{
						number: 200,
						value: "दोनशे"
					},
					{
						number: 100,
						value: "एकशे"
					},
					{
						number: 99,
						value: "नव्व्याण्णव"
					},
					{
						number: 98,
						value: "अठ्ठ्याण्णव"
					},
					{
						number: 97,
						value: "सत्त्याण्णव"
					},
					{
						number: 96,
						value: "शहाण्णव"
					},
					{
						number: 95,
						value: "पंच्याण्णव"
					},
					{
						number: 94,
						value: "चौऱ्याण्णव"
					},
					{
						number: 93,
						value: "त्र्याण्णव"
					},
					{
						number: 92,
						value: "ब्याण्णव"
					},
					{
						number: 91,
						value: "एक्क्याण्णव"
					},
					{
						number: 90,
						value: "नव्वद"
					},
					{
						number: 89,
						value: "एकोणनव्वद"
					},
					{
						number: 88,
						value: "अठ्ठ्याऐंशी"
					},
					{
						number: 87,
						value: "सत्त्याऐंशी"
					},
					{
						number: 86,
						value: "शहाऐंशी"
					},
					{
						number: 85,
						value: "पंच्याऐंशी"
					},
					{
						number: 84,
						value: "चौऱ्याऐंशी"
					},
					{
						number: 83,
						value: "त्र्याऐंशी"
					},
					{
						number: 82,
						value: "ब्याऐंशी"
					},
					{
						number: 81,
						value: "एक्क्याऐंशी"
					},
					{
						number: 80,
						value: "ऐंशी"
					},
					{
						number: 79,
						value: "एकोणऐंशी"
					},
					{
						number: 78,
						value: "अठ्ठ्याहत्तर"
					},
					{
						number: 77,
						value: "सत्याहत्तर"
					},
					{
						number: 76,
						value: "शहात्तर"
					},
					{
						number: 75,
						value: "पंच्याहत्तर"
					},
					{
						number: 74,
						value: "चौर्‍याहत्तर"
					},
					{
						number: 73,
						value: "त्र्याहत्तर"
					},
					{
						number: 72,
						value: "बाहत्तर"
					},
					{
						number: 71,
						value: "एक्काहत्तर"
					},
					{
						number: 70,
						value: "सत्तर"
					},
					{
						number: 69,
						value: "एकोणसत्तर"
					},
					{
						number: 68,
						value: "अडुसष्ठ"
					},
					{
						number: 67,
						value: "सदुसष्ठ"
					},
					{
						number: 66,
						value: "सहासष्ठ"
					},
					{
						number: 65,
						value: "पासष्ठ"
					},
					{
						number: 64,
						value: "चौसष्ठ"
					},
					{
						number: 63,
						value: "त्रेसष्ठ"
					},
					{
						number: 62,
						value: "बासष्ठ"
					},
					{
						number: 61,
						value: "एकसष्ठ"
					},
					{
						number: 60,
						value: "साठ"
					},
					{
						number: 59,
						value: "एकोणसाठ"
					},
					{
						number: 58,
						value: "अठ्ठावन्न"
					},
					{
						number: 57,
						value: "सत्तावन्न"
					},
					{
						number: 56,
						value: "छप्पन्न"
					},
					{
						number: 55,
						value: "पंचावन्न"
					},
					{
						number: 54,
						value: "चोपन्न"
					},
					{
						number: 53,
						value: "त्रेपन्न"
					},
					{
						number: 52,
						value: "बावन्न"
					},
					{
						number: 51,
						value: "एक्कावन्न"
					},
					{
						number: 50,
						value: "पन्नास"
					},
					{
						number: 49,
						value: "एकोणपन्नास"
					},
					{
						number: 48,
						value: "अठ्ठेचाळीस"
					},
					{
						number: 47,
						value: "सत्तेचाळीस"
					},
					{
						number: 46,
						value: "सेहेचाळीस"
					},
					{
						number: 45,
						value: "पंचेचाळीस"
					},
					{
						number: 44,
						value: "चव्वेचाळीस"
					},
					{
						number: 43,
						value: "त्रेचाळीस"
					},
					{
						number: 42,
						value: "बेचाळीस"
					},
					{
						number: 41,
						value: "एक्केचाळीस"
					},
					{
						number: 40,
						value: "चाळीस"
					},
					{
						number: 39,
						value: "एकोणचाळीस"
					},
					{
						number: 38,
						value: "अडतीस"
					},
					{
						number: 37,
						value: "सदतीस"
					},
					{
						number: 36,
						value: "छत्तीस"
					},
					{
						number: 35,
						value: "पस्तीस"
					},
					{
						number: 34,
						value: "चौतीस"
					},
					{
						number: 33,
						value: "तेहेतीस"
					},
					{
						number: 32,
						value: "बत्तीस"
					},
					{
						number: 31,
						value: "एकतीस"
					},
					{
						number: 30,
						value: "तीस"
					},
					{
						number: 29,
						value: "एकोणतीस"
					},
					{
						number: 28,
						value: "अठ्ठावीस"
					},
					{
						number: 27,
						value: "सत्तावीस"
					},
					{
						number: 26,
						value: "सव्वीस"
					},
					{
						number: 25,
						value: "पंचवीस"
					},
					{
						number: 24,
						value: "चोवीस"
					},
					{
						number: 23,
						value: "तेवीस"
					},
					{
						number: 22,
						value: "बावीस"
					},
					{
						number: 21,
						value: "एकवीस"
					},
					{
						number: 20,
						value: "वीस"
					},
					{
						number: 19,
						value: "एकोणीस"
					},
					{
						number: 18,
						value: "अठरा"
					},
					{
						number: 17,
						value: "सतरा"
					},
					{
						number: 16,
						value: "सोळा"
					},
					{
						number: 15,
						value: "पंधरा"
					},
					{
						number: 14,
						value: "चौदा"
					},
					{
						number: 13,
						value: "तेरा"
					},
					{
						number: 12,
						value: "बारा"
					},
					{
						number: 11,
						value: "अकरा"
					},
					{
						number: 10,
						value: "दहा"
					},
					{
						number: 9,
						value: "नऊ"
					},
					{
						number: 8,
						value: "आठ"
					},
					{
						number: 7,
						value: "सात"
					},
					{
						number: 6,
						value: "सहा"
					},
					{
						number: 5,
						value: "पाच"
					},
					{
						number: 4,
						value: "चार"
					},
					{
						number: 3,
						value: "तीन"
					},
					{
						number: 2,
						value: "दोन"
					},
					{
						number: 1,
						value: "एक"
					},
					{
						number: 0,
						value: "शून्य"
					}
				],
				namedLessThan1000: true
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/nl-SR.js
var require_nl_SR = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Surinaamse dollar",
					plural: "Surinaamse dollars",
					singular: "Surinaamse dollar",
					symbol: "SRD",
					fractionalUnit: {
						name: "Cent",
						singular: "Cent",
						plural: "Centen",
						symbol: "SRD"
					}
				},
				texts: {
					and: "En",
					minus: "Negatief",
					only: "",
					point: "Punt"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Biljard"
					},
					{
						number: 0xe8d4a51000,
						value: "Biljoen"
					},
					{
						number: 1e9,
						value: "Miljard"
					},
					{
						number: 1e6,
						value: "Miljoen"
					},
					{
						number: 1e3,
						value: "Duizend"
					},
					{
						number: 100,
						value: "Honderd"
					},
					{
						number: 99,
						value: "Negenennegentig"
					},
					{
						number: 98,
						value: "Achtennegentig"
					},
					{
						number: 97,
						value: "Zevenennegentig"
					},
					{
						number: 96,
						value: "Zesennegentig"
					},
					{
						number: 95,
						value: "Vijfennegentig"
					},
					{
						number: 94,
						value: "Vierennegentig"
					},
					{
						number: 93,
						value: "Drieënnegentig"
					},
					{
						number: 92,
						value: "Tweeënnegentig"
					},
					{
						number: 91,
						value: "Eenennegentig"
					},
					{
						number: 90,
						value: "Negentig"
					},
					{
						number: 89,
						value: "Negenentachtig"
					},
					{
						number: 88,
						value: "Achtentachtig"
					},
					{
						number: 87,
						value: "Zevenentachtig"
					},
					{
						number: 86,
						value: "Zesentachtig"
					},
					{
						number: 85,
						value: "Vijfentachtig"
					},
					{
						number: 84,
						value: "Vierentachtig"
					},
					{
						number: 83,
						value: "Drieëntachtig"
					},
					{
						number: 82,
						value: "Tweeëntachtig"
					},
					{
						number: 81,
						value: "Eenentachtig"
					},
					{
						number: 80,
						value: "Tachtig"
					},
					{
						number: 79,
						value: "Negenenzeventig"
					},
					{
						number: 78,
						value: "Achtenzeventig"
					},
					{
						number: 77,
						value: "Zevenenzeventig"
					},
					{
						number: 76,
						value: "Zesenzeventig"
					},
					{
						number: 75,
						value: "Vijfenzeventig"
					},
					{
						number: 74,
						value: "Vierenzeventig"
					},
					{
						number: 73,
						value: "Drieënzeventig"
					},
					{
						number: 72,
						value: "Tweeënzeventig"
					},
					{
						number: 71,
						value: "Eenenzeventig"
					},
					{
						number: 70,
						value: "Zeventig"
					},
					{
						number: 69,
						value: "Negenenzestig"
					},
					{
						number: 68,
						value: "Achtenzestig"
					},
					{
						number: 67,
						value: "Zevenenzestig"
					},
					{
						number: 66,
						value: "Zesenzestig"
					},
					{
						number: 65,
						value: "Vijfenzestig"
					},
					{
						number: 64,
						value: "Vierenzestig"
					},
					{
						number: 63,
						value: "Drieënzestig"
					},
					{
						number: 62,
						value: "Tweeënzestig"
					},
					{
						number: 61,
						value: "Eenenzestig"
					},
					{
						number: 60,
						value: "Zestig"
					},
					{
						number: 59,
						value: "Negenenvijftig"
					},
					{
						number: 58,
						value: "Achtenvijftig"
					},
					{
						number: 57,
						value: "Zevenenvijftig"
					},
					{
						number: 56,
						value: "Zesenvijftig"
					},
					{
						number: 55,
						value: "Vijfenvijftig"
					},
					{
						number: 54,
						value: "Vierenvijftig"
					},
					{
						number: 53,
						value: "Drieënvijftig"
					},
					{
						number: 52,
						value: "Tweeënvijftig"
					},
					{
						number: 51,
						value: "Eenenvijftig"
					},
					{
						number: 50,
						value: "Vijftig"
					},
					{
						number: 49,
						value: "Negenenveertig"
					},
					{
						number: 48,
						value: "Achtenveertig"
					},
					{
						number: 47,
						value: "Zevenenveertig"
					},
					{
						number: 46,
						value: "Zesenveertig"
					},
					{
						number: 45,
						value: "Vijfenveertig"
					},
					{
						number: 44,
						value: "Vierenveertig"
					},
					{
						number: 43,
						value: "Drieënveertig"
					},
					{
						number: 42,
						value: "Tweeënveertig"
					},
					{
						number: 41,
						value: "Eenenveertig"
					},
					{
						number: 40,
						value: "Veertig"
					},
					{
						number: 39,
						value: "Negenendertig"
					},
					{
						number: 38,
						value: "Achtendertig"
					},
					{
						number: 37,
						value: "Zevenendertig"
					},
					{
						number: 36,
						value: "Zesendertig"
					},
					{
						number: 35,
						value: "Vijfendertig"
					},
					{
						number: 34,
						value: "Vierendertig"
					},
					{
						number: 33,
						value: "Drieëndertig"
					},
					{
						number: 32,
						value: "Tweeëndertig"
					},
					{
						number: 31,
						value: "Eenendertig"
					},
					{
						number: 30,
						value: "Dertig"
					},
					{
						number: 29,
						value: "Negenentwintig"
					},
					{
						number: 28,
						value: "Achtentwintig"
					},
					{
						number: 27,
						value: "Zevenentwintig"
					},
					{
						number: 26,
						value: "Zesentwintig"
					},
					{
						number: 25,
						value: "Vijfentwintig"
					},
					{
						number: 24,
						value: "Vierentwintig"
					},
					{
						number: 23,
						value: "Drieëntwintig"
					},
					{
						number: 22,
						value: "Tweeëntwintig"
					},
					{
						number: 21,
						value: "Eenentwintig"
					},
					{
						number: 20,
						value: "Twintig"
					},
					{
						number: 19,
						value: "Negentien"
					},
					{
						number: 18,
						value: "Achttien"
					},
					{
						number: 17,
						value: "Zeventien"
					},
					{
						number: 16,
						value: "Zestien"
					},
					{
						number: 15,
						value: "Vijftien"
					},
					{
						number: 14,
						value: "Veertien"
					},
					{
						number: 13,
						value: "Dertien"
					},
					{
						number: 12,
						value: "Twaalf"
					},
					{
						number: 11,
						value: "Elf"
					},
					{
						number: 10,
						value: "Tien"
					},
					{
						number: 9,
						value: "Negen"
					},
					{
						number: 8,
						value: "Acht"
					},
					{
						number: 7,
						value: "Zeven"
					},
					{
						number: 6,
						value: "Zes"
					},
					{
						number: 5,
						value: "Vijf"
					},
					{
						number: 4,
						value: "Vier"
					},
					{
						number: 3,
						value: "Drie"
					},
					{
						number: 2,
						value: "Twee"
					},
					{
						number: 1,
						value: "Een"
					},
					{
						number: 0,
						value: "Nul"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "Honderd"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/np-NP.js
var require_np_NP = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "रुपैयाँ",
					plural: "रुपैयाँ",
					singular: "रुपैयाँ",
					symbol: "रु",
					fractionalUnit: {
						name: "पैसा",
						singular: "पैसा",
						plural: "पैसा",
						symbol: ""
					}
				},
				texts: {
					and: "र",
					minus: "माइनस",
					only: "मात्र",
					point: "दशमलब"
				},
				numberWordsMapping: [
					{
						number: 1e11,
						value: "खर्ब"
					},
					{
						number: 1e9,
						value: "अर्ब"
					},
					{
						number: 1e7,
						value: "करोड"
					},
					{
						number: 1e5,
						value: "लाख"
					},
					{
						number: 1e3,
						value: "हजार"
					},
					{
						number: 100,
						value: "सय"
					},
					{
						number: 99,
						value: "उनान्सय"
					},
					{
						number: 98,
						value: "अन्ठानब्बे"
					},
					{
						number: 97,
						value: "सन्तानब्बे"
					},
					{
						number: 96,
						value: "छयानब्बे"
					},
					{
						number: 95,
						value: "पन्चानब्बे"
					},
					{
						number: 94,
						value: "चौरानब्बे"
					},
					{
						number: 93,
						value: "त्रियानब्बे"
					},
					{
						number: 92,
						value: "बयान्नब्बे"
					},
					{
						number: 91,
						value: "एकानब्बे"
					},
					{
						number: 90,
						value: "नब्बे"
					},
					{
						number: 89,
						value: "उनान्नब्बे"
					},
					{
						number: 88,
						value: "अठासी"
					},
					{
						number: 87,
						value: "सतासी"
					},
					{
						number: 86,
						value: "छयासी"
					},
					{
						number: 85,
						value: "पचासी"
					},
					{
						number: 84,
						value: "चौरासी"
					},
					{
						number: 83,
						value: "त्रियासी"
					},
					{
						number: 82,
						value: "बयासी"
					},
					{
						number: 81,
						value: "एकासी"
					},
					{
						number: 80,
						value: "अस्सी"
					},
					{
						number: 79,
						value: "उनासी"
					},
					{
						number: 78,
						value: "अठहत्तर"
					},
					{
						number: 77,
						value: "सतहत्तर"
					},
					{
						number: 76,
						value: "छयहत्तर"
					},
					{
						number: 75,
						value: "पचहत्तर"
					},
					{
						number: 74,
						value: "चौहत्तर"
					},
					{
						number: 73,
						value: "त्रिहत्तर"
					},
					{
						number: 72,
						value: "बहत्तर"
					},
					{
						number: 71,
						value: "एकहत्तर"
					},
					{
						number: 70,
						value: "सत्तरी"
					},
					{
						number: 69,
						value: "उनन्सत्तरी"
					},
					{
						number: 68,
						value: "अठसट्ठी"
					},
					{
						number: 67,
						value: "सड्सट्ठी"
					},
					{
						number: 66,
						value: "छैंसट्ठी"
					},
					{
						number: 65,
						value: "पैंसट्ठी"
					},
					{
						number: 64,
						value: "चौंसट्ठी"
					},
					{
						number: 63,
						value: "त्रिसट्ठी"
					},
					{
						number: 62,
						value: "बइसट्ठी"
					},
					{
						number: 61,
						value: "एकसट्ठी"
					},
					{
						number: 60,
						value: "साठी"
					},
					{
						number: 59,
						value: "उनन्साठी"
					},
					{
						number: 58,
						value: "अन्ठाउन्न"
					},
					{
						number: 57,
						value: "सन्ताउन्न"
					},
					{
						number: 56,
						value: "छपन्न"
					},
					{
						number: 55,
						value: "पचपन्न"
					},
					{
						number: 54,
						value: "चवन्न"
					},
					{
						number: 53,
						value: "त्रिपन्न"
					},
					{
						number: 52,
						value: "बाउन्न"
					},
					{
						number: 51,
						value: "एकाउन्न"
					},
					{
						number: 50,
						value: "पचास"
					},
					{
						number: 49,
						value: "उनन्चास"
					},
					{
						number: 48,
						value: "अठचालीस"
					},
					{
						number: 47,
						value: "सट्चालीस"
					},
					{
						number: 46,
						value: "छयालीस"
					},
					{
						number: 45,
						value: "पैंतालीस"
					},
					{
						number: 44,
						value: "चवालीस"
					},
					{
						number: 43,
						value: "त्रिचालीस"
					},
					{
						number: 42,
						value: "बयालीस"
					},
					{
						number: 41,
						value: "एकचालीस"
					},
					{
						number: 40,
						value: "चालीस"
					},
					{
						number: 39,
						value: "उनन्चालीस"
					},
					{
						number: 38,
						value: "अठतीस"
					},
					{
						number: 37,
						value: "सैंतीस"
					},
					{
						number: 36,
						value: "छत्तीस"
					},
					{
						number: 35,
						value: "पैंतीस"
					},
					{
						number: 34,
						value: "चौँतीस"
					},
					{
						number: 33,
						value: "तेत्तीस"
					},
					{
						number: 32,
						value: "बत्तीस"
					},
					{
						number: 31,
						value: "एकतीस"
					},
					{
						number: 30,
						value: "तीस"
					},
					{
						number: 29,
						value: "उनन्तिस"
					},
					{
						number: 28,
						value: "अठ्ठाइस"
					},
					{
						number: 27,
						value: "सत्ताइस"
					},
					{
						number: 26,
						value: "छब्बिस"
					},
					{
						number: 25,
						value: "पच्चिस"
					},
					{
						number: 24,
						value: "चौबीस"
					},
					{
						number: 23,
						value: "तेइस"
					},
					{
						number: 22,
						value: "बाइस"
					},
					{
						number: 21,
						value: "एक्काइस"
					},
					{
						number: 20,
						value: "बीस"
					},
					{
						number: 19,
						value: "उन्नाइस"
					},
					{
						number: 18,
						value: "अठार"
					},
					{
						number: 17,
						value: "सत्र"
					},
					{
						number: 16,
						value: "सोह्र"
					},
					{
						number: 15,
						value: "पन्ध्र"
					},
					{
						number: 14,
						value: "चौध"
					},
					{
						number: 13,
						value: "तेह्र"
					},
					{
						number: 12,
						value: "बाह्र"
					},
					{
						number: 11,
						value: "एघार"
					},
					{
						number: 10,
						value: "दश"
					},
					{
						number: 9,
						value: "नौ"
					},
					{
						number: 8,
						value: "आठ"
					},
					{
						number: 7,
						value: "सात"
					},
					{
						number: 6,
						value: "छ"
					},
					{
						number: 5,
						value: "पाँच"
					},
					{
						number: 4,
						value: "चार"
					},
					{
						number: 3,
						value: "तीन"
					},
					{
						number: 2,
						value: "दुई"
					},
					{
						number: 1,
						value: "एक"
					},
					{
						number: 0,
						value: "शून्य"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "एक सय"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/pt-BR.js
var require_pt_BR = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Real",
					plural: "Reais",
					singular: "Real",
					symbol: "R$",
					fractionalUnit: {
						name: "Centavo",
						singular: "Centavo",
						plural: "Centavos",
						symbol: ""
					}
				},
				texts: {
					and: "E",
					minus: "Menos",
					only: "",
					point: "Vírgula"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Quadrilhões"
					},
					{
						number: 0xe8d4a51000,
						value: "Trilhões"
					},
					{
						number: 1e9,
						value: "Bilhões"
					},
					{
						number: 1e6,
						value: "Milhões"
					},
					{
						number: 1e3,
						value: "Mil"
					},
					{
						number: 900,
						value: "Novecentos"
					},
					{
						number: 800,
						value: "Oitocentos"
					},
					{
						number: 700,
						value: "Setecentos"
					},
					{
						number: 600,
						value: "Seiscentos"
					},
					{
						number: 500,
						value: "Quinhentos"
					},
					{
						number: 400,
						value: "Quatrocentos"
					},
					{
						number: 300,
						value: "Trezentos"
					},
					{
						number: 200,
						value: "Duzentos"
					},
					{
						number: 100,
						value: "Cento"
					},
					{
						number: 90,
						value: "Noventa"
					},
					{
						number: 80,
						value: "Oitenta"
					},
					{
						number: 70,
						value: "Setenta"
					},
					{
						number: 60,
						value: "Sessenta"
					},
					{
						number: 50,
						value: "Cinquenta"
					},
					{
						number: 40,
						value: "Quarenta"
					},
					{
						number: 30,
						value: "Trinta"
					},
					{
						number: 20,
						value: "Vinte"
					},
					{
						number: 19,
						value: "Dezenove"
					},
					{
						number: 18,
						value: "Dezoito"
					},
					{
						number: 17,
						value: "Dezesete"
					},
					{
						number: 16,
						value: "Dezesseis"
					},
					{
						number: 15,
						value: "Quinze"
					},
					{
						number: 14,
						value: "Quatorze"
					},
					{
						number: 13,
						value: "Treze"
					},
					{
						number: 12,
						value: "Doze"
					},
					{
						number: 11,
						value: "Onze"
					},
					{
						number: 10,
						value: "Dez"
					},
					{
						number: 9,
						value: "Nove"
					},
					{
						number: 8,
						value: "Oito"
					},
					{
						number: 7,
						value: "Sete"
					},
					{
						number: 6,
						value: "Seis"
					},
					{
						number: 5,
						value: "Cinco"
					},
					{
						number: 4,
						value: "Quatro"
					},
					{
						number: 3,
						value: "Três"
					},
					{
						number: 2,
						value: "Dois"
					},
					{
						number: 1,
						value: "Um"
					},
					{
						number: 0,
						value: "Zero"
					}
				],
				exactWordsMapping: [
					{
						number: 100,
						value: "Cem"
					},
					{
						number: 0x38d7ea4c68000,
						value: "Um Quadrilhão"
					},
					{
						number: 0xe8d4a51000,
						value: "Um Trilhão"
					},
					{
						number: 1e9,
						value: "Um Bilhão"
					},
					{
						number: 1e6,
						value: "Um Milhão"
					}
				],
				ignoreOneForWords: [
					"Mil",
					"Novecentos",
					"Oitocentos",
					"Setecentos",
					"Seiscentos",
					"Quinhentos",
					"Quatrocentos",
					"Trezentos",
					"Duzentos",
					"Cento"
				],
				splitWord: "E",
				noSplitWordAfter: [
					"Mil",
					"Milhões",
					"Bilhões",
					"Trilhões",
					"Quadrilhões"
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/tr-TR.js
var require_tr_TR = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "lira",
					plural: "",
					singular: "",
					symbol: "₺",
					fractionalUnit: {
						name: "",
						singular: "lira",
						plural: "lira",
						symbol: ""
					}
				},
				texts: {
					and: "virgül",
					minus: "eksi",
					only: "",
					point: "virgül"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "katrilyon"
					},
					{
						number: 0xe8d4a51000,
						value: "trilyon"
					},
					{
						number: 1e9,
						value: "milyar"
					},
					{
						number: 1e6,
						value: "milyon"
					},
					{
						number: 1e3,
						value: "bin"
					},
					{
						number: 900,
						value: "dokuz yüz"
					},
					{
						number: 800,
						value: "sekiz yüz"
					},
					{
						number: 700,
						value: "yedi yüz"
					},
					{
						number: 600,
						value: "altı yüz"
					},
					{
						number: 500,
						value: "beş yüz"
					},
					{
						number: 400,
						value: "dört yüz"
					},
					{
						number: 300,
						value: "üç yüz"
					},
					{
						number: 200,
						value: "iki yüz"
					},
					{
						number: 100,
						value: "yüz"
					},
					{
						number: 90,
						value: "doksan"
					},
					{
						number: 80,
						value: "seksen"
					},
					{
						number: 70,
						value: "yetmiş"
					},
					{
						number: 60,
						value: "altmış"
					},
					{
						number: 50,
						value: "elli"
					},
					{
						number: 40,
						value: "kırk"
					},
					{
						number: 30,
						value: "otuz"
					},
					{
						number: 20,
						value: "yirmi"
					},
					{
						number: 19,
						value: "on dokuz"
					},
					{
						number: 18,
						value: "on sekiz"
					},
					{
						number: 17,
						value: "on yedi"
					},
					{
						number: 16,
						value: "on altı"
					},
					{
						number: 15,
						value: "on beş"
					},
					{
						number: 14,
						value: "on dört"
					},
					{
						number: 13,
						value: "on üç"
					},
					{
						number: 12,
						value: "on iki"
					},
					{
						number: 11,
						value: "on bir"
					},
					{
						number: 10,
						value: "on"
					},
					{
						number: 9,
						value: "dokuz"
					},
					{
						number: 8,
						value: "sekiz"
					},
					{
						number: 7,
						value: "yedi"
					},
					{
						number: 6,
						value: "altı"
					},
					{
						number: 5,
						value: "beş"
					},
					{
						number: 4,
						value: "dört"
					},
					{
						number: 3,
						value: "üç"
					},
					{
						number: 2,
						value: "iki"
					},
					{
						number: 1,
						value: "bir"
					},
					{
						number: 0,
						value: "sıfır"
					}
				],
				namedLessThan1000: true,
				ignoreZeroInDecimals: true,
				decimalLengthWordMapping: {
					1: "onuncu",
					2: "yüzüncü",
					3: "bininci",
					4: "on bininci",
					5: "yüz bininci",
					6: "on milyonuncu",
					7: "milyonuncu",
					8: "yüz milyonuncu"
				}
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/ur-PK.js
var require_ur_PK = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "روپیہ",
					plural: "روپے",
					singular: "روپے",
					symbol: "₨",
					fractionalUnit: {
						name: "پیسہ",
						plural: "پیسے",
						singular: "پیسہ",
						symbol: ""
					}
				},
				texts: {
					and: "اور",
					minus: "منفی",
					only: "صرف",
					point: "اعشاریہ"
				},
				numberWordsMapping: [
					{
						number: 1e7,
						value: "کروڑ"
					},
					{
						number: 1e5,
						value: "لاکھ"
					},
					{
						number: 1e3,
						value: "ہزار"
					},
					{
						number: 100,
						value: "سو"
					},
					{
						number: 99,
						value: "نناوے"
					},
					{
						number: 98,
						value: "اٹھانوے"
					},
					{
						number: 97,
						value: "ستانوے"
					},
					{
						number: 96,
						value: "چھیانوے"
					},
					{
						number: 95,
						value: "پچانوے"
					},
					{
						number: 94,
						value: "چورانوے"
					},
					{
						number: 93,
						value: "ترانوے"
					},
					{
						number: 92,
						value: "بانوے"
					},
					{
						number: 91,
						value: "اکانوے"
					},
					{
						number: 90,
						value: "نوے"
					},
					{
						number: 89,
						value: "نواسی"
					},
					{
						number: 88,
						value: "اٹھاسی"
					},
					{
						number: 87,
						value: "ستاسی"
					},
					{
						number: 86,
						value: "چھیاسی"
					},
					{
						number: 85,
						value: "پچاسی"
					},
					{
						number: 84,
						value: "چوراسی"
					},
					{
						number: 83,
						value: "تراسی"
					},
					{
						number: 82,
						value: "بیاسی"
					},
					{
						number: 81,
						value: "اکیاسی"
					},
					{
						number: 80,
						value: "اسی"
					},
					{
						number: 79,
						value: "انہتر"
					},
					{
						number: 78,
						value: "اٹھہتر"
					},
					{
						number: 77,
						value: "ستتر"
					},
					{
						number: 76,
						value: "چھہتر"
					},
					{
						number: 75,
						value: "پچھتر"
					},
					{
						number: 74,
						value: "چوہتر"
					},
					{
						number: 73,
						value: "تہتر"
					},
					{
						number: 72,
						value: "بہتر"
					},
					{
						number: 71,
						value: "اکہتر"
					},
					{
						number: 70,
						value: "ستر"
					},
					{
						number: 69,
						value: "انہتر"
					},
					{
						number: 68,
						value: "اڑسٹھ"
					},
					{
						number: 67,
						value: "سڑسٹھ"
					},
					{
						number: 66,
						value: "چھیاسٹھ"
					},
					{
						number: 65,
						value: "پینسٹھ"
					},
					{
						number: 64,
						value: "چونسٹھ"
					},
					{
						number: 63,
						value: "تریسٹھ"
					},
					{
						number: 62,
						value: "باسٹھ"
					},
					{
						number: 61,
						value: "اکسٹھ"
					},
					{
						number: 60,
						value: "ساٹھ"
					},
					{
						number: 59,
						value: "انسٹھ"
					},
					{
						number: 58,
						value: "اٹھاون"
					},
					{
						number: 57,
						value: "ستاون"
					},
					{
						number: 56,
						value: "چھپن"
					},
					{
						number: 55,
						value: "پچپن"
					},
					{
						number: 54,
						value: "چون"
					},
					{
						number: 53,
						value: "ترپن"
					},
					{
						number: 52,
						value: "باون"
					},
					{
						number: 51,
						value: "اکیاون"
					},
					{
						number: 50,
						value: "پچاس"
					},
					{
						number: 49,
						value: "انچاس"
					},
					{
						number: 48,
						value: "اڑتالیس"
					},
					{
						number: 47,
						value: "سینتالیس"
					},
					{
						number: 46,
						value: "چھیالیس"
					},
					{
						number: 45,
						value: "پینتالیس"
					},
					{
						number: 44,
						value: "چوالیس"
					},
					{
						number: 43,
						value: "تینتالیس"
					},
					{
						number: 42,
						value: "بیالیس"
					},
					{
						number: 41,
						value: "اکتالیس"
					},
					{
						number: 40,
						value: "چالیس"
					},
					{
						number: 39,
						value: "انتالیس"
					},
					{
						number: 38,
						value: "اڑتیس"
					},
					{
						number: 37,
						value: "سینتیس"
					},
					{
						number: 36,
						value: "چھتیس"
					},
					{
						number: 35,
						value: "پینتیس"
					},
					{
						number: 34,
						value: "چونتیس"
					},
					{
						number: 33,
						value: "تینتیس"
					},
					{
						number: 32,
						value: "بتیس"
					},
					{
						number: 31,
						value: "اکتیس"
					},
					{
						number: 30,
						value: "تیس"
					},
					{
						number: 29,
						value: "انتیس"
					},
					{
						number: 28,
						value: "اٹھائیس"
					},
					{
						number: 27,
						value: "ستائیس"
					},
					{
						number: 26,
						value: "چھبیس"
					},
					{
						number: 25,
						value: "پچیس"
					},
					{
						number: 24,
						value: "چوبیس"
					},
					{
						number: 23,
						value: "تئیس"
					},
					{
						number: 22,
						value: "بائیس"
					},
					{
						number: 21,
						value: "اکیس"
					},
					{
						number: 20,
						value: "بیس"
					},
					{
						number: 19,
						value: "انیس"
					},
					{
						number: 18,
						value: "اٹھارہ"
					},
					{
						number: 17,
						value: "سترہ"
					},
					{
						number: 16,
						value: "سولہ"
					},
					{
						number: 15,
						value: "پندرہ"
					},
					{
						number: 14,
						value: "چودہ"
					},
					{
						number: 13,
						value: "تیرہ"
					},
					{
						number: 12,
						value: "بارہ"
					},
					{
						number: 11,
						value: "گیارہ"
					},
					{
						number: 10,
						value: "دس"
					},
					{
						number: 9,
						value: "نو"
					},
					{
						number: 8,
						value: "آٹھ"
					},
					{
						number: 7,
						value: "سات"
					},
					{
						number: 6,
						value: "چھ"
					},
					{
						number: 5,
						value: "پانچ"
					},
					{
						number: 4,
						value: "چار"
					},
					{
						number: 3,
						value: "تین"
					},
					{
						number: 2,
						value: "دو"
					},
					{
						number: 1,
						value: "ایک"
					},
					{
						number: 0,
						value: "صفر"
					}
				],
				exactWordsMapping: [{
					number: 100,
					value: "ایک سو"
				}]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/es-VE.js
var require_es_VE = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	var Locale = class {
		constructor() {
			this.config = {
				currency: {
					name: "Bolivar",
					plural: "Bolivares",
					singular: "Bolivar",
					symbol: "",
					fractionalUnit: {
						name: "Centimo",
						plural: "Centimos",
						singular: "Centimo",
						symbol: ""
					}
				},
				texts: {
					and: "Con",
					minus: "Menos",
					only: "",
					point: "Coma"
				},
				numberWordsMapping: [
					{
						number: 0x38d7ea4c68000,
						value: "Trillon"
					},
					{
						number: 0xe8d4a51000,
						value: "Billon"
					},
					{
						number: 1e6,
						value: "Millon"
					},
					{
						number: 1e3,
						value: "Mil"
					},
					{
						number: 900,
						value: "Novecientos"
					},
					{
						number: 800,
						value: "Ochocientos"
					},
					{
						number: 700,
						value: "Setecientos"
					},
					{
						number: 600,
						value: "Seiscientos"
					},
					{
						number: 500,
						value: "Quinientos"
					},
					{
						number: 400,
						value: "Cuatrocientos"
					},
					{
						number: 300,
						value: "Trescientos"
					},
					{
						number: 200,
						value: "Doscientos"
					},
					{
						number: 100,
						value: "Ciento"
					},
					{
						number: 90,
						value: "Noventa Y"
					},
					{
						number: 80,
						value: "Ochenta Y"
					},
					{
						number: 70,
						value: "Setenta Y"
					},
					{
						number: 60,
						value: "Sesenta Y"
					},
					{
						number: 50,
						value: "Cincuenta Y"
					},
					{
						number: 40,
						value: "Cuarenta Y"
					},
					{
						number: 30,
						value: "Treinta Y"
					},
					{
						number: 29,
						value: "Veintinueve"
					},
					{
						number: 28,
						value: "Veintiocho"
					},
					{
						number: 27,
						value: "Veintisiete"
					},
					{
						number: 26,
						value: "Veintiseis"
					},
					{
						number: 25,
						value: "Veinticinco"
					},
					{
						number: 24,
						value: "Veinticuatro"
					},
					{
						number: 23,
						value: "Veintitrés"
					},
					{
						number: 22,
						value: "Veintidos"
					},
					{
						number: 21,
						value: "Veintiuno"
					},
					{
						number: 20,
						value: "Veinte"
					},
					{
						number: 19,
						value: "Diecinueve"
					},
					{
						number: 18,
						value: "Dieciocho"
					},
					{
						number: 17,
						value: "Dieciciete"
					},
					{
						number: 16,
						value: "Dieciseis"
					},
					{
						number: 15,
						value: "Quince"
					},
					{
						number: 14,
						value: "Catorce"
					},
					{
						number: 13,
						value: "Trece"
					},
					{
						number: 12,
						value: "Doce"
					},
					{
						number: 11,
						value: "Once"
					},
					{
						number: 10,
						value: "Diez"
					},
					{
						number: 9,
						value: "Nueve"
					},
					{
						number: 8,
						value: "Ocho"
					},
					{
						number: 7,
						value: "Siete"
					},
					{
						number: 6,
						value: "Seis"
					},
					{
						number: 5,
						value: "Cinco"
					},
					{
						number: 4,
						value: "Cuatro"
					},
					{
						number: 3,
						value: "Tres"
					},
					{
						number: 2,
						value: "Dos"
					},
					{
						number: 1,
						value: "Uno"
					},
					{
						number: 0,
						value: "Cero"
					}
				],
				ignoreOneForWords: [
					"Cien",
					"Ciento",
					"Doscientos",
					"Trescientos",
					"Cuatrocientos",
					"Quinientos",
					"Seiscientos",
					"Setecientos",
					"Ochocientos",
					"Novecientos",
					"Mil"
				],
				pluralMark: "es",
				pluralWords: [
					"Millon",
					"Billon",
					"Trillon"
				],
				exactWordsMapping: [
					{
						number: 100,
						value: "Cien"
					},
					{
						number: 90,
						value: "Noventa"
					},
					{
						number: 80,
						value: "Ochenta"
					},
					{
						number: 70,
						value: "Setenta"
					},
					{
						number: 60,
						value: "Sesenta"
					},
					{
						number: 50,
						value: "Cincuenta"
					},
					{
						number: 40,
						value: "Cuarenta"
					},
					{
						number: 30,
						value: "Treinta"
					},
					{
						number: 1,
						value: ["Un", "Uno"]
					}
				]
			};
		}
	};
	exports.default = Locale;
}));
//#endregion
//#region node_modules/.pnpm/to-words@4.6.0/node_modules/to-words/dist/locales/index.js
var require_locales = /* @__PURE__ */ __commonJSMin(((exports) => {
	var __importDefault = exports && exports.__importDefault || function(mod) {
		return mod && mod.__esModule ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	var bn_IN_1 = __importDefault(require_bn_IN());
	var ee_EE_1 = __importDefault(require_ee_EE());
	var en_AE_1 = __importDefault(require_en_AE());
	var en_BD_1 = __importDefault(require_en_BD());
	var en_GB_1 = __importDefault(require_en_GB());
	var en_GH_1 = __importDefault(require_en_GH());
	var en_IE_1 = __importDefault(require_en_IE());
	var en_IN_1 = __importDefault(require_en_IN());
	var en_MM_1 = __importDefault(require_en_MM());
	var en_MU_1 = __importDefault(require_en_MU());
	var en_NG_1 = __importDefault(require_en_NG());
	var en_NP_1 = __importDefault(require_en_NP());
	var en_PH_1 = __importDefault(require_en_PH());
	var en_US_1 = __importDefault(require_en_US());
	var es_AR_1 = __importDefault(require_es_AR());
	var es_ES_1 = __importDefault(require_es_ES());
	var es_MX_1 = __importDefault(require_es_MX());
	var fa_IR_1 = __importDefault(require_fa_IR());
	var fr_BE_1 = __importDefault(require_fr_BE());
	var fr_FR_1 = __importDefault(require_fr_FR());
	var gu_IN_1 = __importDefault(require_gu_IN());
	var hi_IN_1 = __importDefault(require_hi_IN());
	var kn_IN_1 = __importDefault(require_kn_IN());
	var ko_KR_1 = __importDefault(require_ko_KR());
	var lv_LV_1 = __importDefault(require_lv_LV());
	var mr_IN_1 = __importDefault(require_mr_IN());
	var nl_SR_1 = __importDefault(require_nl_SR());
	var np_NP_1 = __importDefault(require_np_NP());
	var pt_BR_1 = __importDefault(require_pt_BR());
	var tr_TR_1 = __importDefault(require_tr_TR());
	var ur_PK_1 = __importDefault(require_ur_PK());
	var es_VE_1 = __importDefault(require_es_VE());
	exports.default = {
		"bn-IN": bn_IN_1.default,
		"ee-EE": ee_EE_1.default,
		"en-AE": en_AE_1.default,
		"en-BD": en_BD_1.default,
		"en-GB": en_GB_1.default,
		"en-GH": en_GH_1.default,
		"en-IE": en_IE_1.default,
		"en-IN": en_IN_1.default,
		"en-MM": en_MM_1.default,
		"en-MU": en_MU_1.default,
		"en-NG": en_NG_1.default,
		"en-NP": en_NP_1.default,
		"en-PH": en_PH_1.default,
		"en-US": en_US_1.default,
		"es-AR": es_AR_1.default,
		"es-ES": es_ES_1.default,
		"es-MX": es_MX_1.default,
		"fa-IR": fa_IR_1.default,
		"fr-BE": fr_BE_1.default,
		"fr-FR": fr_FR_1.default,
		"gu-IN": gu_IN_1.default,
		"hi-IN": hi_IN_1.default,
		"kn-IN": kn_IN_1.default,
		"ko-KR": ko_KR_1.default,
		"lv-LV": lv_LV_1.default,
		"mr-IN": mr_IN_1.default,
		"nl-SR": nl_SR_1.default,
		"np-NP": np_NP_1.default,
		"pt-BR": pt_BR_1.default,
		"tr-TR": tr_TR_1.default,
		"ur-PK": ur_PK_1.default,
		"es-VE": es_VE_1.default
	};
}));
//#endregion
//#region src/Format.ts
var import_ToWords = (/* @__PURE__ */ __commonJSMin(((exports) => {
	var __importDefault = exports && exports.__importDefault || function(mod) {
		return mod && mod.__esModule ? mod : { "default": mod };
	};
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.ToWords = exports.DefaultToWordsOptions = exports.DefaultConverterOptions = exports.LOCALES = void 0;
	var locales_1 = __importDefault(require_locales());
	exports.LOCALES = locales_1.default;
	exports.DefaultConverterOptions = {
		currency: false,
		ignoreDecimal: false,
		ignoreZeroCurrency: false,
		doNotAddOnly: false
	};
	exports.DefaultToWordsOptions = {
		localeCode: "en-IN",
		converterOptions: exports.DefaultConverterOptions
	};
	var ToWords = class {
		constructor(options = {}) {
			this.options = {};
			this.locale = void 0;
			this.options = Object.assign({}, exports.DefaultToWordsOptions, options);
		}
		getLocaleClass() {
			if (!(this.options.localeCode in locales_1.default)) throw new Error(`Unknown Locale "${this.options.localeCode}"`);
			return locales_1.default[this.options.localeCode];
		}
		getLocale() {
			if (this.locale === void 0) this.locale = new (this.getLocaleClass())();
			return this.locale;
		}
		convert(number, options = {}) {
			var _a;
			options = Object.assign({}, this.options.converterOptions, options);
			if (!this.isValidNumber(number)) throw new Error(`Invalid Number "${number}"`);
			if (options.ignoreDecimal) number = Number.parseInt(number.toString());
			let words = [];
			if (options.currency) words = this.convertCurrency(number, options);
			else words = this.convertNumber(number);
			if ((_a = this.locale) === null || _a === void 0 ? void 0 : _a.config.trim) return words.join("");
			return words.join(" ");
		}
		convertNumber(number) {
			var _a, _b, _c;
			const locale = this.getLocale();
			const isNegativeNumber = number < 0;
			if (isNegativeNumber) number = Math.abs(number);
			const split = number.toString().split(".");
			const ignoreZero = this.isNumberZero(number) && locale.config.ignoreZeroInDecimals;
			let words = this.convertInternal(Number(split[0]), true);
			const isFloat = this.isFloat(number);
			if (isFloat && ignoreZero) words = [];
			const wordsWithDecimal = [];
			if (isFloat) {
				if (!ignoreZero) wordsWithDecimal.push(locale.config.texts.point);
				if (split[1].startsWith("0") && !((_a = locale.config) === null || _a === void 0 ? void 0 : _a.decimalLengthWordMapping)) {
					const zeroWords = [];
					for (const num of split[1]) zeroWords.push(...this.convertInternal(Number(num), true));
					wordsWithDecimal.push(...zeroWords);
				} else {
					wordsWithDecimal.push(...this.convertInternal(Number(split[1]), true));
					const decimalLengthWord = (_c = (_b = locale.config) === null || _b === void 0 ? void 0 : _b.decimalLengthWordMapping) === null || _c === void 0 ? void 0 : _c[split[1].length];
					if (decimalLengthWord) wordsWithDecimal.push(decimalLengthWord);
				}
			}
			if (!(words.length <= 0) && isNegativeNumber) words.unshift(locale.config.texts.minus);
			words.push(...wordsWithDecimal);
			return words;
		}
		convertCurrency(number, options = {}) {
			var _a, _b, _c, _d;
			const locale = this.getLocale();
			const currencyOptions = (_a = options.currencyOptions) !== null && _a !== void 0 ? _a : locale.config.currency;
			const isNegativeNumber = number < 0;
			if (isNegativeNumber) number = Math.abs(number);
			number = this.toFixed(number);
			const split = number.toString().split(".");
			let words = [...this.convertInternal(Number(split[0]))];
			if (Number(split[0]) === 1 && currencyOptions.singular) words.push(currencyOptions.singular);
			else if (currencyOptions.plural) words.push(currencyOptions.plural);
			const ignoreZero = this.isNumberZero(number) && (options.ignoreZeroCurrency || ((_b = locale.config) === null || _b === void 0 ? void 0 : _b.ignoreZeroInDecimals) && number !== 0);
			if (ignoreZero) words = [];
			const wordsWithDecimal = [];
			if (this.isFloat(number)) {
				if (!ignoreZero) wordsWithDecimal.push(locale.config.texts.and);
				const decimalPart = Number(split[1]) * (!locale.config.decimalLengthWordMapping ? Math.pow(10, 2 - split[1].length) : 1);
				wordsWithDecimal.push(...this.convertInternal(decimalPart));
				const decimalLengthWord = (_d = (_c = locale.config) === null || _c === void 0 ? void 0 : _c.decimalLengthWordMapping) === null || _d === void 0 ? void 0 : _d[split[1].length];
				if (decimalLengthWord === null || decimalLengthWord === void 0 ? void 0 : decimalLengthWord.length) wordsWithDecimal.push(decimalLengthWord);
				if (decimalPart === 1 && currencyOptions.fractionalUnit.singular) wordsWithDecimal.push(currencyOptions.fractionalUnit.singular);
				else wordsWithDecimal.push(currencyOptions.fractionalUnit.plural);
			} else if (locale.config.decimalLengthWordMapping && words.length) wordsWithDecimal.push(currencyOptions.fractionalUnit.plural);
			const isEmpty = words.length <= 0 && wordsWithDecimal.length <= 0;
			if (!isEmpty && isNegativeNumber) words.unshift(locale.config.texts.minus);
			if (!isEmpty && locale.config.texts.only && !options.doNotAddOnly && !locale.config.onlyInFront) wordsWithDecimal.push(locale.config.texts.only);
			if (wordsWithDecimal.length) words.push(...wordsWithDecimal);
			if (!isEmpty && !options.doNotAddOnly && locale.config.onlyInFront) words.splice(0, 0, locale.config.texts.only);
			return words;
		}
		convertInternal(number, trailing = false) {
			var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
			const locale = this.getLocale();
			if (locale.config.exactWordsMapping) {
				const exactMatch = (_b = (_a = locale.config) === null || _a === void 0 ? void 0 : _a.exactWordsMapping) === null || _b === void 0 ? void 0 : _b.find((elem) => {
					return number === elem.number;
				});
				if (exactMatch) return [Array.isArray(exactMatch.value) ? exactMatch.value[+trailing] : exactMatch.value];
			}
			const match = locale.config.numberWordsMapping.find((elem) => {
				return number >= elem.number;
			});
			const words = [];
			if (number <= 100 || number < 1e3 && locale.config.namedLessThan1000) {
				words.push(Array.isArray(match.value) ? match.value[0] : match.value);
				number -= match.number;
				if (number > 0) {
					if ((_d = (_c = locale.config) === null || _c === void 0 ? void 0 : _c.splitWord) === null || _d === void 0 ? void 0 : _d.length) words.push(locale.config.splitWord);
					words.push(...this.convertInternal(number, trailing));
				}
				return words;
			}
			const quotient = Math.floor(number / match.number);
			const remainder = number % match.number;
			let matchValue = Array.isArray(match.value) ? match.value[0] : match.value;
			if (quotient > 1 && ((_f = (_e = locale.config) === null || _e === void 0 ? void 0 : _e.pluralWords) === null || _f === void 0 ? void 0 : _f.find((word) => word === match.value)) && ((_g = locale.config) === null || _g === void 0 ? void 0 : _g.pluralMark)) matchValue += locale.config.pluralMark;
			if (quotient % 10 === 1) matchValue = match.singularValue || (Array.isArray(matchValue) ? matchValue[0] : matchValue);
			if (quotient === 1 && ((_j = (_h = locale.config) === null || _h === void 0 ? void 0 : _h.ignoreOneForWords) === null || _j === void 0 ? void 0 : _j.includes(matchValue))) words.push(matchValue);
			else words.push(...this.convertInternal(quotient, false), matchValue);
			if (remainder > 0) {
				if ((_l = (_k = locale.config) === null || _k === void 0 ? void 0 : _k.splitWord) === null || _l === void 0 ? void 0 : _l.length) {
					if (!((_o = (_m = locale.config) === null || _m === void 0 ? void 0 : _m.noSplitWordAfter) === null || _o === void 0 ? void 0 : _o.find((word) => word === match.value))) words.push(locale.config.splitWord);
				}
				words.push(...this.convertInternal(remainder, trailing));
			}
			return words;
		}
		toFixed(number, precision = 2) {
			return Number(Number(number).toFixed(precision));
		}
		isFloat(number) {
			return Number(number) === number && number % 1 !== 0;
		}
		isValidNumber(number) {
			return !isNaN(parseFloat(number)) && isFinite(number);
		}
		isNumberZero(number) {
			return number >= 0 && number < 1;
		}
	};
	exports.ToWords = ToWords;
})))();
var isJint = typeof Jint !== "undefined" && Jint !== void 0;
var Format = class {
	static date(date, props = {}) {
		if (!date) return "";
		const { format: format$1 } = {
			format: "MMMM d, yyyy",
			...props
		};
		let parsedDate;
		if (date instanceof Date) parsedDate = date;
		else if (typeof date === "string") parsedDate = parseISO(date);
		else throw new Error(`Invalid date: ${JSON.stringify(date)}`);
		try {
			return format(parsedDate, format$1);
		} catch (error) {
			if (error instanceof RangeError) {
				if (error.message.match(/Invalid time value/)) throw new Error(`Invalid date: ${JSON.stringify(date)}`);
				else if (error.message.match(/Format string contains an unescaped latin/)) throw new Error(`Invalid date format: ${JSON.stringify(format$1)}`);
			}
			throw error;
		}
	}
	static number(value, props = {}) {
		if (value === null || value === void 0) return "";
		const numericValue = typeof value === "string" ? tryParseNumber(value) : value;
		if (!numericValue) return value.toString();
		const { decimalPlaces, thousandsSeparator, removeTrailingZeros, currency, currencyDisplay, style } = props;
		if (isJint) return Jint.formatNumber(numericValue, "en-US", {
			decimalPlaces,
			useThousandsSeparator: thousandsSeparator,
			removeTrailingZeros,
			style,
			currency,
			currencyDisplay
		});
		else return numericValue.toLocaleString("en-US", {
			minimumFractionDigits: removeTrailingZeros ? 0 : decimalPlaces,
			maximumFractionDigits: decimalPlaces,
			useGrouping: thousandsSeparator,
			style,
			currency,
			currencyDisplay
		});
	}
	static currency(value, props = {}) {
		return this.number(value, {
			currency: "USD",
			...props,
			style: "currency"
		});
	}
	static percent(value, props = {}) {
		return this.number(value, {
			...props,
			style: "percent"
		});
	}
	static numberToWords(value, localeCode = "en-US", converterOptions = {}) {
		if (value === null || value === void 0) return "";
		let detectedCurrency = {};
		if (typeof value === "string") {
			const currencyOptions = currencyOptionsFromString(value);
			if (currencyOptions) detectedCurrency = {
				currency: true,
				currencyOptions
			};
		}
		const numericValue = typeof value === "string" ? tryParseNumber(value) : value;
		if (numericValue === null) return typeof value === "string" ? value : "";
		if (typeof numericValue !== "number" || isNaN(numericValue)) return value.toString();
		if (!isFinite(numericValue)) {
			if (numericValue === Infinity) return "infinity";
			if (numericValue === -Infinity) return "negative infinity";
			return value.toString();
		}
		const finalConverterOptions = {
			doNotAddOnly: true,
			...detectedCurrency,
			...converterOptions
		};
		return new import_ToWords.ToWords({ localeCode }).convert(numericValue, finalConverterOptions).toLowerCase();
	}
};
/**
* Attempts to parse a string as a number, removing any extraneous characters.
*
* This supports the following formats:
* - "$1,000.00"
* - "US$1,000.00" (and a few other variations)
* - "£1,000.00"
* - "€1,000.00"
* - "¥1,000.00"
* - "1,000.00"
* - "1000.00"
* - "1000"
* - "1000%"
* @param value
* @returns
*/
function tryParseNumber(value) {
	if (typeof value === "number") return value;
	let transformed = value.trim();
	const symbols = Object.keys(currencyConfigs).sort((a, b) => b.length - a.length);
	for (const symbol of symbols) if (transformed.startsWith(symbol)) {
		transformed = transformed.slice(symbol.length);
		break;
	}
	if (transformed.endsWith("%")) transformed = transformed.slice(0, -1);
	if (transformed.includes(",")) transformed = transformed.replace(/,/g, "");
	const parsedValue = parseFloat(transformed);
	return isNaN(parsedValue) ? null : parsedValue;
}
/**
* Detects currency symbols in a string and returns appropriate currency configuration
*/
function currencyOptionsFromString(value) {
	for (const [symbol, config] of Object.entries(currencyConfigs)) if (value.startsWith(symbol)) return config;
	return null;
}
var usdConfig = {
	name: "US Dollar",
	singular: "US Dollar",
	plural: "US Dollars",
	symbol: "US$",
	fractionalUnit: {
		name: "Cent",
		singular: "Cent",
		plural: "Cents",
		symbol: "¢"
	}
};
var cadConfig = {
	name: "Canadian Dollar",
	singular: "Canadian Dollar",
	plural: "Canadian Dollars",
	symbol: "CA$",
	fractionalUnit: {
		name: "Cent",
		singular: "Cent",
		plural: "Cents",
		symbol: "¢"
	}
};
var currencyConfigs = {
	US$: usdConfig,
	USD$: usdConfig,
	USD: usdConfig,
	CA$: cadConfig,
	CAD$: cadConfig,
	CAD: cadConfig,
	$: {
		name: "Dollar",
		singular: "Dollar",
		plural: "Dollars",
		symbol: "$",
		fractionalUnit: {
			name: "Cent",
			singular: "Cent",
			plural: "Cents",
			symbol: "¢"
		}
	},
	"£": {
		name: "Pound",
		singular: "Pound",
		plural: "Pounds",
		symbol: "£",
		fractionalUnit: {
			name: "Penny",
			singular: "Penny",
			plural: "Pence",
			symbol: "p"
		}
	},
	"€": {
		name: "Euro",
		singular: "Euro",
		plural: "Euros",
		symbol: "€",
		fractionalUnit: {
			name: "Cent",
			singular: "Cent",
			plural: "Cents",
			symbol: "¢"
		}
	},
	"¥": {
		name: "Yen",
		singular: "Yen",
		plural: "Yen",
		symbol: "¥",
		fractionalUnit: {
			name: "Sen",
			plural: "Sen",
			symbol: ""
		}
	}
};
//#endregion
export { BaseTemplate, Collection, Format, ObjectWatcher, SignTab };
